/*

& Every reducer of the global slice.

  10 Oct 2026: the delete_account slice (the old delete-link token) and the thunks and
  reducers that served the old delete flows (deleteBusinessProfile, sendDeleteBPLink) were
  removed; deletion now uses a server-sent code (lib/teams/deleteFlowsClient).

*/

/*

? First, let's import the AnyAction type to make it easier to describe the shape of our actions.

*/

import { AnyAction } from "redux";
import { BoostInterface, PropertyInterface, ServiceInterface } from "../models";

/*

? Next, we will import the interface that will describe the shape of our global state.

*/

import StateInterface from "./state-interface";
import {
	AiBoostB2BLeadsInterface,
	BoostLocation,
	BroadcastInterface,
	BusinessProfileInterface,
	ChatInterface,
	ChatMessageInterface,
	ChatStatesInterface,
	CtaLeadsInterface,
	KYCInformationBusinessProfile,
	Page,
	PostInterface,
	ProfilePicture,
	PropertyClosureInterface,
	SalesSupportInvestmentInterface,
	TeamInviteInterface,
	UserInterface,
	PropertyTranslationsInterface,
	BbmPushNotificationTemplateInterface,
	BbmScheduledPushNotificationLogInterface,
	NotificationQueueInterface,
} from "./sub-interfaces/sub-interfaces";
// import { homePage } from "./sub-state-data/sub-state-data";
import { AlertColor, AlertPropsColorOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { Dayjs } from "dayjs";
import { ObjectId } from "mongodb";
import CalculateMinBoostBudget from "../lib/CalculateMinBoostBudget";
import { PropertyBoostInterface } from "./sub-interfaces/boost-interface";
import { LastMessageInterface, NotificationSettingInterface, Participants } from "./sub-interfaces/chat-interface";
import { SearchApiResponseInterface, SearchFilterId, SearchFilterOption } from "./sub-interfaces/search-types";
import { boost } from "./sub-state-data/boost";

/*

& Now, we can describe all our reducers.

* Reducers are objects with two properties - the reducer function itself and the prepare callback.

* The reducer in our case uses the Immer library which allows us to conveniently mutate the immutable redux store. Alternatively, we could return a new object with the new state.

* The prepare callback is a function that is called before the reducer. It is used to "prepare" the payload that the reducer will receive. Think of it like a "before" hook.

*/

export const changeMe = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.tester = "tester-new-value" + action.payload;
	},
	prepare: (payload: string) => ({ payload: payload }),
};

export const setThemeReducer = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.user.settings.site_preferences.default_theme = action.payload;
	},
	prepare: (payload: string) => ({ payload: payload }),
};

export const fetchSingleProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile = action.payload.user_profile;
		state.single_business_profile = action.payload.business_profile;
		state.single_property_page = action.payload;
		state.location = {
			location: [
				action.payload.property.search_metrics.location_coordinates[1],
				action.payload.property.search_metrics.location_coordinates[0],
			],
		};
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const CreateNewPost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.is_business_profile) {
			state.single_business_profile.posts = action.payload.posts;
			state.postSwrForBusiness = action.payload.posts;
		} else {
			state.single_user_profile.posts = action.payload.posts;
			state.postSwrForUser = action.payload.posts;
		}
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => {
		return {
			payload: payload.payload,
		};
	},
};

export const CreateNewComments = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload.path.includes("post")) {
			state.single_post_page.post.comments = action.payload.payload.comments;
		} else {
			if (action.payload.payload.is_business_profile) {
				if (action.payload.tabValue === 0) {
					const postSwrIndex: number = state.single_business_profile.posts.findIndex(
						(post: PostInterface) => post._id === action.payload.payload[0]._id,
					);
					state.single_business_profile.posts[postSwrIndex].comments = action.payload.payload[0].comments;
				} else {
					if (action.payload.payload.path.includes("users")) {
						const postSwrIndex: number = state.single_user_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_user_profile.saved_items.posts[postSwrIndex].comments = action.payload.payload[0].comments;
					} else {
						const postSwrIndex: number = state.single_business_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_business_profile.saved_items.posts[postSwrIndex].comments = action.payload.payload[0].comments;
					}
				}
			} else {
				if (action.payload.tabValue === 0) {
					const postSwrIndex: number = state.single_user_profile.posts.findIndex(
						(post: PostInterface) => post._id === action.payload.payload[0]._id,
					);
					state.single_user_profile.posts[postSwrIndex].comments = action.payload.payload[0].comments;
				} else {
					if (action.payload.payload.path.includes("users")) {
						const postSwrIndex: number = state.single_user_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_user_profile.saved_items.posts[postSwrIndex].comments = action.payload.payload[0].comments;
					} else {
						const postSwrIndex: number = state.single_business_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_business_profile.saved_items.posts[postSwrIndex].comments = action.payload.payload[0].comments;
					}
				}
			}
		}
	},
	prepare: (payload: {
		tabValue: number;
		response: {
			status: string;
			payload: any;
			missingFields: Array<string> | null;
			error: string | null;
		};
	}) => ({
		payload: { payload: payload.response.payload, tabValue: payload.tabValue },
	}),
};
export const setImageCategory = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.category = action.payload;
	},
	prepare: (payload: StateInterface["category"]) => ({ payload: payload }),
};
// export const VerifyEmail = {
//   reducer: (state: StateInterface, action: AnyAction) => {
//     state.category = action.payload;
//   },
//   prepare: (payload: StateInterface["verify_email"]) => ({ payload: payload }),
// };

export const setLanguage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.default_language = action.payload;
	},
	prepare: (payload: StateInterface["default_language"]) => ({
		payload: payload,
	}),
};

export const setImagesIndex = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.images_index = action.payload;
	},
	prepare: (payload: StateInterface["images_index"]) => ({ payload: payload }),
};

export const KycProfileTypes = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.kyc_types = action.payload;
	},
	prepare: (payload: StateInterface["kyc_types"]) => ({ payload: payload }),
};

export const setTabValue = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.tabValue = action.payload;
	},
	prepare: (payload: StateInterface["tabValue"]) => ({ payload: payload }),
};

export const setSaveTabValue = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.saved_tabValue = action.payload;
	},
	prepare: (payload: StateInterface["saved_tabValue"]) => ({
		payload: payload,
	}),
};
export const setListingTabValue = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.listing_tabValue = action.payload;
	},
	prepare: (payload: StateInterface["listing_tabValue"]) => ({
		payload: payload,
	}),
};
export const setAnalyticsTabValue = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.analytics_tabValue = action.payload;
	},
	prepare: (payload: StateInterface["analytics_tabValue"]) => ({
		payload: payload,
	}),
};

export const setUserLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.user_location = action.payload;
	},
	prepare: (payload: StateInterface["user_location"]) => ({ payload: payload }),
};

export const fetchSingleBusinessProfile = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile = action.payload.user_profile;
		state.single_business_profile = action.payload;
		state.single_business_profile.properties_og = action.payload.properties;
		state.single_business_profile.services_og = action.payload.services;
		state.single_business_profile.ofb_properties_og = action.payload.ofb_properties;
	},
	prepare: (payload: StateInterface["single_business_profile"]) => ({
		payload: payload,
	}),
};

export const fetchGlobalSearchResults = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.global_search_results = action.payload;
	},
	prepare: (payload: StateInterface["global_search_results"]) => ({
		payload: payload,
	}),
};

export const loadMoreGlobalSearchResults = {
	reducer: (state: StateInterface, action: AnyAction) => {
		Array.isArray(state.global_search_results.results)
			? ((state.global_search_results.number_of_results = action.payload.number_of_results),
				(state.global_search_results.search_duration = action.payload.search_duration),
				state.global_search_results.results.push(...action.payload.results))
			: (state.global_search_results = action.payload);
	},
	prepare: (payload: StateInterface["global_search_results"]) => ({
		payload: payload,
	}),
};

export const LoadingMoreUserProfilePosts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		Array.isArray(state.single_user_profile.posts)
			? ((state.single_user_profile.postsCount = action.payload.postsCount),
				//@ts-ignore
				state.single_user_profile.posts.push(...action.payload.posts))
			: (state.single_user_profile.posts = action.payload.posts);
	},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};

export const loadMoreGlobalSearchPosts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		!Array.isArray(state.global_search_results.results)
			? ((state.global_search_results.number_of_results = action.payload.number_of_results),
				(state.global_search_results.search_duration = action.payload.search_duration),
				state.global_search_results.results.posts.push(...action.payload.results.posts))
			: (state.global_search_results = action.payload);
	},
	prepare: (payload: StateInterface["global_search_results"]) => ({
		payload: payload,
	}),
};

export const setIsSearching = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.is_searching = action.payload;
	},
	prepare: (payload: StateInterface["is_searching"]) => ({
		payload: payload,
	}),
};

export const setSearchResultsPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.search_results_page_number = action.payload;
	},
	prepare: (payload: StateInterface["search_results_page_number"]) => ({
		payload: payload,
	}),
};

export const setIsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.is_loading = action.payload;
	},
	prepare: (payload: StateInterface["is_loading"]) => ({
		payload: payload,
	}),
};

export const setOpenPostModal = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.open_post_modal = action.payload;
	},
	prepare: (payload: StateInterface["open_post_modal"]) => ({
		payload: payload,
	}),
};

export const CreateNewReview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.source.includes("properties") && action.payload.source.includes("/reviews")) {
			state.property_reviews_page.property_reviews.push(
				//@ts-ignore
				action.payload.payload,
			);
		} else if (action.payload.source.includes("properties")) {
			if (action.payload.payload.type === "neighbourhood") {
				state.single_property_page.neighbourhoodReviews.push(action.payload.payload);
			} else {
				state.single_property_page.propertyReviews.push(action.payload.payload);
			}
		}
		if (action.payload.source.includes("/services") && action.payload.source.includes("/reviews")) {
			state.service_reviews_page.service_reviews.push(
				//@ts-ignore
				action.payload.payload,
			);
		} else if (action.payload.source.includes("services")) {
			//@ts-ignore
			state.single_service_page.service_reviews.push(action.payload.payload);
		} else if (action.payload.source.includes("users")) {
			//@ts-ignore
			state.single_user_profile.reviews.push(action.payload.payload);
		} else if (action.payload.source.includes("neighbourhood-reviews")) {
			//@ts-ignore
			state.neighbourhood_reviews_page.reviews.push(action.payload.payload);
		} else {
			//@ts-ignore
			state.single_business_profile.reviews.push(action.payload.payload);
		}
	},
	prepare: (payload: {
		source: string;
		response: {
			status: string;
			payload: StateInterface["review"];
			missingFields: Array<string> | null;
			error: string | null;
		};
	}) => ({
		payload: { payload: payload.response.payload, source: payload.source },
	}),
};

export const fetchPropertyReviewsPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.property_reviews_page = action.payload;
	},
	prepare: (payload: StateInterface["property_reviews_page"]) => ({
		payload: payload,
	}),
};

export const fetchSingleUserProfile = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile = action.payload;
	},
	prepare: (payload: StateInterface["single_user_profile"]) => ({
		payload: payload,
	}),
};

export const Setintro = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.intro = action.payload;
	},
	prepare: (payload: StateInterface["intro"]) => ({
		payload: payload,
	}),
};

export const fetchBoostPostPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost_post_page = action.payload;
	},
	prepare: (payload: StateInterface["boost_post_page"]) => ({
		payload: payload,
	}),
};

export const fetchBoostUserProfilePageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost_user_profile_page = action.payload;
	},
	prepare: (payload: StateInterface["boost_user_profile_page"]) => ({
		payload: payload,
	}),
};

export const fetchBoostBusinessProfilePageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost_business_profile_page = action.payload;
	},
	prepare: (payload: StateInterface["boost_business_profile_page"]) => ({
		payload: payload,
	}),
};

export const fetchBoostServicePageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost_service_page = action.payload;
	},
	prepare: (payload: StateInterface["boost_service_page"]) => ({
		payload: payload,
	}),
};

export const fetchBoostPropertyPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost_property_page = action.payload;
	},
	prepare: (payload: StateInterface["boost_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSingleServicePageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page = action.payload;
	},
	prepare: (payload: StateInterface["single_service_page"]) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data._id = action.payload.service_data._id;
		state.single_service_page.service_data.slug = action.payload.service_data.slug;
		state.single_service_page.service_data.title = action.payload.service_data.title;
		state.single_service_page.service_data.images = action.payload.service_data.images;
		state.single_service_page.service_data.published = action.payload.service_data.published;
		state.single_service_page.service_data.saved_content = action.payload.service_data.saved_content;
		state.single_service_page.service_data.show_contact_details = action.payload.service_data.show_contact_details;
		state.service_loading.singleServiceImages = false;
	},
	prepare: (
		payload: {
			service_data: ServiceInterface;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.location.title = action.payload.service_data.location.title;
		state.single_service_page.service_data.service_category = action.payload.service_data.service_category;
		state.single_service_page.service_data.service_type = action.payload.service_data.service_type;
		state.single_service_page.service_data.service_subtype = action.payload.service_data.service_subtype;
		state.single_service_page.service_data.other_service_subtype = action.payload.service_data.other_service_subtype;
		state.single_service_page.service_data.tags = action.payload.service_data.tags;
		state.single_service_page.service_data.price.min = action.payload.service_data.price.min;
		state.single_service_page.service_data.price.max = action.payload.service_data.price.max;
		state.single_service_page.service_data.price.currency = action.payload.service_data.price.currency;
		state.single_service_page.service_data.price.show_price = action.payload.service_data.price.show_price;
		state.single_service_page.service_data.status = action.payload.service_data.status;
		state.single_service_page.service_data.assigned_to = action.payload.service_data.assigned_to;
		state.service_loading.serviceDetails = false;
	},
	prepare: (
		payload: {
			service_data: ServiceInterface;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.description = action.payload.service_data.description;
		state.service_loading.singleServiceOverview = false;
	},
	prepare: (
		payload: {
			service_data: ServiceInterface;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceSpecifications = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_specifications = action.payload.service_data.service_specifications;
		state.service_loading.singleServiceSpecification = false;
	},
	prepare: (
		payload: {
			service_data: ServiceInterface;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceProvider = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_provider = action.payload.service_data.service_provider;
		state.service_loading.singleServiceProvider = false;
	},
	prepare: (
		payload: {
			service_data: ServiceInterface;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceAreasOfOperation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.areas_of_operation = action.payload.service_data.areas_of_operation;
		state.service_loading.singleServiceAreaofOperation = false;
	},
	prepare: (
		payload: {
			service_data: ServiceInterface;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceMapView = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.search_metrics.location_coordinates =
			action.payload.service_data.search_metrics.location_coordinates;
		state.single_service_page.service_data.location = action.payload.service_data.location;

		state.location = {
			location: [
				action.payload.service_data.search_metrics.location_coordinates[1],
				action.payload.service_data.search_metrics.location_coordinates[0],
			],
		};
		state.service_loading.singleServiceLocationCoordinates = false;
	},
	prepare: (
		payload: {
			service_data: ServiceInterface;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_reviews = action.payload.service_data;
		state.service_loading.singleServiceReviews = false;
	},
	prepare: (
		payload: {
			service_reviews:
				| Array<{
						name: string;
						reviewer_id: ObjectId | string;
						profile_picture: any;
						rating: number;
						body: string;
				  }>
				| [];
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceCreatorDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.created_by = action.payload.service_data.created_by;
		state.service_loading.singleServiceCreator = false;
	},
	prepare: (
		payload: {
			service_data: ServiceInterface;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceTopServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.hotServices = action.payload.top_services;
		state.service_loading.singleServiceHotServices = false;
	},
	prepare: (
		payload: {
			top_services: Array<ServiceInterface>;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchSingleServiceTrendingServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.trendingServices = action.payload.trending_services;
		state.service_loading.singleServiceTrendingServices = false;
	},
	prepare: (
		payload: {
			trending_services: Array<ServiceInterface>;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const setSingleServiceCategory = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_category = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSingleServiceType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_type = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSingleServiceSubtype = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_subtype = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSingleServiceOtherSubtype = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.other_service_subtype = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSingleServiceTags = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.tags = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSingleServiceShowPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.price.show_price = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setSingleServiceMinPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.price.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSingleServiceMaxPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.price.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSingleServiceOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.description = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSingleServiceSpecification = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_specifications = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSingleServiceProviderName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_provider.name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSingleServiceProviderEstablishedYear = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_provider.year_established = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSingleServiceProviderDescription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_provider.description = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSingleServiceAreasOfOperation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.areas_of_operation = action.payload;
	},
	prepare: (
		payload:
			| Array<{
					title: string;
					pincode: string;
					location_coordinates: Array<number>;
			  }>
			| [],
	) => ({
		payload: payload,
	}),
};

export const setSingleServiceLocationTitle = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.location.title = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSingleServiceLocationPincode = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.location.pincode = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSingleServiceLocationCoordinates = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.search_metrics.location_coordinates = action.payload;
	},
	prepare: (payload: Array<number>) => ({
		payload: payload,
	}),
};

export const setSectionToEdit = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.section_to_edit = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setDataBeforeEdit = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.data_before_edit = action.payload;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const fetchNeighbourhoodReviewsPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// state.neighbourhood_reviews_page.location = action.payload.location;
		// state.neighbourhood_reviews_page.reviews = action.payload.reviews;
		state.neighbourhood_reviews_page = action.payload;
	},
	prepare: (payload: StateInterface["neighbourhood_reviews_page"]) => ({
		payload: payload,
	}),
};

export const fetchServiceReviewsPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.service_reviews_page = action.payload;
	},
	prepare: (payload: StateInterface["service_reviews_page"]) => ({
		payload: payload,
	}),
};

export const fetchHomePageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage = action.payload;
		state.single_user_profile = action.payload.user_profile;
		state.single_business_profile = action.payload.business_profile;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};

export const fetchNotificationPage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.notifications_page = action.payload;
		state.notifications_loading.notifications = false;
	},
	prepare: (payload: StateInterface["notifications_page"]) => ({
		payload: payload,
	}),
};

export const loadMoreNotificationPage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refused request (signed out, not on the team) carries no page; keep what is shown. */
		if (!Array.isArray(action.payload?.payload)) return;
		state.notifications_page.all_notifications.push(...action.payload.payload);
	},
	prepare: (
		payload: {
			status: string;
			payload: any;
			missingFields: Array<string> | null;
			error: string | null;
		} | null,
	) => ({
		payload: payload,
	}),
};

export const fetchRealtimeNotification = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.notifications_page.all_notifications.unshift(...action.payload.all_notifications);
		state.notifications_page.unread_notifications_count = action.payload.unread_notifications_count;
		state.notifications_page.total_notifications_count = action.payload.total_notifications_count;
	},
	prepare: (payload: StateInterface["notifications_page"]) => ({
		payload: payload,
	}),
};

export const updateNotificationViewed = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refused request (signed out, not on the team) carries no payload; change nothing. */
		if (!action.payload || !action.payload.payload) return;
		state.notifications_page.all_notifications.map((notification) => {
			if (notification._id === action.payload.payload.notification_id) {
				notification.viewed = true;
			}
		});
		state.notifications_page.all_notifications = state.notifications_page.all_notifications.sort((a, b) => {
			return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
		});
		state.notifications_page.unread_notifications_count = action.payload.payload.unread_notifications_count;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const updateNotificationsViewed = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refused request (signed out, not on the team) carries no payload; change nothing. */
		if (!action.payload || !action.payload.payload) return;
		state.notifications_page.all_notifications.map((notification) => {
			if (action.payload.payload.notification_ids?.includes(notification._id)) {
				notification.viewed = true;
			}
		});
		state.notifications_page.unread_notifications_count = action.payload.payload.unread_notifications_count;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const fetchFaqs = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// Handle both array and object responses from API
		if (Array.isArray(action.payload)) {
			// If API returns array directly, wrap it in the expected structure
			state.faqs_page = {
				faqs: action.payload,
				search_results: action.payload,
			};
		} else {
			// If API returns object with faqs property
			state.faqs_page = action.payload;
			state.faqs_page.search_results = action.payload.faqs || [];
		}
	},
	prepare: (payload: StateInterface["faqs_page"] | Array<StateInterface["faq"]>) => ({
		payload: payload,
	}),
};

export const setFaqSearchResults = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.faqs_page) {
			state.faqs_page.search_results = action.payload;
		}
	},
	prepare: (payload: Array<StateInterface["faq"]>) => ({
		payload: payload,
	}),
};

export const fetchSingleFaq = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_faq_page = action.payload;
	},
	prepare: (payload: StateInterface["single_faq_page"]) => ({
		payload: payload,
	}),
};

export const FaqAddUpvote = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_faq_page.faq = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FaqAddDownvote = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_faq_page.faq = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FaqRemoveUpvote = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_faq_page.faq = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FaqRemoveDownvote = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_faq_page.faq = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const addAdminUserDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// state.single_business_profile.business_profile.admin_user_details = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const deleteAdminUserDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// state.single_business_profile.business_profile.admin_user_details = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessAbout = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about = action.payload.about;
		state.business_profile_snackbar.update_about = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessProfileShortDescription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about.short_description = action.payload;
	},
	prepare: (short_description: string) => ({
		payload: short_description,
	}),
};
export const UpdateBusinessProfilecountry_code = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.phone.country_code = action.payload;
	},
	prepare: (country_code: string) => ({
		payload: country_code,
	}),
};
export const UpdateBusinessProfilePhone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.phone.phone_number = action.payload;
	},
	prepare: (phone: string) => ({
		payload: phone,
	}),
};
export const UpdateBusinessProfileEmail = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.email.email = action.payload;
	},
	prepare: (email: string) => ({
		payload: email,
	}),
};
export const UpdateBusinessProfileEstDate = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about.year_established = action.payload;
	},
	prepare: (estDate: string) => ({
		payload: estDate,
	}),
};
export const UpdateBusinessProfileWebsite = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about.website_url = action.payload;
	},
	prepare: (Website: string) => ({
		payload: Website,
	}),
};
export const UpdateBusinessProfileOfficeAddresses = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about.office_address.push(action.payload);
	},
	prepare: (officeaddress: Array<string>) => ({
		payload: officeaddress,
	}),
};

export const UpdateBusinessBrokerage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.agent_brokerage = action.payload.agent_brokerage;
		state.business_profile_snackbar.brokerages = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessExpertise = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.expertise = action.payload.expertise;
		state.business_profile_snackbar.experties = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessProfileSpecialization = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.property_type_specialization =
			action.payload.property_type_specialization;
		// state.business_profile_snackbar.specialization = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};
export const UpdateBusinessLanguages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.languages = action.payload.languages;
		state.business_profile_snackbar.language = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.services_offered = action.payload.services_offered;
		state.business_profile_snackbar.service_offered = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessFAQs = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.faqs = action.payload.faqs;
		state.business_profile_snackbar.business_faq = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const AddSepcialOfferDetailsForBusinessProfile = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessLicences = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.licences_and_certifications =
			action.payload.licences_and_certifications;
		state.business_profile_snackbar.licences_and_certificates = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const SetDefaultLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.location = action.payload;
	},
	prepare: (payload: StateInterface["location"]) => ({
		payload: payload,
	}),
};
export const UpdateUserAboutDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refused update carries no payload: keep what is shown. */
		if (!action.payload) return;
		state.single_user_profile.user_profile.first_name = action.payload.first_name;
		state.single_user_profile.user_profile.last_name = action.payload.last_name;
		state.single_user_profile.user_profile.about.short_description = action.payload.about.short_description;
		state.single_user_profile.user_profile.recovery_email.email = action.payload.recovery_email.email;
		state.single_user_profile.user_profile.phone = action.payload.phone;
		state.single_user_profile.user_profile.country_code = action.payload.country_code;
		state.single_user_profile.user_profile.date_of_birth = action.payload.date_of_birth;
		state.user_profile_snackbar.update_about = true;
	},
	prepare: (payload: {
		status: string;
		payload: UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateUserDetail = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile.first_name = action.payload.first_name;
		state.single_user_profile.user_profile.last_name = action.payload.last_name;
		state.single_user_profile.user_profile.recovery_email.email = action.payload.recovery_email.email;

		state.account_settings_page.user_data.first_name = action.payload.first_name;
		state.account_settings_page.user_data.last_name = action.payload.last_name;
		state.account_settings_page.user_data.recovery_email.email = action.payload.recovery_email.email;
	},
	prepare: (payload: {
		status: string;
		payload: UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateUserSkills = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};
export const AddUserCourses = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};
export const addExperienceForUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};
export const AddUserLanguage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const AddHonorsAndAwardsDetailsForUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const AddLicenseAndCertificationDetailsForUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const AddSaveEntityBusinessProfile = {
	reducer: (state: StateInterface, action: AnyAction) => {},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const AddSaveEntityUserProfile = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateServiceDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_category = action.payload.payload.service_category;
		state.single_service_page.service_data.service_type = action.payload.payload.service_type;
		state.single_service_page.service_data.service_subtype = action.payload.payload.service_subtype;
		state.single_service_page.service_data.tags = action.payload.payload.tags;
		state.single_service_page.service_data.price = action.payload.payload.price;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.service_details = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateServiceOverView = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.description = action.payload.payload.overview;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.service_overview = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateServiceSpecification = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_specifications = action.payload.payload.service_specifications;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.service_specification = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateServiceProvider = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.service_provider = action.payload.payload.service_provider;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.about_service_provider = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateServiceAreasOfOperation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.areas_of_operation = action.payload.payload.areas_of_operation;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.areas_of_operation = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const updateServiceLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.location.title = action.payload.payload.location_title;
		state.single_service_page.service_data.location.pincode = action.payload.payload.location_pincode;
		state.single_service_page.service_data.search_metrics.location_coordinates =
			action.payload.payload.location_coordinates;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.service_map = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const followUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.is_business_profile) {
			state.single_business_profile.business_profile.followers = action.payload.addFollower;
			// state.single_business_profile.business_profile.following =
			// action.payload.payload.addFollowing;
		} else {
			state.single_user_profile.user_profile.followers = action.payload.addFollower;
			// state.single_user_profile.user_profile.following =
			// action.payload.payload.addFollowing;
		}
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const unfollowUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.is_business_profile) {
			state.single_business_profile.business_profile.followers = action.payload.removefollower;
			// state.single_business_profile.business_profile.following =
			//   action.payload.payload.removefollowing;
		} else {
			state.single_user_profile.user_profile.followers = action.payload.removefollower;
			// state.single_user_profile.user_profile.following =
			//   action.payload.payload.removefollowing;
		}
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const UpdatePropertyDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.transaction_type = action.payload.payload.transaction_type;
		state.single_property_page.property.property_category = action.payload.payload.property_category;
		state.single_property_page.property.property_type = action.payload.payload.property_type;
		state.single_property_page.property.property_subtype = action.payload.payload.property_subtype;
		state.single_property_page.property.price = action.payload.payload.price;
		state.single_property_page.property.security_deposit = action.payload.payload.security_deposit;
		state.single_property_page.property.possession_date = action.payload.payload.possession_date;
		state.single_property_page.property.property_age = action.payload.payload.property_age;
		state.single_property_page.property.availability = action.payload.payload.availability;
		state.single_property_page.property.unit_facing = action.payload.payload.unit_facing;
		state.single_property_page.property.land_facing = action.payload.payload.land_facing;
		state.single_property_page.property.tags = action.payload.payload.tags;
		state.single_property_page.property.status = action.payload.payload.status;
		state.single_property_page.property.published = action.payload.payload.published;
		state.property_snackbar.property_details = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateConfigAndArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.area = action.payload.payload.area;
		state.single_property_page.property.plot_dimensions = action.payload.payload.plot_dimensions.map(
			(plot_dimension: string) =>
				plot_dimension.replace(/_/g, " ").charAt(0).toUpperCase() + plot_dimension.replace(/_/g, " ").slice(1),
		);
		state.single_property_page.property.other_plot_dimensions = action.payload.payload.other_plot_dimensions;
		state.single_property_page.property.furnished = action.payload.payload.furnished;
		state.single_property_page.property.bhk = action.payload.payload.bhk;
		state.single_property_page.property.bathroom = action.payload.payload.bathroom;
		state.single_property_page.property.balconies = action.payload.payload.balconies;
		state.single_property_page.property.floors = action.payload.payload.floors;
		state.single_property_page.property.floor_no = action.payload.payload.floor_no;
		state.single_property_page.property.no_of_units = action.payload.payload.no_of_units;
		state.single_property_page.property.no_of_seats = action.payload.payload.no_of_seats;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.property_config = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdatePropertyRegulatoryInfo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload?.regulatory_permit) {
			state.single_property_page.property.regulatory_permit = action.payload.payload.regulatory_permit;
		}
		if (action.payload.payload?.broker_license) {
			state.single_property_page.property.broker_license = action.payload.payload.broker_license;
		}
		if (action.payload.payload?.rera_number !== undefined) {
			state.single_property_page.property.rera_number = action.payload.payload.rera_number;
		}
		if (action.payload.payload?.published !== undefined) {
			state.single_property_page.property.published = action.payload.payload.published;
		}
		if (action.payload.payload?.status !== undefined) {
			state.single_property_page.property.status = action.payload.payload.status;
		}
		state.property_snackbar.property_regulatory_info = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateAboutDeveloperProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.builder.name = action.payload.payload.developer_name;
		state.single_property_page.property.builder.slug = action.payload.payload.developer_slug;
		state.single_property_page.property.builder.description = action.payload.payload.developer_description;
		state.single_property_page.property.builder.year_est = action.payload.payload.developer_year_est;
		state.single_property_page.property.builder.ongoing_projects = action.payload.payload.developer_ongoing_projects;
		state.single_property_page.property.builder.completed_projects =
			action.payload.payload.developer_completed_projects;
		state.single_property_page.property.builder.upcoming_projects = action.payload.payload.developer_upcoming_projects;
		state.single_property_page.property.builder.total_projects = action.payload.payload.developer_total_projects;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.property_developer = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const updateLocationProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.location.title = action.payload.payload.location_title;
		state.single_property_page.property.location.pincode = action.payload.payload.location_pincode;
		state.single_property_page.property.search_metrics.location_coordinates =
			action.payload.payload.location_coordinates;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.property_location_coordinates = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateAboutLocationProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.location.about_location = action.payload.payload.about_location;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.property_about_location = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateAboutProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.about_property = action.payload.payload.about_property;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.about_property = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateAmenities = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.amenities = action.payload.payload.amenities;
		state.single_property_page.property.status = action.payload.payload.status;
		state.single_property_page.property.published = action.payload.payload.published;
		state.property_snackbar.property_amenities = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateOverView = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.description = action.payload.payload.overview;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.single_property_page.property.manual_overview = action.payload.payload.manual_overview;
		state.property_snackbar.property_overview = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateAiOverView = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.description = action.payload.overview;
		state.single_property_page.property.published = action.payload.published;
		state.single_property_page.property.status = action.payload.status;
		state.property_snackbar.property_overview = true;
		state.single_property_page.property.is_overview_ai_generated = action.payload.is_overview_ai_generated;
		state.single_property_page.property.manual_overview = action.payload.manual_overview;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const changeDataTuning = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.use_data_for_fine_tuning = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const UpdateVicinity = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.vicinity = action.payload.payload.vicinity;
		state.single_property_page.property.status = action.payload.payload.status;
		state.single_property_page.property.published = action.payload.payload.published;
		state.property_snackbar.property_vicinity = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const setCreateBoost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost = action.payload;
	},
	prepare: (payload: StateInterface["boost"]) => ({ payload: payload }),
};

export const setStartBoost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost = action.payload;
		state.boost_msg = true;
	},
	prepare: (payload: {
		status: string;
		payload: BoostInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const EmployeeRequest = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.employees = action.payload.employees;
		state.single_business_profile.business_profile.employee_details = action.payload.employee_details;
	},
	prepare: (payload: {
		status: string;
		payload: {
			newEmployeeRequest: BusinessProfileInterface;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload.newEmployeeRequest,
	}),
};
export const acceptEmployeeRequest = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.employee_details = action.payload.employee_details;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};
export const deleteEmployeeRequest = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.employee_details = action.payload.employee_details;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const updateBoost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			state.boost_msg = true;
			state.boost = action.payload.payload;
		} else {
			state.boost_error = action.error;
			state.helper_text = action.error;
		}
	},

	prepare: (payload: {
		status: string;
		payload: BoostInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => {
		return {
			payload: payload,
			error: payload.error,
		};
	},
};

export const cancelBoost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost = action.payload;
		state.boost_msg = true;
	},
	prepare: (payload: {
		status: string;
		payload: BoostInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const SetDefaultLookingFor = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.looking_for = action.payload;
	},
	prepare: (payload: StateInterface["looking_for"]) => ({
		payload: payload,
	}),
};

export const SetDefaultLocationBoost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.location_boost = action.payload;
	},
	prepare: (payload: StateInterface["location_boost"]) => ({
		payload: payload,
	}),
};

export const fetchReceiptPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.receipt_page = action.payload;
	},
	prepare: (payload: StateInterface["receipt_page"]) => ({
		payload: payload,
	}),
};

export const fetchAccountSettingsPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page = action.payload;
	},
	prepare: (payload: StateInterface["account_settings_page"]) => ({
		payload: payload,
	}),
};

export const fetchAccountSettingsProfileKycInfo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.kyc_info = action.payload.kyc_data;
		state.account_setting_loading.loading_kyc = false;
	},
	prepare: (payload: { kyc_data: KYCInformationBusinessProfile | null }) => ({
		payload: payload,
	}),
};

export const addPropertyImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.images = action.payload.payload.images;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.property_images_upload = true;
	},
	prepare: (payload: StateInterface["single_property_page"]["property"]["images"]) => ({
		payload: payload,
	}),
};

export const createNewProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.properties = action.payload;
		state.new_listing_url = action.payload[0]?.slug;
	},
	prepare: (payload: {
		status: string;
		payload: PropertyInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const createNewPropertyWithUploadTool = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// ! A failed create (refused, missing fields, already owns a business) carries no listing: say so and
		// ! change nothing, instead of throwing on propertyData[0] and leaving the wizard stuck
		if (!Array.isArray(action.payload?.propertyData) || action.payload.propertyData.length === 0) {
			state.new_property_data.snack_property_value = true;
			state.new_property_data.snack_text = "Error while creating Property";
			return;
		}
		{
			action.payload.propertyData[0].transaction_type === "ofb"
				? (action.payload.propertyData[0].created_by.is_business_profile
						? (state.single_business_profile.ofb_properties = action.payload.propertyData)
						: (state.single_user_profile.ofb_properties = action.payload.propertyData),
					(state.new_property_data.snack_ofb_value = true),
					(state.new_property_data.snack_text =
						action.payload.propertyData.length > 0 ? "OFB Created Successfully" : "Error while creating OFB"))
				: ((state.single_business_profile.properties = action.payload.propertyData),
					(state.new_property_data.snack_property_value = true),
					(state.new_property_data.snack_text =
						action.payload.propertyData.length > 0 ? "Property Created Successfully" : "Error while creating Property"),
					(state.new_property_data._id = action.payload.propertyData[0]._id));
			if (state.business_dashboard.search_listings_response.filter_listing_type === "property") {
				state.business_dashboard.search_listings_response.filter_status === "all";
				const propertyIds: Array<string | ObjectId> = action.payload.propertyData.map(
					(property: PropertyInterface) => property._id,
				);
				state.business_dashboard.search_listings_response.listing_ids = propertyIds;
				state.business_dashboard.search_listings_response.listings_count = propertyIds.length;
			}

			state.new_listing_url = action.payload.creator_slug;
			if (state.redux_session && state.redux_session.user && state.redux_session.user.dbUser) {
				state.redux_session.user.dbUser.profile_context = action.payload.user_profile_context;
			}
		}
	},
	prepare: (payload: {
		status: string;
		payload: PropertyInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const setPreviousPropertyData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data = action.payload;
	},
	prepare: (payload: StateInterface["new_property_data"] | null) => ({
		payload: payload,
	}),
};

export const setPreviousServiceData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data = action.payload;
	},
	prepare: (payload: StateInterface["new_service_data"] | null) => ({
		payload: payload,
	}),
};

export const setPostPropertyWithUploadToolSnackbarError = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.snack_property_value = action.payload;
		state.new_property_data.snack_text = action.payload === true ? "Error while creating Property" : "";
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const createNewServiceWithUploadTool = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// ! A failed create carries no service: say so and leave the profile's services and the
		// ! dashboard's listing ids as they are, instead of overwriting them with nothing
		if (!Array.isArray(action.payload?.serviceData) || action.payload.serviceData.length === 0) {
			state.new_service_data.snack_value = true;
			state.new_service_data.snack_text = "Error while creating service";
			return;
		}
		state.new_service_data.snack_value = true;
		state.new_service_data.snack_text =
			action.payload.serviceData.length > 0 ? "Service Created Successfully" : "Error while creating service";
		state.single_business_profile.services = action.payload.serviceData;
		state.new_listing_url = action.payload.business_profile_slug;
		if (state.business_dashboard.search_listings_response.filter_listing_type === "service") {
			state.business_dashboard.search_listings_response.filter_status === "all";
			const serviceIds: Array<string | ObjectId> = action.payload.serviceData.map(
				(service: ServiceInterface) => service._id,
			);
			state.business_dashboard.search_listings_response.listing_ids = serviceIds;
			state.business_dashboard.search_listings_response.listings_count = serviceIds.length;
		}
	},
	prepare: (payload: {
		status: string;
		payload: ServiceInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const AllowcopyProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.allow_copy = action.payload.allow_copy;
	},
	prepare: (payload: {
		status: string;
		payload: PropertyInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const ShowContactDetailsProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.show_contact_details = action.payload.show_contact_details;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const ShowContactDetailsService = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.show_contact_details = action.payload.show_contact_details;
	},
	prepare: (payload: {
		status: string;
		payload: PropertyInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const CreateNewService = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.services = action.payload;
		state.new_listing_url = action.payload[0].slug;
	},
	prepare: (payload: {
		status: string;
		payload: ServiceInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const createNewOFBProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.ofb_properties = action.payload;
		state.new_listing_url = action.payload[0].slug;
	},
	prepare: (payload: {
		status: string;
		payload: PropertyInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const updatePropertyImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refused or failed save (payload null) leaves the listing's photos as they were. */
		if (!action.payload?.payload?.images) return;
		state.single_property_page.property.images = action.payload.payload.images;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.property_images_edit = true;
	},
	prepare: (payload: StateInterface["single_property_page"]["property"]["images"]) => ({
		payload: payload,
	}),
};

export const cropPropertyImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.images = action.payload.payload.images;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.property_images_edit = true;
	},
	prepare: (payload: StateInterface["single_property_page"]["property"]["images"]) => ({
		payload: payload,
	}),
};

export const deletePropertyImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.images = action.payload.payload.images;
		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
		state.property_snackbar.property_images_delete = true;
	},
	prepare: (payload: StateInterface["single_property_page"]["property"]["images"]) => ({
		payload: payload,
	}),
};

export const deleteServiceImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.images = action.payload.payload.images;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.service_images_delete = true;
	},
	prepare: (payload: StateInterface["single_service_page"]["service_data"]["images"]) => ({
		payload: payload,
	}),
};

export const deleteBusinessProfilePicture = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.display_picture_url = action.payload.payload;
		state.profile_context.business_profile_image.exists = false;
		state.business_profile_snackbar.delete_profile_picture = true;
	},
	prepare: (payload: StateInterface["single_business_profile"]["business_profile"]["display_picture_url"]) => ({
		payload: payload,
	}),
};

export const deleteUserProfilePicture = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile.profile_picture = action.payload.payload;
		state.account_settings_page.user_data.profile_picture = action.payload.payload;
		state.user_profile_snackbar.delete_profile_picture = true;
	},
	prepare: (payload: StateInterface["single_user_profile"]["user_profile"]["profile_picture"]) => ({
		payload: payload,
	}),
};

export const deleteUserCoverImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile.cover_image = action.payload.payload;
	},
	prepare: (payload: StateInterface["single_user_profile"]["user_profile"]["cover_image"]) => ({
		payload: payload,
	}),
};

export const CreateNewSubscription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.subscriptions.push(action.payload.create_new_subscription);
		state.account_settings_page.user_data.beegru_points = action.payload.debit_beegru_points.beegru_points;
		state.account_settings_page.user_beegru_points_transactions.push(action.payload.store_beegru_points_transaction);
		state.business_dashboard.business_profile.subscription_information.subscription_id =
			action.payload.create_new_subscription._id;
		state.business_dashboard.business_profile.subscription_information.plan_slug =
			action.payload.create_new_subscription.plan_slug;
		state.business_dashboard.business_profile.subscription_information.end_date =
			action.payload.create_new_subscription.end_date;
		state.business_dashboard.business_profile.subscription_information.active =
			action.payload.create_new_subscription.active;
		state.accountsetting_snackbar.subscription_snackbar = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const RenewSubscription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.subscriptions.filter(
			(subscription: any) => subscription._id === action.payload.renew_subscription._id,
		)[0] = action.payload.renew_subscription;
		state.account_settings_page.user_data.beegru_points = action.payload.debit_beegru_points.beegru_points;
		state.account_settings_page.user_beegru_points_transactions.push(action.payload.store_beegru_points_transaction);
		state.accountsetting_snackbar.subscription_snackbar = true;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["subscriptions"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const CancelSubscription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.subscriptions.push(action.payload.cancel_subscription);
		state.accountsetting_snackbar.cancel_subscription_snackbar = true;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["subscriptions"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const BusinessDashboardCreateNewSubscription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.subscription_information.subscription_id =
			action.payload.create_new_subscription._id;
		state.business_dashboard.business_profile.subscription_information.plan_slug =
			action.payload.create_new_subscription.plan_slug;
		state.business_dashboard.business_profile.subscription_information.end_date =
			action.payload.create_new_subscription.end_date;
		state.business_dashboard.business_profile.subscription_information.active =
			action.payload.create_new_subscription.active;
		state.business_dashboard.business_profile.subscription_information.cancelled =
			action.payload.create_new_subscription.cancelled;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const BusinessDashboardRenewSubscription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.subscription_information =
			action.payload.update_user_or_bp.subscription_information;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["subscriptions"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const BusinessDashboardCancelSubscription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.subscription_information =
			action.payload.updateBusinessProfile.subscription_information;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["subscriptions"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const ProfileToUpgrade = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.profile_to_upgrade = action.payload;
	},
	prepare: (payload: StateInterface["profile_to_upgrade"]) => ({
		payload: payload,
	}),
};

export const ProfileToUpgradeId = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.profile_to_upgrade_id = action.payload;
	},
	prepare: (payload: StateInterface["profile_to_upgrade_id"]) => ({
		payload: payload,
	}),
};

export const ProfileToUpgradeType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.profile_to_upgrade_type = action.payload;
	},
	prepare: (payload: StateInterface["profile_to_upgrade_type"]) => ({
		payload: payload,
	}),
};

export const deletePropertyDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.properties = action.payload.payload.properties;
		state.single_business_profile.propCount = action.payload.payload.propertiesCount;
		state.business_profile_snackbar.delete_property_listing = true;
		const propertyIds: Array<string | ObjectId> = action.payload.payload.properties.map(
			(property: PropertyInterface) => property._id,
		);
		state.business_dashboard.search_listings_response.listing_ids = propertyIds;
		state.business_dashboard.search_listings_response.listings_count = propertyIds.length;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const updatePersonalInfo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.first_name = action.payload.first_name;
		state.account_settings_page.user_data.last_name = action.payload.last_name;
		state.account_settings_page.user_data.work_experience = action.payload.work_experience;
		state.account_settings_page.user_data.recovery_email.email = action.payload.recovery_email.email;
		state.account_settings_page.user_data.address.line1 = action.payload.address.line1;
		state.account_settings_page.user_data.address.line2 = action.payload.address.line2;
		state.account_settings_page.user_data.address.city = action.payload.address.city;
		state.account_settings_page.user_data.address.state = action.payload.address.state;
		state.account_settings_page.user_data.address.pincode = action.payload.address.pincode;
		state.account_settings_page.user_data.address.country = action.payload.address.country;
		state.accountsetting_snackbar.personalinfo_snackbar = true;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const updateUserKycInfo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.kyc_info = action.payload.payload;
		state.accountsetting_snackbar.kyc_snackbar = true;
	},
	prepare: (payload: StateInterface["user"]["kyc_info"]) => ({
		payload: payload,
	}),
};

export const updateBusinessProfileKycInfo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.kyc_info = action.payload.payload;
		state.accountsetting_snackbar.kyc_snackbar = true;
	},
	prepare: (payload: StateInterface["user"]["kyc_info"]) => ({
		payload: payload,
	}),
};

export const addProfilePicture = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.profile_picture = action.payload.profile_picture;
		state.single_user_profile.user_profile.profile_picture = action.payload.profile_picture;
		if (action.payload.snack_value === "upload_profile_picture") {
			state.user_profile_snackbar.upload_profile_picture = true;
		} else if (action.payload.snack_value === "edit_profile_picture") {
			state.user_profile_snackbar.edit_profile_picture = true;
		} else if (action.payload.snack_value === "corrupt_file") {
			state.user_profile_snackbar.corrupt_file = true;
		}
	},
	prepare: (payload: StateInterface["user"]["profile_picture"]) => ({
		payload: payload,
	}),
};

export const addCoverImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile.cover_image = action.payload.cover_image;
		if (action.payload.snack_value === "upload_cover_image") {
			state.user_profile_snackbar.upload_cover_image = true;
		} else if (action.payload.snack_value === "edit_cover_image") {
			state.user_profile_snackbar.edit_cover_image = true;
		} else if (action.payload.snack_value === "corrupt_file") {
			state.user_profile_snackbar.corrupt_file = true;
		}
	},
	prepare: (payload: StateInterface["single_user_profile"]["user_profile"]["cover_image"]) => ({
		payload: payload,
	}),
};

export const addBusinessProfilePicture = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.display_picture_url = action.payload.profile_picture;
		state.profile_context.business_profile_image.exists = action.payload.profile_picture.exists;
		state.profile_context.business_profile_image.file_id = action.payload.profile_picture.file_id;
		if (action.payload.snack_value === "upload_profile_picture") {
			state.business_profile_snackbar.upload_profile_picture = true;
		} else if (action.payload.snack_value === "edit_profile_picture") {
			state.business_profile_snackbar.edit_profile_picture = true;
		}
	},
	prepare: (payload: StateInterface["business_profile"]["display_picture_url"]) => ({
		payload: payload,
	}),
};

export const addServiceImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.images = action.payload.payload.images;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.service_images_upload = true;
	},
	prepare: (payload: StateInterface["single_service_page"]["service_data"]["images"]) => ({
		payload: payload,
	}),
};

export const updateServiceImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.images = action.payload.payload.images;
		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.service_snackbar.service_images_edit = true;
	},
	prepare: (payload: StateInterface["single_service_page"]["service_data"]["images"]) => ({
		payload: payload,
	}),
};

export const cropServiceImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.images = action.payload.payload;
		state.service_snackbar.service_images_edit = true;
	},
	prepare: (payload: StateInterface["single_service_page"]["service_data"]["images"]) => ({
		payload: payload,
	}),
};

export const createNewBusinessProfile = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refused create (for example 409 already_owns_business) carries no business: keep the acting profile. */
		if (!action.payload || !action.payload.business_profile_id) return;
		state.profile_context.kyc_info = {
			kyc_status: action.payload.business_profile_kyc_status,
		};
		state.new_business_profile_url = action.payload.business_profile_slug;
		state.profile_context.is_business_profile = true;
		state.profile_context.business_profile_id = action.payload.business_profile_id;
		state.profile_context.business_profile_image = action.payload.business_profile_image;
		state.profile_context.page_type = action.payload.business_profile_type;
		state.profile_context.page_slug = action.payload.business_profile_slug;
		state.profile_context.page_title = action.payload.business_profile_title;
		// state.profile_context.kyc_info.kyc_status = action.payload.business_profile_kyc_status;
		state.profile_context.subscription_information = action.payload.business_profile_subscription_information;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const deletePropertyOFBDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload.url.includes("users")) {
			state.single_user_profile.ofb_properties = action.payload.payload.properties;
			state.single_user_profile.propertiesCount = action.payload.payload.ofbCount;
			state.business_profile_snackbar.delete_property_listing = true;
		} else {
			state.single_business_profile.ofb_properties = action.payload.payload.properties;
			state.single_business_profile.ofbpropertiesCount = action.payload.payload.ofbCount;
			state.business_profile_snackbar.delete_property_listing = true;
		}
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};
export const deletePropertyOFBUsers = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.ofb_properties = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: PropertyInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const createNewOFBPropertyUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.ofb_properties = action.payload;
		state.new_listing_url = action.payload[0].slug;
	},
	prepare: (payload: {
		status: string;
		payload: PropertyInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};
export const updateBooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload.is_business_profile) {
			if (action.payload.tabValue === 0) {
				const postSwrIndex: number = state.single_business_profile.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload[0]._id,
				);
				state.single_business_profile.posts[postSwrIndex].booms = action.payload.payload[0].booms;
			} else {
				if (action.payload.tabValue === 1) {
					state.single_post_page.post.booms = action.payload.payload[0].booms;
				} else {
					if (action.payload.payload.url.includes("users")) {
						const postSavedIndex: number = state.single_user_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_user_profile.saved_items.posts[postSavedIndex].booms = action.payload.payload[0].booms;
					} else {
						const postSavedIndex: number = state.single_business_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_business_profile.saved_items.posts[postSavedIndex].booms = action.payload.payload[0].booms;
					}
				}
			}
		} else {
			if (action.payload.tabValue === 0) {
				const postSwrIndex: number = state.single_user_profile.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload[0]._id,
				);
				state.single_user_profile.posts[postSwrIndex].booms = action.payload.payload[0].booms;
			} else {
				if (action.payload.tabValue === 1) {
					state.single_post_page.post.booms = action.payload.payload[0].booms;
				} else {
					if (action.payload.payload.url.includes("users")) {
						const postSavedIndex: number = state.single_user_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_user_profile.saved_items.posts[postSavedIndex].booms = action.payload.payload[0].booms;
					} else {
						const postSavedIndex: number = state.single_business_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_business_profile.saved_items.posts[postSavedIndex].booms = action.payload.payload[0].booms;
					}
				}
			}
		}
	},
	prepare: (payload: {
		tabValue: number;
		response: {
			status: string;
			payload: any;
			missingFields: Array<string> | null;
			error: string | null;
		};
	}) => ({
		payload: { payload: payload.response.payload, tabValue: payload.tabValue },
	}),
};
export const RemoveBooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload.is_business_profile) {
			if (action.payload.tabValue === 0) {
				const postSwrIndex: number = state.single_business_profile.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload[0]._id,
				);
				state.single_business_profile.posts[postSwrIndex].booms = action.payload.payload[0].booms;
			} else {
				if (action.payload.tabValue === 1) {
					state.single_post_page.post.booms = action.payload.payload[0].booms;
				} else {
					if (action.payload.payload.url.includes("users")) {
						const postSavedIndex: number = state.single_user_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_user_profile.saved_items.posts[postSavedIndex].booms = action.payload.payload[0].booms;
					} else {
						const postSavedIndex: number = state.single_business_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_business_profile.saved_items.posts[postSavedIndex].booms = action.payload.payload[0].booms;
					}
				}
			}
		} else {
			if (action.payload.tabValue === 0) {
				const postSwrIndex: number = state.single_user_profile.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload[0]._id,
				);
				state.single_user_profile.posts[postSwrIndex].booms = action.payload.payload[0].booms;
			} else {
				if (action.payload.tabValue === 1) {
					state.single_post_page.post.booms = action.payload.payload[0].booms;
				} else {
					if (action.payload.payload.url.includes("users")) {
						const postSavedIndex: number = state.single_user_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_user_profile.saved_items.posts[postSavedIndex].booms = action.payload.payload[0].booms;
					} else {
						const postSavedIndex: number = state.single_business_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload[0]._id,
						);
						state.single_business_profile.saved_items.posts[postSavedIndex].booms = action.payload.payload[0].booms;
					}
				}
			}
		}
	},
	prepare: (payload: {
		tabValue: number;
		response: {
			status: string;
			payload: any;
			missingFields: Array<string> | null;
			error: string | null;
		};
	}) => ({
		payload: { payload: payload.response.payload, tabValue: payload.tabValue },
	}),
};
export const updateBlasts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload.is_business_profile) {
			if (action.payload.tabValue === 0) {
				const postSwrIndex: number = state.single_business_profile.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload.post[0]._id,
				);
				state.single_business_profile.posts[postSwrIndex].blasts = action.payload.payload.post[0].blasts;
			} else {
				if (action.payload.tabValue === 1) {
					state.single_post_page.post.blasts = action.payload.payload.post[0].blasts;
				} else {
					if (action.payload.payload.url.includes("users")) {
						const postSavedIndex: number = state.single_user_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload.post[0]._id,
						);
						state.single_user_profile.saved_items.posts[postSavedIndex].blasts = action.payload.payload.post[0].blasts;
					} else {
						const postSwrIndex: number = state.single_business_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload.post[0]._id,
						);
						state.single_business_profile.saved_items.posts[postSwrIndex].blasts =
							action.payload.payload.post[0].blasts;
					}
				}
			}
		} else {
			if (action.payload.tabValue === 0) {
				const postSwrIndex: number = state.single_user_profile.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload.post[0]._id,
				);
				state.single_user_profile.posts[postSwrIndex].blasts = action.payload.payload.post[0].blasts;
			} else {
				if (action.payload.tabValue === 1) {
					state.single_post_page.post.blasts = action.payload.payload.post[0].blasts;
				} else {
					if (action.payload.payload.url.includes("users")) {
						const postSavedIndex: number = state.single_user_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload.post[0]._id,
						);
						state.single_user_profile.saved_items.posts[postSavedIndex].blasts = action.payload.payload.post[0].blasts;
					} else {
						const postSwrIndex: number = state.single_business_profile.saved_items.posts.findIndex(
							(post: PostInterface) => post._id === action.payload.payload.post[0]._id,
						);
						state.single_business_profile.saved_items.posts[postSwrIndex].blasts =
							action.payload.payload.post[0].blasts;
					}
				}
			}
		}
	},
	prepare: (payload: {
		tabValue: number;
		response: {
			status: string;
			payload: any;
			missingFields: Array<string> | null;
			error: string | null;
		};
	}) => ({
		payload: { payload: payload.response.payload, tabValue: payload.tabValue },
	}),
};

export const CreateNewPostUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.type === "reshare") {
		} else {
			if (action.payload.payload.is_business_profile) {
				state.single_business_profile.posts = action.payload.payload.posts;
				state.postSwrForBusiness = action.payload.payload;
			} else {
				state.single_user_profile.posts = action.payload.payload.posts;
				state.postSwrForUser = action.payload.payload;
			}
		}
	},
	prepare: (payload: {
		type: string;
		response: {
			status: string;
			payload: any;
			missingFields: Array<string> | null;
			error: string | null;
		};
	}) => ({
		payload: { payload: payload.response.payload, type: payload.type },
	}),
};
export const CreateNewCommentsUserPost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.posts.map((post: any) => {
			if (post._id === action.payload._id) {
				post.comments = action.payload.comments;
			}
		});
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => {
		return {
			payload: payload.payload,
		};
	},
};
export const deletePostUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.is_business_profile) {
			state.single_business_profile.posts = action.payload.posts;
			state.postSwrForBusiness = action.payload.posts;
		} else {
			state.single_user_profile.posts = action.payload.posts;
			state.postSwrForUser = action.payload.posts ? action.payload.posts : [];
		}
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => {
		return {
			payload: payload.payload,
		};
	},
};
export const ReplyToComment = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload.type.includes("post")) {
			state.single_post_page.post.comments = action.payload.payload.comments;
		}
		if (action.payload.payload.is_business_profile) {
			if (action.payload.tabValue === 0) {
				const postSwrIndex: number = state.single_business_profile.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload[0]._id,
				);
				state.single_business_profile.posts[postSwrIndex].comments = action.payload.payload[0].comments;
			} else {
				const postSwrIndex: number = state.single_business_profile.saved_items.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload[0]._id,
				);
				state.single_business_profile.saved_items.posts[postSwrIndex].comments = action.payload.payload[0].comments;
			}
		} else {
			if (action.payload.tabValue === 0) {
				const postSwrIndex: number = state.single_user_profile.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload[0]._id,
				);
				state.single_user_profile.posts[postSwrIndex].comments = action.payload.payload[0].comments;
			} else {
				const postSwrIndex: number = state.single_user_profile.saved_items.posts.findIndex(
					(post: PostInterface) => post._id === action.payload.payload[0]._id,
				);
				state.single_user_profile.saved_items.posts[postSwrIndex].comments = action.payload.payload[0].comments;
			}
		}
	},
	prepare: (payload: {
		tabValue: number;
		response: {
			status: string;
			payload: any;
			missingFields: Array<string> | null;
			error: string | null;
		};
	}) => ({
		payload: { payload: payload.response.payload, tabValue: payload.tabValue },
	}),
};

export const deleteServiceDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.services = action.payload.payload.services;
		state.single_business_profile.servicesCount = action.payload.payload.serviceCount;
		state.business_profile_snackbar.delete_service_listing = true;
		const serviceIds: Array<string | ObjectId> = action.payload.payload.services.map(
			(service: ServiceInterface) => service._id,
		);
		state.business_dashboard.search_listings_response.listing_ids = serviceIds;
		state.business_dashboard.search_listings_response.listings_count = serviceIds.length;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const setProfileContext = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload) {
			state.profile_context = {
				is_business_profile: action.payload.profile_context.is_business_profile,
				user_id: action.payload._id,
				business_profile_id: action.payload.profile_context.business_profile_id,
				business_profile_image: action.payload.profile_context.business_profile_image,
				page_type: action.payload.profile_context.page_type,
				page_title: action.payload.profile_context.page_title,
				page_slug: action.payload.profile_context.page_slug,
				subscription_information: action.payload.profile_context.subscription_information,
				kyc_info: action.payload.profile_context.kyc_info,
				remember_choice: action.payload.profile_context.remember_choice,
			};

			if (
				state.redux_session !== null &&
				action.payload.profile_context.is_business_profile === true &&
				state.redux_session.user.dbUser.pages.length < 2 &&
				state.redux_session.user.dbUser.pages.filter(
					(item: any) => item.page_id === action.payload.profile_context.business_profile_id,
				).length === 0
			) {
				state.redux_session.user.dbUser.pages = [
					{
						_id: action.payload.profile_context.business_profile_id,
						page_id: action.payload.profile_context.business_profile_id,
						page_type: action.payload.profile_context.page_type,
						page_title: action.payload.profile_context.page_title,
						page_slug: action.payload.profile_context.page_slug,
						display_picture_url: action.payload.profile_context.business_profile_image,
						subscription_information: action.payload.profile_context.subscription_information,
						kyc_info: action.payload.profile_context.kyc_info,
					},
					...state.redux_session.user.dbUser.pages,
				];
			}
		}
	},
	prepare: (payload: {
		status: string;
		payload: UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const setActionForbooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.actionForBooms = action.payload;
	},
	prepare: (payload: StateInterface["actionForBooms"]) => ({
		payload: payload,
	}),
};
export const profileContext = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.profile_context = action.payload;
	},
	prepare: (payload: StateInterface["profile_context"]) => ({
		payload: {
			...payload,
			user_id: payload.user_id.toString(),
			business_profile_id: payload.business_profile_id.toString(),
		},
	}),
};
export const bookmark = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// if (action.payload.is_business_profile) {
		//   const postIndex: number = state.single_business_profile.posts.findIndex(
		//     (post: PostInterface) => post._id === action.payload[0]._id
		//   );
		//   state.single_business_profile.posts[postIndex].saved_content =
		//     action.payload[0].saved_content;
		//   const postSwrIndex: number = state.postSwrForBusiness.findIndex(
		//     (post: PostInterface) => post._id === action.payload[0]._id
		//   );
		//   state.postSwrForBusiness[postSwrIndex].blasts = action.payload[0].blasts;
		// } else {
		//   const postIndex: number = state.single_user_profile.posts.findIndex(
		//     (post: PostInterface) => post._id === action.payload[0]._id
		//   );
		//   state.single_user_profile.posts[postIndex].saved_content =
		//     action.payload[0].saved_content;
		//   const postSwrIndex: number = state.postSwrForUser.findIndex(
		//     (post: PostInterface) => post._id === action.payload[0]._id
		//   );
		//   state.postSwrForUser[postSwrIndex].blasts = action.payload[0].blasts;
		// }
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface | UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const reportPost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.is_Business_Profile) {
			const postIndex: number = state.single_business_profile.posts.findIndex(
				(post: PostInterface) => post._id === action.payload[0]._id,
			);
			state.single_business_profile.posts[postIndex].flags = action.payload[0].flags;
		} else {
			const postIndex: number = state.single_user_profile.posts.findIndex(
				(post: PostInterface) => post._id === action.payload[0]._id,
			);
			state.single_user_profile.posts[postIndex].flags = action.payload[0].flags;
		}
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface | UserInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const postSwrForBusiness = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.postSwrForBusiness = action.payload;
	},
	prepare: (payload: StateInterface["postSwrForBusiness"]) => ({
		payload: payload,
	}),
};

export const postSwrForUser = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.postSwrForUser = action.payload;
	},
	prepare: (payload: StateInterface["postSwrForUser"]) => ({
		payload: payload,
	}),
};

export const commentSwr = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.comment_swr = action.payload;
	},
	prepare: (payload: StateInterface["comment_swr"]) => ({
		payload: payload,
	}),
};

export const publishproperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.status = action.payload.status;
		state.single_property_page.property.published = action.payload.published;
	},
	prepare: (payload: StateInterface["property"] | any) => ({
		payload: payload.payload,
	}),
};

export const publicproperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property = action.payload.payload;
	},
	prepare: (payload: StateInterface["property"]) => ({
		payload: payload,
	}),
};

export const publishservice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.status = action.payload.payload.status;
		state.single_service_page.service_data.published = action.payload.payload.published;
	},
	prepare: (payload: StateInterface["service"]) => ({
		payload: payload,
	}),
};

export const publicservice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data = action.payload.payload;
	},
	prepare: (payload: StateInterface["service"]) => ({
		payload: payload,
	}),
};

export const OnBoarding = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.user = action.payload.payload;

		/*

		? Also update redux_session so dependent useEffects (e.g. team invite) re-trigger.

		*/

		if (state.redux_session?.user?.dbUser) {
			state.redux_session.user.dbUser.onboarding_complete = true;
		}
	},
	prepare: (payload: StateInterface["user"]) => ({
		payload: payload,
	}),
};
export const updateNofEmployees = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.no_of_employees = action.payload.no_of_employees;
		state.business_profile_snackbar.no_of_employees = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};
export const storeleads = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.cta_leads = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: CtaLeadsInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const updateleads = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.cta_leads = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: CtaLeadsInterface | null;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const deleteDesktopLeads = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refused or failed delete carries no payload: keep the list as it is. */
		if (!action.payload || !action.payload._id) return;
		/* Keep every lead except the deleted one (this used to keep ONLY the deleted one). */
		state.business_dashboard.leads = state.business_dashboard.leads.filter(
			(lead: CtaLeadsInterface) => String(lead._id) !== String(action.payload._id),
		);
		state.business_dashboard.search_leads_response.leads_count -= 1;
	},
	prepare: (payload: {
		status: string;
		payload: CtaLeadsInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const deleteMobileLeads = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refused or failed delete carries no payload: keep the list as it is. */
		if (!action.payload || !action.payload._id) return;
		state.business_dashboard.search_leads_response.lead_ids =
			state.business_dashboard.search_leads_response.lead_ids.filter((leadId: string) => leadId !== action.payload._id);
		state.business_dashboard.search_leads_response.leads_count -= 1;
	},
	prepare: (payload: {
		status: string;
		payload: CtaLeadsInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const setSavedIsPublic = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile = action.payload;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const fetchSinglePost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_post_page = action.payload;
	},
	prepare: (payload: StateInterface["single_post_page"]) => ({
		payload: payload,
	}),
};

export const businessProfileListTabView = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_profile_listings_tab_view = action.payload;
	},
	prepare: (payload: StateInterface["business_profile_listings_tab_view"]) => ({
		payload: payload,
	}),
};

export const setSearchProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.properties = action.payload;
	},
	prepare: (payload: StateInterface["single_business_profile"]["properties_og"]) => ({
		payload: payload,
	}),
};
export const setSearchServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.services = action.payload;
	},
	prepare: (payload: StateInterface["single_business_profile"]["services_og"]) => ({
		payload: payload,
	}),
};
export const setSearchOFBProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.ofb_properties = action.payload;
	},
	prepare: (payload: StateInterface["single_business_profile"]["properties_og"]) => ({
		payload: payload,
	}),
};

export const loadMoreProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		//@ts-ignore
		state.single_business_profile.properties.push(...action.payload.payload);
	},
	prepare: (payload: StateInterface["single_business_profile"]["properties"]) => ({
		payload: payload,
	}),
};

export const loadMoreServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		//@ts-ignore
		state.single_business_profile.services.push(...action.payload.payload);
	},
	prepare: (payload: StateInterface["service_list"]) => ({
		payload: payload,
	}),
};

export const loadMoreOfbProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.ofb_properties.push(
			//@ts-ignore
			...action.payload.payload,
		);
	},
	prepare: (payload: StateInterface["single_business_profile"]["ofb_properties"]) => ({
		payload: payload,
	}),
};

export const loadMoreSavedProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		//@ts-ignore
		state.single_business_profile.saved_items.properties.push(...action.payload.payload);
	},
	prepare: (payload: StateInterface["property"]) => ({
		payload: payload,
	}),
};

export const loadMoreSavedOFB = {
	reducer: (state: StateInterface, action: AnyAction) => {
		//@ts-ignore
		state.single_business_profile.saved_items.ofb.push(...action.payload.payload);
	},
	prepare: (payload: StateInterface["property"]) => ({
		payload: payload,
	}),
};

export const loadMoreUserSavedProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.saved_properties_list = action.payload.payload;
	},
	prepare: (payload: StateInterface["saved_properties_list"]) => ({
		payload: payload,
	}),
};

export const loadMoreSavedServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		//@ts-ignore
		state.single_business_profile.saved_items.services.push(...action.payload.payload);
	},
	prepare: (payload: StateInterface["service"]) => ({
		payload: payload,
	}),
};

export const loadMoreUserSavedServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.saved_services_list = action.payload.payload;
	},
	prepare: (payload: StateInterface["saved_services_list"]) => ({
		payload: payload,
	}),
};

export const loadMoreSavedPosts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		//@ts-ignore
		state.single_business_profile.saved_items.posts.push(...action.payload.payload.posts);
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const loadMoreUserSavedPosts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		//@ts-ignore
		state.single_user_profile.saved_items.posts.push(...action.payload.payload.posts);
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const loadMoreUserSavedOFB = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.saved_items.ofb.push(...action.payload.payload);
	},
	prepare: (payload: StateInterface["property"]) => ({
		payload: payload,
	}),
};

export const loadMoreUserProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		//@ts-ignore
		state.single_user_profile.ofb_properties.push(...action.payload.payload);
	},
	prepare: (payload: StateInterface["single_user_profile"]["ofb_properties"]) => ({
		payload: payload,
	}),
};

export const searchPropertyListings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.searched_property = action.payload.payload;
	},
	prepare: (payload: StateInterface["single_business_profile"]["searched_property"]) => ({
		payload: payload,
	}),
};

export const searchUserPropertyListings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.searched_properties = action.payload.payload;
	},
	prepare: (payload: StateInterface["single_user_profile"]["searched_properties"]) => ({
		payload: payload,
	}),
};

export const changeRecievePromotionalEmails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.user_privacy.send_promotional_emails =
			action.payload.receive_promotional_emails;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const changeAutoplayVideos = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.settings.site_preferences.autoplay_videos = action.payload.autoplay_videos;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const changeDefaultVideoQuality = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.settings.site_preferences.default_video_quality = action.payload.f;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const CreateNewBugReport = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.bug_report = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const CreateNewReport = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.report = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserProfileCoverImageAndProfilePicture = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile._id = action.payload._id;
		state.single_user_profile.user_profile.cover_image = action.payload.cover_image;
		state.single_user_profile.user_profile.profile_picture = action.payload.profile_picture;
		state.single_user_profile.user_profile.first_name = action.payload.first_name;
		state.single_user_profile.user_profile.last_name = action.payload.last_name;
		state.single_user_profile.user_profile.verified = action.payload.verified;
		state.user_profile_loading.userProfileImages = false;
		state.single_user_profile.user_profile_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserProfileFollowersAndFollowings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile.followers = action.payload.followers;
		state.single_user_profile.user_profile.following = action.payload.following;
		state.user_profile_loading.userProfileFollowers = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserProfileAboutDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.user_profile.about = action.payload.about;
		state.single_user_profile.user_profile.phone = action.payload.phone;
		state.single_user_profile.user_profile.recovery_email = action.payload.recovery_email;
		state.single_user_profile.user_profile.country_code = action.payload.country_code;
		state.single_user_profile.user_profile.date_of_birth = action.payload.date_of_birth;
		state.single_user_profile.user_profile.assigned_to = action.payload.assigned_to;
		state.single_user_profile.user_profile.verified = action.payload.verified;
		state.single_user_profile.user_profile.is_subscribed = action.payload.subscription_active;
		// if (action.payload) {
		state.user_profile_loading.userProfileAboutDetails = false;
		// }
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchSingleUserProfilePosts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.posts = action.payload.posts;
		state.single_user_profile.postsCount = action.payload.postsCount;
		state.user_profile_loading.userProfileProfilePostCard = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserProfileListings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.ofb_properties = action.payload.ofb_properties;
		state.single_user_profile.propertiesCount = action.payload.propertiesCount;
		state.user_profile_loading.userProfileOfbPropertyListings = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserProfileReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.reviews = action.payload.reviews;
		state.single_user_profile.reviewsCount = action.payload.reviewsCount;
		state.user_profile_loading.userProfileReviews = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchSingleUserProfileGivenReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.given_reviews = action.payload.reviews;
		state.single_user_profile.given_reviews_count = action.payload.givenReviewsCount;
		state.user_profile_loading.userProfileReviews = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserSavedProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.saved_items.properties = action.payload.saved_item_ids;
		state.single_user_profile.savedPropertiesLength = action.payload.saved_items_count;
		state.user_profile_loading.userProfileSavedProperty = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserSavedPost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.saved_items.posts = action.payload.posts;
		state.single_user_profile.savedPostsLength = action.payload.savedPostLength;
		state.user_profile_loading.userProfileSavedPost = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserSavedService = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.saved_items.services = action.payload.services;
		state.single_user_profile.savedServicesLength = action.payload.savedServicesLength;
		state.user_profile_loading.userProfileSavedService = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchSingleUserSavedOFB = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.saved_items.ofb = action.payload.properties;
		state.single_user_profile.savedOFBLength = action.payload.propertiesCount;
		state.user_profile_loading.userProfileSavedOfb = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchSingleUserAnalytics = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.analytics = action.payload.analytics;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setHomeLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.home_location = action.payload;
	},
	prepare: (payload: StateInterface["home_location"]) => ({
		payload: payload,
	}),
};
export const setHomeLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.home_loading = action.payload;
	},
	prepare: (payload: StateInterface["home_loading"]) => ({
		payload: payload,
	}),
};
export const setUserLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.user_profile_loading = action.payload;
	},
	prepare: (payload: StateInterface["user_profile_loading"]) => ({
		payload: payload,
	}),
};

export const fetchHomePageHotPropertiesData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.hot_properties = action.payload.hot_properties;
		// state.home_loading.loading_hot_properties = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};
export const fetchHomePageFeaturedPropertiesData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.featured_properties = action.payload.featured_properties;
		state.home_loading.loading_featured_properties = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};
export const fetchHomePageFeaturedPlotsData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.featured_plots = action.payload.featured_plots;
		state.home_loading.loading_featured_plots = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};

export const fetchHomePageTopVillasData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.top_villas = action.payload.top_villas;
		state.home_loading.loading_top_villas = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};

export const fetchHomePageBeegruTopPicksData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.beegru_top_picks = action.payload.beegru_top_picks;
		state.home_loading.loading_beegru_top_picks = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};
export const fetchHomePageTopServicesData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.top_services = action.payload.top_services;
		// state.home_loading.loading_top_services = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};
// export const fetchHomePageTrendingServicesData = {
// 	reducer: (state: StateInterface, action: AnyAction) => {
// 		state.homePage.trending_services = action.payload.trending_services;
// 		state.home_loading.loading_trending_services = false;
// 	},
// 	prepare: (payload: StateInterface["homePage"]) => ({
// 		payload: payload,
// 	}),
// };
export const fetchHomePageTopUsersData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.top_users = action.payload.top_users;
		state.home_loading.loading_top_users = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};
// export const fetchHomePageTrendingUsersData = {
// 	reducer: (state: StateInterface, action: AnyAction) => {
// 		state.homePage.trending_users = action.payload.trending_users;
// 		state.home_loading.loading_trending_users = false;
// 	},
// 	prepare: (payload: StateInterface["homePage"]) => ({
// 		payload: payload,
// 	}),
// };
export const fetchHomePageTopBusinessProfilesData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.top_business_profiles = action.payload.top_business_profiles;
		// state.home_loading.loading_top_business_profiles = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};

export const fetchHomePageTopDevelopersData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.top_developers = action.payload.top_business_profiles;
		// state.home_loading.loading_top_developers = false;
	},
	prepare: (payload: StateInterface["homePage"]) => ({
		payload: payload,
	}),
};

// export const fetchHomePageTrendingBusinessProfilesData = {
// 	reducer: (state: StateInterface, action: AnyAction) => {
// 		state.homePage.trending_business_profiles = action.payload.trending_business_profiles;
// 		state.home_loading.loading_trending_business_profiles = false;
// 	},
// 	prepare: (payload: StateInterface["homePage"]) => ({
// 		payload: payload,
// 	}),
// };

export const setHomePageBusinessProfileLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload) {
			state.homePage.trending_business_profiles = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
			state.homePage.top_business_profiles = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
		}
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};
export const setHomePagePropertiesLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload) {
			state.homePage.featured_properties = [0, 0, 0, 0, 0, 0];
			state.homePage.hot_properties = [0, 0, 0, 0, 0, 0];
		}
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchBusinessProfileImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile._id = action.payload.business_profile._id;
		state.single_business_profile.business_profile.display_picture_url =
			action.payload.business_profile.display_picture_url;
		state.single_business_profile.business_profile.verified = action.payload.business_profile.verified;
		state.single_business_profile.business_profile.title = action.payload.business_profile.title;
		state.single_business_profile.business_profile.business_profile_type =
			action.payload.business_profile.business_profile_type;
		state.business_profile_loading.businessProfileImages = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessDashboardProfileData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile._id = action.payload.business_profile._id;
		state.business_dashboard.business_profile.display_picture_url = action.payload.business_profile.display_picture_url;
		state.business_dashboard.business_profile.verified = action.payload.business_profile.verified;
		state.business_dashboard.business_profile.kyc_info = action.payload.business_profile.kyc_info;
		state.business_dashboard.business_profile.subscription_information =
			action.payload.business_profile.subscription_information;
		state.business_dashboard.business_profile.title = action.payload.business_profile.title;
		state.business_dashboard.business_profile.business_profile_type =
			action.payload.business_profile.business_profile_type;
		state.business_dashboard.business_profile.email = action.payload.business_profile.email;
		state.business_dashboard.business_profile.phone = action.payload.business_profile.phone;
		state.business_dashboard.business_profile.areas_of_operation = action.payload.business_profile.areas_of_operation;
		state.business_dashboard.business_profile.about.year_established =
			action.payload.business_profile.about.year_established;
		state.business_dashboard.business_profile.languages = action.payload.business_profile.languages;
		state.business_dashboard.business_profile.about.short_description =
			action.payload.business_profile.about.short_description;
		state.business_dashboard.business_profile.about.website_url = action.payload.business_profile.about.website_url;
		state.business_dashboard.business_profile.created_by = action.payload.business_profile.created_by;
		state.business_dashboard.business_profile.admins = action.payload.business_profile.admins;
		state.business_dashboard.business_profile_data_loading = false;
		state.business_dashboard.business_profile.show_contact_details =
			action.payload.business_profile.show_contact_details;
		state.business_dashboard.business_profile.expertise = action.payload.business_profile.expertise;
		state.business_dashboard.business_profile.property_type_specialization =
			action.payload.business_profile.property_type_specialization;
		state.business_dashboard.business_profile.assigned_to = action.payload.business_profile.assigned_to;
		state.business_dashboard.business_profile.regulatory_permits = action.payload.business_profile.regulatory_permits;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileFollowerAndFollowing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.followers = action.payload.followers;
		state.single_business_profile.business_profile.following = action.payload.following;
		state.business_profile_loading.businessProfileFollowers = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileAboutDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about = action.payload.about;
		state.single_business_profile.business_profile.phone = action.payload.phone;
		state.single_business_profile.business_profile.email = action.payload.email;
		state.single_business_profile.business_profile.admins = action.payload.admins;
		state.single_business_profile.business_profile.created_by = action.payload.created_by;
		state.single_business_profile.business_profile.created_at = action.payload.created_at;
		state.single_business_profile.business_profile.slug = action.payload.slug;
		state.single_business_profile.business_profile.assigned_to = action.payload.assigned_to;
		state.business_profile_loading.businessProfileAboutDetails = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileServiceOffered = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.services_offered = action.payload.services_offered;
		state.business_profile_loading.businessProfileServicesOffered = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileSubscription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.subscription_information = action.payload.subscription_information;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileExpertise = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.expertise = action.payload.expertise;
		state.business_profile_loading.businessProfileExpertise = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileBrokerage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.agent_brokerage = action.payload.agent_brokerage;
		state.business_profile_loading.businessProfileBrokerage = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileFaqs = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.faqs = action.payload.faqs;
		state.business_profile_loading.businessProfileFaqs = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileLanguages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.languages = action.payload.languages;
		state.business_profile_loading.businessProfileLanguage = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileLicencesAndCertificates = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.licences_and_certifications =
			action.payload.licences_and_certifications;
		state.business_profile_loading.businessProfileLicences = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileNoOfEmployees = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.no_of_employees = action.payload.no_of_employees;
		state.business_profile_loading.businessProfileEmployees = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileListingProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.properties = action.payload.property;
		state.single_business_profile.propCount = action.payload.propertyCount;
		state.business_profile_loading.businessProfileListingProperty = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.reviews = action.payload.reviews;
		state.single_business_profile.reviewsCount = action.payload.reviewsCount;
		state.business_profile_loading.businessProfileReviews = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileListingService = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.services = action.payload.services;
		state.single_business_profile.servicesCount = action.payload.servicesCount;
		state.business_profile_loading.businessProfileListingService = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileListingOFB = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.ofb_properties = action.payload.OfbProperty;
		state.single_business_profile.ofbpropertiesCount = action.payload.OfbPropertyCount;
		state.business_profile_loading.businessProfileListingOFB = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileLeads = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.leads = action.payload.leads;
		state.business_profile_loading.buinessProfileLeads = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileSavedProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.saved_items.properties = action.payload.properties;
		state.single_business_profile.savedPropertiesLength = action.payload.propertiesCount;
		state.business_profile_loading.businessProfileSavedProperty = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileSavedService = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.saved_items.services = action.payload.services;
		state.single_business_profile.savedServicesLength = action.payload.servicesLength;
		state.business_profile_loading.businessProfileSavedService = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileSavedOFB = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.saved_items.ofb = action.payload.ofb;
		state.single_business_profile.savedOFBLength = action.payload.savedOFBLength;
		state.business_profile_loading.businessProfileSavedOFB = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileSavedPost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.saved_items.posts = action.payload.posts;
		state.single_business_profile.savedPostsLength = action.payload.savedPostLength;
		state.business_profile_loading.businessProfileSavedPost = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileAnalyticsProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.analytics.property_views_per_day = action.payload.property_views_per_day;
		state.business_profile_loading.businessProfileAnalyticsProperty = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileAnalyticsService = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.analytics.service_views_per_day = action.payload.service_views_per_day;
		state.business_profile_loading.businessProfileAnalyticsService = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileAnalyticsPost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.analytics.post_blasts_per_day = action.payload.post_blasts_per_day;
		state.single_business_profile.analytics.post_views_per_day = action.payload.post_views_per_day;
		state.single_business_profile.analytics.post_booms_per_day = action.payload.post_booms_per_day;
		state.business_profile_loading.businessProfileAnalyticsPost = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfileAnalyticsOFBs = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.analytics.ofb_property_views_per_day = action.payload.ofb_property_views_per_day;
		state.business_profile_loading.businessProfileAnalyticsOFB = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileAdminsDetailsAndEmployeeRequest = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.admins = action.payload.business_profile.admins;
		// state.single_business_profile.business_profile.admin_user_details =
		// 	action.payload.business_profile.admin_user_details;
		state.single_business_profile.business_profile.employee_details = action.payload.business_profile.employee_details;
		state.business_profile_loading.businessProfileAdmin = false;
		state.business_profile_loading.businessProfileEmployeeRequest = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchPropertyDataReviewsPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.property_reviews_page.property_data = action.payload.property_data;
	},
	prepare: (payload: StateInterface["property_reviews_page"]) => ({
		payload: payload,
	}),
};
export const fetchPropertyUserDataReviewsPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.property_reviews_page.property_reviews = action.payload.property_reviews;
	},
	prepare: (payload: StateInterface["property_reviews_page"]) => ({
		payload: payload,
	}),
};

export const fetchServiceDataReviewsPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.service_reviews_page.service_data = action.payload.service_data;
	},
	prepare: (payload: StateInterface["service_reviews_page"]) => ({
		payload: payload,
	}),
};
export const fetchServiceUserDataReviewsPageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.service_reviews_page.service_reviews = action.payload.service_data;
	},
	prepare: (payload: StateInterface["service_reviews_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.price = action.payload.property_data.price;
		state.single_property_page.property.price_per_unit = action.payload.property_data.price_per_unit;
		state.single_property_page.property.transaction_type = action.payload.property_data.transaction_type;
		state.single_property_page.property.property_category = action.payload.property_data.property_category;
		state.single_property_page.property.property_type = action.payload.property_data.property_type;
		state.single_property_page.property.property_subtype = action.payload.property_data.property_subtype;
		state.single_property_page.property.tags = action.payload.property_data.tags;
		state.single_property_page.property.is_ofb = action.payload.property_data.is_ofb;
		state.single_property_page.property.unit_facing = action.payload.property_data.unit_facing;
		state.single_property_page.property.land_facing = action.payload.property_data.land_facing;
		state.single_property_page.property.property_age = action.payload.property_data.property_age;
		state.single_property_page.property.security_deposit = action.payload.property_data.security_deposit;
		state.single_property_page.property.availability = action.payload.property_data.availability;
		state.single_property_page.property.possession_date = action.payload.property_data.possession_date;
		state.single_property_page.property.short_description = action.payload.property_data.short_description;
		state.single_property_page.property.created_at = action.payload.property_data.created_at;
		state.single_property_page.property.rera_number = action.payload.property_data.rera_number;
		state.single_property_page.property.regulatory_permit = action.payload.property_data.regulatory_permit;
		state.single_property_page.property.broker_license = action.payload.property_data.broker_license;
		state.single_property_page.property.overview_pointers = action.payload.property_data.overview_pointers;
		state.property_loading.propertyDetails = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchDevelopers = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.developers = action.payload.developers;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchNearbyProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.nearbyProperties = action.payload.nearbyProperties;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const setSinglePropertyTransactionType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.transaction_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyTransactionType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.transaction_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyCurrentPage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.currentPage = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceCurrentPage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.currentPage = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyCategoryType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.property_category = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyCategoryType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.property_category = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.property_type = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.property_type = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertySubType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.property_subtype = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertySubType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.property_subtype = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMinPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.price.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMinPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.price.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMaxPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.price.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMaxPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.price.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertySecurityDeposit = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.security_deposit.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertySecurityDeposit = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.security_deposit.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyCurrency = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.price.currency = action.payload;
		state.new_property_data.price_per_unit.currency = action.payload;
		state.new_property_data.security_deposit.currency = action.payload;
	},
	prepare: (payload: "INR" | "AED") => ({
		payload: payload,
	}),
};

export const setSinglePropertyCurrency = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.price.currency = action.payload;
		state.single_property_page.property.price_per_unit.currency = action.payload;
		state.single_property_page.property.security_deposit.currency = action.payload;
	},
	prepare: (payload: "INR" | "AED") => ({
		payload: payload,
	}),
};

export const setSinglePropertyAvailabilty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.availability = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyAvailabilty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.availability = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertyPossessionDate = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.possession_date = action.payload;
	},
	prepare: (payload: string | Date) => ({
		payload: payload,
	}),
};

export const setCreateNewePropertyPossessionDate = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.possession_date = action.payload;
	},
	prepare: (payload: string | Date) => ({
		payload: payload,
	}),
};

export const setSinglePropertyTags = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.tags = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertyUnitFacing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.unit_facing = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyUnitFacing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.unit_facing = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertyLandFacing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.land_facing = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyLandFacing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.land_facing = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertyAge = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.property_age = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyAge = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.property_age = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyFurnishing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.furnished = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyFurnishing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.furnished = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertyPlotDimensions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.plot_dimensions = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyPlotDimensions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.plot_dimensions = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.description = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyPermitNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.regulatory_permit.permit_number = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyIssuingAuthority = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.regulatory_permit.issuing_authority = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyBrokerLicensePermitNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.broker_license.permit_number = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyBrokerLicensePermitName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.broker_license.permit_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyOtherPlotDimensions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.other_plot_dimensions = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertyLandArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.area.land_area.max.acre = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyLandArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.area.land_area.max.acre = action.payload;
		let land_area: Area = {
			ft: 0,
			mt: 0,
			acre: 0,
		};

		let price_per_unit: {
			min: {
				ft: number;
				mt: number;
			};
			max: {
				ft: number;
				mt: number;
			};
			currency: string;
		} = {
			min: {
				ft: 0,
				mt: 0,
			},
			max: {
				ft: 0,
				mt: 0,
			},
			currency: "INR",
		};

		if (state.new_property_data.area.land_area.land_area_unit === "sqft") {
			land_area = {
				acre: action.payload ? action.payload * 0.0000229568 : 0,
				ft: action.payload ? action.payload : 0,
				mt: action.payload ? action.payload * 0.092903 : 0,
			};
		} else if (state.new_property_data.area.land_area.land_area_unit === "sqmt") {
			land_area = {
				acre: action.payload ? action.payload * 0.000247105 : 0,
				ft: action.payload ? action.payload * 10.7639 : 0,
				mt: action.payload ? action.payload : 0,
			};
		} else if (state.new_property_data.area.land_area.land_area_unit === "acre") {
			land_area = {
				acre: action.payload ? action.payload : 0,
				ft: action.payload ? action.payload * 43560 : 0,
				mt: action.payload ? action.payload * 4046.86 : 0,
			};
		}
		price_per_unit = {
			min: {
				ft:
					state.new_property_data.price.min && land_area.ft !== 0
						? (state.new_property_data.price.min !== null && state.new_property_data.price.min !== 0
								? state.new_property_data.price.min
								: 1) / land_area.ft
						: 0,
				mt:
					state.new_property_data.price.min && land_area.mt !== 0
						? (state.new_property_data.price.min !== null && state.new_property_data.price.min !== 0
								? state.new_property_data.price.min
								: 1) / land_area.mt
						: 0,
			},
			max: {
				ft:
					state.new_property_data.price.max && land_area.ft !== 0
						? (state.new_property_data.price.max !== null && state.new_property_data.price.max !== 0
								? state.new_property_data.price.max
								: 1) / land_area.ft
						: 0,
				mt:
					state.new_property_data.price.max && land_area.mt !== 0
						? (state.new_property_data.price.max !== null && state.new_property_data.price.max !== 0
								? state.new_property_data.price.max
								: 1) / land_area.mt
						: 0,
			},
			currency: "INR",
		};
		state.new_property_data.price_per_unit = price_per_unit;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

interface Area {
	ft: number;
	mt: number;
	acre: number;
}

export const setCreateNewPropertyLandAreaUnit = {
	reducer: (state: StateInterface, action: AnyAction) => {
		let land_area: Area = {
			ft: 0,
			mt: 0,
			acre: 0,
		};

		let price_per_unit: {
			min: {
				ft: number;
				mt: number;
			};
			max: {
				ft: number;
				mt: number;
			};
			currency: string;
		} = {
			min: {
				ft: 0,
				mt: 0,
			},
			max: {
				ft: 0,
				mt: 0,
			},
			currency: "INR",
		};

		state.new_property_data.area.land_area.land_area_unit = action.payload;

		if (action.payload === "sqft") {
			land_area = {
				acre: state.new_property_data.area.land_area.max.acre
					? state.new_property_data.area.land_area.max.acre * 0.0000229568
					: 0,
				ft: state.new_property_data.area.land_area.max.acre ? state.new_property_data.area.land_area.max.acre : 0,
				mt: state.new_property_data.area.land_area.max.acre
					? state.new_property_data.area.land_area.max.acre * 0.092903
					: 0,
			};
		} else if (action.payload === "sqmt") {
			land_area = {
				acre: state.new_property_data.area.land_area.max.acre
					? state.new_property_data.area.land_area.max.acre * 0.000247105
					: 0,
				ft: state.new_property_data.area.land_area.max.acre
					? state.new_property_data.area.land_area.max.acre * 10.7639
					: 0,
				mt: state.new_property_data.area.land_area.max.acre ? state.new_property_data.area.land_area.max.acre : 0,
			};
		} else if (action.payload === "acre") {
			land_area = {
				acre: state.new_property_data.area.land_area.max.acre ? state.new_property_data.area.land_area.max.acre : 0,
				ft: state.new_property_data.area.land_area.max.acre
					? state.new_property_data.area.land_area.max.acre * 43560
					: 0,
				mt: state.new_property_data.area.land_area.max.acre
					? state.new_property_data.area.land_area.max.acre * 4046.86
					: 0,
			};
		}
		price_per_unit = {
			min: {
				ft:
					state.new_property_data.price.min && land_area.ft !== 0
						? (state.new_property_data.price.min !== null && state.new_property_data.price.min !== 0
								? state.new_property_data.price.min
								: 1) / land_area.ft
						: 0,
				mt:
					state.new_property_data.price.min && land_area.mt !== 0
						? (state.new_property_data.price.min !== null && state.new_property_data.price.min !== 0
								? state.new_property_data.price.min
								: 1) / land_area.mt
						: 0,
			},
			max: {
				ft:
					state.new_property_data.price.max && land_area.ft !== 0
						? (state.new_property_data.price.max !== null && state.new_property_data.price.max !== 0
								? state.new_property_data.price.max
								: 1) / land_area.ft
						: 0,
				mt:
					state.new_property_data.price.max && land_area.mt !== 0
						? (state.new_property_data.price.max !== null && state.new_property_data.price.max !== 0
								? state.new_property_data.price.max
								: 1) / land_area.mt
						: 0,
			},
			currency: "INR",
		};
		state.new_property_data.price_per_unit = price_per_unit;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertySuperBuiltUpAreaUnit = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.area.super_built_up_area.super_built_up_area_unit = action.payload;
		state.new_property_data.area.carpet_area.carpet_area_unit = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMinSuperBuiltUpArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.area.super_built_up_area.min.ft = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNePropertyMinSuperBuiltUpArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.area.super_built_up_area.min.ft = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMaxSuperBuiltUpArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.area.super_built_up_area.max.ft = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMaxSuperBuiltUpArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.area.super_built_up_area.max.ft = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMinCarpetArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.area.carpet_area.min.ft = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMinCarpetArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.area.carpet_area.min.ft = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMaxCarpetArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.area.carpet_area.max.ft = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMaxCarpetArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.area.carpet_area.max.ft = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMinBedrooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.bhk.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMinBedrooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.bhk.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMaxBedrooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.bhk.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMaxBedrooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.bhk.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMinBathrooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.bathroom.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMinBathrooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.bathroom.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMaxBathrooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.bathroom.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMaxBathrooms = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.bathroom.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMinBalconies = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.balconies.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNePropertyMinBalconies = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.balconies.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMaxBalconies = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.balconies.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMaxBalconies = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.balconies.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyMaxFloors = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.floors.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyMaxFloors = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.floors.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyFloorNo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.floor_no.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};
export const setCreateNewPropertyFloorNo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.floor_no.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyNoOfUnits = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.no_of_units.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyNoOfUnits = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.no_of_units.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyNoOfSeats = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.no_of_seats.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyNoOfSeats = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.no_of_seats.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreatePropertySelectedImagesUrl = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.images = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyUploadImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.images = action.payload;
	},
	prepare: (
		payload: Array<{
			file_id: ObjectId | string;
			mime_type: string;
			category: string;
			caption: string;
			priority: number;
			stage: string;
		}>,
	) => ({
		payload: payload,
	}),
};

export const setPropertyImagesUploadCompleted = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.image_upload_completed = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setPropertyIsImagesUploading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.uploading_images = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setServiceIsImagesUploading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.uploading_images = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setServiceImagesUploadCompleted = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.image_upload_completed = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setCreateNewPropertyDefaultLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_property_data.search_metrics.location_coordinates = action.payload.location_coordinates;
		state.new_property_data.location.title = action.payload.address;
		state.new_property_data.location.country = action.payload.country;
		state.new_property_data.location.state_or_province = action.payload.state_or_province;
		state.new_property_data.location.city = action.payload.city;
	},
	prepare: (payload: {
		location_coordinates: Array<number>;
		address: string;
		country: string;
		state_or_province: string;
		city: string;
	}) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceCategory = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.service_category = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.service_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceSubType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.service_subtype = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceOtherSubType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.other_service_subtype = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceShowPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.price.show_price = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceMinPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.price.min = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceMaxPrice = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.price.max = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceUploadImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.images = action.payload;
	},
	prepare: (
		payload: Array<{
			file_id: ObjectId | string;
			mime_type: string;
			category: string;
			caption: string;
			priority: number;
			stage: string;
		}>,
	) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceDefaultLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.search_metrics.location_coordinates = action.payload.location_coordinates;
		state.new_service_data.location.title = action.payload.address;
	},
	prepare: (payload: { location_coordinates: Array<number>; address: string }) => ({
		payload: payload,
	}),
};

export const setCreateNewServiceProviderName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.new_service_data.service_provider.name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyAmenities = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.amenities = action.payload;
	},
	prepare: (
		payload: Array<{
			id: string;
			label: string;
			icon: string;
		}>,
	) => ({
		payload: payload,
	}),
};

export const setSinglePropertyOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.description = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyManualOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.manual_overview = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyAbout = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.about_property = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertyDeveloperName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.builder.name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyDeveloperSlug = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.builder.slug = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};
export const setSinglePropertyEstablishedYear = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.builder.year_est = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setSinglePropertyDescription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.builder.description = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setSinglePropertyAboutLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.location.about_location = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setSinglePropertyVicinity = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.vicinity = action.payload;
	},
	prepare: (
		payload: Array<{
			category: string;
			values: Array<{
				title: string;
				distance: number;
			}>;
		}>,
	) => ({
		payload: payload,
	}),
};

export const fetchAboutSingleProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.about_property = action.payload.property_data.about_property;
		state.property_loading.aboutSingleProperty = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyCreator = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.created_by = action.payload.property_data.created_by;
		state.property_loading.singlePropertyCreator = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyAboutLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.location = action.payload.property_data.location;
		state.property_loading.singlePropertyAboutLocation = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyAmenities = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.amenities = action.payload.property_data.amenities;
		state.property_loading.singlePropertyAmenities = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyDeveloper = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.builder = action.payload.property_data.builder;
		state.property_loading.singlePropertyDeveloper = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};
export const fetchSinglePropertyImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property._id = action.payload.property_data._id;
		state.single_property_page.property.slug = action.payload.property_data.slug;
		state.single_property_page.property.images = action.payload.property_data.images;
		state.single_property_page.property.title = action.payload.property_data.title;
		state.property_loading.singlePropertyImages = false;
		state.single_property_page.property.saved_content = action.payload.property_data.saved_content;
		state.single_property_page.property.location = action.payload.property_data.location;
		state.single_property_page.property.show_contact_details = action.payload.property_data.show_contact_details;
		state.single_property_page.property.allow_copy = action.payload.property_data.allow_copy;
		state.single_property_page.property.linked = action.payload.property_data.linked;
		state.single_property_page.property.linked_to = action.payload.property_data.linked_to;
		state.single_property_page.property.published = action.payload.property_data.published;
		state.single_property_page.property.status = action.payload.property_data.status;
		state.single_property_page.property.assigned_to = action.payload.property_data.assigned_to;
		state.single_property_page.property.ad_text = action.payload.property_data.ad_text;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};
export const fetchSinglePropertyLocationCoordinates = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.search_metrics.location_coordinates =
			action.payload.property_data.search_metrics.location_coordinates;
		state.location = {
			location: [
				action.payload.property_data.search_metrics.location_coordinates[1],
				action.payload.property_data.search_metrics.location_coordinates[0],
			],
		};
		state.property_loading.singlePropertyLocationCoordinates = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.description = action.payload.property_data.description;
		state.single_property_page.property.is_overview_ai_generated =
			action.payload.property_data.is_overview_ai_generated;
		state.property_loading.singlePropertyOverview = false;
		state.single_property_page.property.manual_overview = action.payload.property_data.manual_overview;
		state.single_property_page.property.use_data_for_fine_tuning =
			action.payload.property_data.use_data_for_fine_tuning;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.propertyReviews = action.payload.property_data;
		state.property_loading.singlePropertyReviews = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyVicinity = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.vicinity = action.payload.property_data.vicinity;
		state.property_loading.singlePropertyVicinity = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyTrendingProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.trendingProperties = action.payload.trending_properties;
		state.property_loading.singlePropertyTrendingProperties = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyHotProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.hotProperties = action.payload.hot_properties;
		state.property_loading.singlePropertyHotProperties = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchSinglePropertyNeighbourhoodReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.neighbourhoodReviews = action.payload.property_data;
		state.property_loading.singlePropertyNeighbourhoodReviews = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const fetchAccountSettingsUserData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data = action.payload.user_data;
		state.account_setting_loading.loading_personal_info = false;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["account_settings_page"]["user_data"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};
export const fetchAccountSettingsUserCashtransactions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_cash_transactions = action.payload.user_cash_transactions;
		state.account_setting_loading.loading_cash_transactions = false;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["account_settings_page"]["user_cash_transactions"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};
export const fetchAccountSettingsUserBeegruPointstransactions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_beegru_points_transactions = action.payload.user_beegru_points_transactions;
		state.account_setting_loading.loading_beegru_points_transactions = false;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["account_settings_page"]["user_beegru_points_transactions"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};
export const fetchAccountSettingsSubscriptionsPlans = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.subscription_plans = action.payload.subscription_plans;
		state.account_setting_loading.loading_subscriptions_plans = false;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["account_settings_page"]["subscription_plans"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};
export const fetchAccountSettingsSubscriptions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.subscriptions = action.payload.subscriptions;
		state.account_setting_loading.loading_subscriptions = false;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["account_settings_page"]["subscriptions"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};
export const fetchAccountSettingsFilesCount = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.file_counts = action.payload.file_counts;
		state.account_settings_page.file_size_percentages = action.payload.file_size_percentages;
		state.account_settings_page.file_sizes = action.payload.file_sizes;
		state.account_settings_page.allocated_storage = action.payload.allocated_storage;
		state.account_setting_loading.loading_preferences = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const setBusinessProfileAboutShortDescription = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about.short_description = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessProfileAboutYearEstablished = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about.year_established = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

/* */
export const setBusinessProfileAboutOfficeAddress = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about.office_address = action.payload;
	},
	prepare: (
		payload: Array<{
			line1: string;
			line2: string;
			city: string;
			state: string;
			country: string;
			pincode: string;
			is_hq: boolean;
		}>,
	) => ({
		payload: payload,
	}),
};
export const setBusinessProfileAboutWebsiteUrl = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.about.website_url = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessProfileServicesOfferedTags = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.services_offered = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};
export const setBusinessProfileExpertise = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.expertise = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setBusinessProfileBrokerage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.agent_brokerage = action.payload;
	},
	prepare: (
		payload: Array<{
			brokerage_for: string;
			brokerage_percentage: number;
		}>,
	) => ({
		payload: payload,
	}),
};

export const setBusinessProfileLicencesAndCertificates = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.licences_and_certifications = action.payload;
	},
	prepare: (
		payload: Array<{
			title: string;
			description: string;
			issued_by: string;
			issued_at: Date | string;
			media_url: string;
			mime_type: string;
		}>,
	) => ({
		payload: payload,
	}),
};
export const setBusinessProfileNoOfEmployees = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.no_of_employees = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};
export const setBusinessProfileFAQ = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.faqs = action.payload;
	},
	prepare: (
		payload: Array<{
			question: string;
			answer: string;
		}>,
	) => ({
		payload: payload,
	}),
};
export const setBusinessProfileLanguages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.languages = action.payload;
	},
	prepare: (
		payload: Array<{
			language: string;
			proficiency: string;
		}>,
	) => ({
		payload: payload,
	}),
};
export const setBusinessProfileEmail = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.email.email = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};
export const setBusinessProfilePhoneCountryCode = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.phone.country_code = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};
export const setBusinessProfilePhoneNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.business_profile.phone.phone_number = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};
export const deleteReview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.source.includes("properties") && action.payload.source.includes("/reviews")) {
			state.property_reviews_page.property_reviews = action.payload.payload;
		} else if (action.payload.source.includes("properties")) {
			if (action.payload.payload.type === "neighbourhood") {
				state.single_property_page.neighbourhoodReviews = action.payload.payload;
			} else {
				state.single_property_page.propertyReviews = action.payload.payload;
			}
		}
		if (action.payload.source.includes("/services") && action.payload.source.includes("/reviews")) {
			state.service_reviews_page.service_reviews = action.payload.payload;
		} else if (action.payload.source.includes("services")) {
			state.single_service_page.service_reviews = action.payload.payload;
		} else if (action.payload.source.includes("users")) {
			state.single_user_profile.reviews = action.payload.payload;
		} else if (action.payload.source.includes("neighbourhood-reviews")) {
			state.neighbourhood_reviews_page.reviews = action.payload.payload;
		} else {
			state.single_business_profile.reviews = action.payload.payload;
		}
	},
	prepare: (payload: {
		source: string;
		response: {
			status: string;
			payload: StateInterface["review"];
			missingFields: Array<string> | null;
			error: string | null;
		};
	}) => ({
		payload: { payload: payload.response.payload, source: payload.source },
	}),
};

export const fetchSinglePropertyConfigAndArea = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.area = action.payload.property_data.area;
		state.single_property_page.property.bhk = action.payload.property_data.bhk;
		state.single_property_page.property.bathroom = action.payload.property_data.bathroom;
		state.single_property_page.property.balconies = action.payload.property_data.balconies;
		state.single_property_page.property.floors = action.payload.property_data.floors;
		state.single_property_page.property.floor_no = action.payload.property_data.floor_no;
		state.single_property_page.property.no_of_units = action.payload.property_data.no_of_units;
		state.single_property_page.property.no_of_seats = action.payload.property_data.no_of_seats;
		state.single_property_page.property.furnished = action.payload.property_data.furnished;
		state.single_property_page.property.plot_dimensions = action.payload.property_data.plot_dimensions.map(
			(plot_dimension: string) =>
				plot_dimension.replace(/_/g, " ").charAt(0).toUpperCase() + plot_dimension.replace(/_/g, " ").slice(1),
		);
		state.single_property_page.property.other_plot_dimensions = action.payload.property_data.other_plot_dimensions;
		state.property_loading.propertyConfigAndArea = false;
	},
	prepare: (payload: StateInterface["single_property_page"]) => ({
		payload: payload,
	}),
};

export const addingbeegrupointsshareproperty = {
	reducer: (state: StateInterface, action: AnyAction) => {},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};
export const AddingBeegruPointsForShareService = {
	reducer: (state: StateInterface, action: AnyAction) => {},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessProfilePosts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.posts = action.payload.posts;
		state.single_business_profile.postsCount = action.payload.postsCount;
		state.business_profile_loading.businessProfilePost = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const LoadingMoreBusinessProfilePosts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		Array.isArray(state.single_business_profile.posts)
			? ((state.single_business_profile.postsCount = action.payload.postsCount),
				//@ts-ignore
				state.single_business_profile.posts.push(...action.payload.posts))
			: (state.single_business_profile.posts = action.payload.posts);
	},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};
export const searchUserProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.ofb_properties = action.payload.properties;
		state.single_user_profile.propertiesCount = action.payload.propertiesCount;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setPropertySnackbar = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.property_snackbar.property_details = action.payload;
		state.property_snackbar.property_config = action.payload;
		state.property_snackbar.about_property = action.payload;
		state.property_snackbar.property_creator = action.payload;
		state.property_snackbar.property_about_location = action.payload;
		state.property_snackbar.property_amenities = action.payload;
		state.property_snackbar.property_developer = action.payload;
		state.property_snackbar.property_images_upload = action.payload;
		state.property_snackbar.property_images_edit = action.payload;
		state.property_snackbar.property_images_delete = action.payload;
		state.property_snackbar.property_location_coordinates = action.payload;
		state.property_snackbar.property_overview = action.payload;
		state.property_snackbar.property_reviews = action.payload;
		state.property_snackbar.property_vicinity = action.payload;
		state.property_snackbar.neighbourhood_reviews = action.payload;
		state.property_snackbar.property_regulatory_info = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setServiceSnackbar = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.service_snackbar.service_details = action.payload;
		state.service_snackbar.service_overview = action.payload;
		state.service_snackbar.service_specification = action.payload;
		state.service_snackbar.service_creator = action.payload;
		state.service_snackbar.about_service_provider = action.payload;
		state.service_snackbar.areas_of_operation = action.payload;
		state.service_snackbar.service_map = action.payload;
		state.service_snackbar.service_images_upload = action.payload;
		state.service_snackbar.service_images_edit = action.payload;
		state.service_snackbar.service_images_delete = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setAccountSettingSnackbar = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.accountsetting_snackbar.personalinfo_snackbar = action.payload;
		state.accountsetting_snackbar.kyc_snackbar = action.payload;
		state.accountsetting_snackbar.subscription_snackbar = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const searchBusinessProfileProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.properties = action.payload.properties;
		state.single_business_profile.propCount = action.payload.propertiesCount;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const searchBusinessProfileOfbProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.ofb_properties = action.payload.properties;
		state.single_business_profile.ofbpropertiesCount = action.payload.propertiesCount;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const searchOfbPropertyListings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.ofb_properties_og = action.payload.payload;
	},
	prepare: (payload: StateInterface["single_business_profile"]["searched_property"]) => ({
		payload: payload,
	}),
};
export const searchBusinessProfileServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.services = action.payload.services;
		state.single_business_profile.servicesCount = action.payload.servicesCount;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const searchServiceListings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.services_og = action.payload.payload;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const setBusinessProfileSnackbar = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_profile_snackbar.upload_cover_image = action.payload;
		state.business_profile_snackbar.edit_cover_image = action.payload;
		state.business_profile_snackbar.delete_cover_image = action.payload;
		state.business_profile_snackbar.upload_profile_picture = action.payload;
		state.business_profile_snackbar.edit_profile_picture = action.payload;
		state.business_profile_snackbar.delete_profile_picture = action.payload;
		state.business_profile_snackbar.update_about = action.payload;
		state.business_profile_snackbar.service_offered = action.payload;
		state.business_profile_snackbar.experties = action.payload;
		state.business_profile_snackbar.why_choose_us = action.payload;
		state.business_profile_snackbar.brokerages = action.payload;
		state.business_profile_snackbar.job_openings = action.payload;
		state.business_profile_snackbar.honors_and_awards = action.payload;
		state.business_profile_snackbar.licences_and_certificates = action.payload;
		state.business_profile_snackbar.no_of_employees = action.payload;
		state.business_profile_snackbar.business_faq = action.payload;
		state.business_profile_snackbar.language = action.payload;
		state.business_profile_snackbar.delete_property_listing = action.payload;
		state.business_profile_snackbar.delete_service_listing = action.payload;
		state.business_profile_snackbar.delete_ofb_listing = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};
export const LoadingMoreUserGivenReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		Array.isArray(state.single_user_profile.given_reviews)
			? ((state.single_user_profile.given_reviews_count = action.payload.givenReviewsCount),
				//@ts-ignore
				state.single_user_profile.given_reviews.push(...action.payload.reviews))
			: (state.single_user_profile.given_reviews = action.payload.reviews);
	},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};

export const LoadingMoreUserReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		Array.isArray(state.single_user_profile.reviews)
			? ((state.single_user_profile.reviewsCount = action.payload.reviewsCount),
				//@ts-ignore
				state.single_user_profile.reviews.push(...action.payload.reviews))
			: (state.single_user_profile.reviews = action.payload.reviews);
	},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileGivenReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.given_reviews = action.payload.reviews;
		state.single_business_profile.given_reviewsCount = action.payload.reviewsCount;
		state.business_profile_loading.businessProfileReviews = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const LoadingMoreBusinessProfileGivenReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		Array.isArray(state.single_business_profile.given_reviews)
			? ((state.single_business_profile.given_reviewsCount = action.payload.givenReviewsCount),
				//@ts-ignore
				state.single_business_profile.given_reviews.push(
					//@ts-ignore
					...action.payload.reviews,
				))
			: (state.single_business_profile.given_reviews = action.payload.reviews);
	},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};

export const LoadingMoreBusinessProfileReviews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		Array.isArray(state.single_business_profile.reviews)
			? ((state.single_business_profile.reviewsCount = action.payload.reviewsCount),
				//@ts-ignore
				state.single_business_profile.reviews.push(...action.payload.reviews))
			: (state.single_user_profile.reviews = action.payload.reviews);
	},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};
export const setTransactionType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.transaction = action.payload;
	},
	prepare: (payload: StateInterface["transaction"]) => ({ payload: payload }),
};
export const setOnBoardingDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.on_boarding_details = action.payload;
	},
	prepare: (payload: StateInterface["on_boarding_details"]) => ({
		payload: payload,
	}),
};
export const fetchOnBoardingBusinessTitle = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.on_boarding_title.options = action.payload.business_title;
	},
	prepare: (payload: StateInterface["on_boarding_title"]) => ({
		payload: payload,
	}),
};

export const setUserProfileSnackbar = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.user_profile_snackbar.upload_cover_image = action.payload;
		state.business_profile_snackbar.edit_cover_image = action.payload;
		state.business_profile_snackbar.delete_cover_image = action.payload;
		state.user_profile_snackbar.upload_profile_picture = action.payload;
		state.user_profile_snackbar.edit_profile_picture = action.payload;
		state.user_profile_snackbar.delete_profile_picture = action.payload;
		state.user_profile_snackbar.update_about = action.payload;
		state.user_profile_snackbar.delete_ofb_listing = action.payload;
		state.user_profile_snackbar.corrupt_file = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};
export const NewEmployeeRquestOnBoarding = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.on_boarding_title.options = action.payload.business_title;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const fetchUserProfileLeads = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.leads = action.payload.leads;
		state.user_profile_loading.userProfileLeads = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const setBottomBuyBeegruPoints = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.bottom_subscribe_buy_beegru_points = action.payload;
	},
	prepare: (payload: StateInterface["bottom_subscribe_buy_beegru_points"]) => ({
		payload: payload,
	}),
};
export const setDefaultProfileContext = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.profile_context = action.payload;
	},
	prepare: (payload: StateInterface["profile_context"]) => ({
		payload: payload,
	}),
};

export const UpdateReduxSessionUserFields = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (!state.redux_session || !state.redux_session.user || !state.redux_session.user.dbUser) {
			return;
		}
		const dbUser = state.redux_session.user.dbUser as any;
		if (action.payload.first_name !== undefined) {
			dbUser.first_name = action.payload.first_name;
		}
		if (action.payload.last_name !== undefined) {
			dbUser.last_name = action.payload.last_name;
		}
		if (action.payload.profile_picture !== undefined) {
			dbUser.profile_picture = action.payload.profile_picture;
		}
	},
	prepare: (payload: { first_name?: string; last_name?: string; profile_picture?: any }) => ({
		payload,
	}),
};

export const updateReduxSession = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.redux_session = action.payload;
		if ((action.payload || { session: null }).session !== null) {
			state.profile_context.is_business_profile = action.payload.user.dbUser.profile_context.is_business_profile;
			state.profile_context.user_id = action.payload.user.dbUser._id;
			state.profile_context.business_profile_id = action.payload.user.dbUser.profile_context.business_profile_id;
			state.profile_context.business_profile_image = action.payload.user.dbUser.profile_context.business_profile_image;
			state.profile_context.page_type = action.payload.user.dbUser.profile_context.page_type;
			state.profile_context.page_title = action.payload.user.dbUser.profile_context.is_business_profile
				? action.payload.user.dbUser.profile_context.page_title
				: "";
			state.profile_context.page_slug = action.payload.user.dbUser.profile_context.is_business_profile
				? action.payload.user.dbUser.profile_context.page_slug
				: "";
			state.profile_context.subscription_information = action.payload.user.dbUser.profile_context.is_business_profile
				? action.payload.user.dbUser.profile_context.subscription_information
				: action.payload.user.dbUser.subscription_information;
			state.profile_context.kyc_info = action.payload.user.dbUser.profile_context.is_business_profile
				? action.payload.user.dbUser.profile_context.kyc_info
				: action.payload.user.dbUser.kyc_info.kyc_status;
			state.profile_context.remember_choice = action.payload.user.dbUser.profile_context.remember_choice;

			if (
				state.redux_session !== null &&
				action.payload.user.dbUser.profile_context.is_business_profile === true &&
				action.payload.user.dbUser.pages.length < 2 &&
				action.payload.user.dbUser.pages.filter(
					(item: any) => item.page_id === action.payload.user.dbUser.profile_context.business_profile_id,
				).length === 0
			) {
				state.redux_session.user.dbUser.pages = [
					{
						_id: action.payload.user.dbUser.profile_context.business_profile_id,
						page_id: action.payload.user.dbUser.profile_context.business_profile_id,
						page_type: action.payload.user.dbUser.profile_context.page_type,
						page_title: action.payload.user.dbUser.profile_context.page_title,
						page_slug: action.payload.user.dbUser.profile_context.page_slug,
						display_picture_url: action.payload.user.dbUser.profile_context.business_profile_image,
						subscription_information: action.payload.user.dbUser.profile_context.subscription_information,
						kyc_info: action.payload.user.dbUser.profile_context.kyc_info,
					},
					...action.payload.user.dbUser.pages,
				];
			}
		}
	},
	prepare: (payload: { session: StateInterface["redux_session"] }) => ({
		payload: payload,
	}),
};

export const FetchMoreBusinessProfiles = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.redux_session !== null) {
			state.redux_session.user.dbUser.pages = [...state.redux_session.user.dbUser.pages, ...action.payload.pages];
		}
	},
	prepare: (payload: Array<Page>) => ({
		payload: payload,
	}),
};
export const fetchBusinessProfilePublishedProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.published_properties = action.payload.PublishProperties;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const updatedPublishedProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.published_properties = action.payload.PublishProperties;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfilePublishedServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.published_services = action.payload.PublishProperties;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const updatedPublishedServices = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.published_services = action.payload.PublishProperties;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchHomePageHotPropertiesBuyLocalStorageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.hot_properties = action.payload;
		state.home_loading.loading_hot_properties = false;
	},
	prepare: (payload: Array<any>) => ({
		payload: payload,
	}),
};
export const fetchHomePageHotPropertiesRentLocalStorageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.hot_properties = action.payload;
		state.home_loading.loading_hot_properties = false;
	},
	prepare: (payload: Array<any>) => ({
		payload: payload,
	}),
};
export const fetchHomePageHotPropertiesOfbLocalStorageData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.hot_properties = action.payload;
		state.home_loading.loading_hot_properties = false;
	},
	prepare: (payload: Array<any>) => ({
		payload: payload,
	}),
};

export const fetchAccountSettingsRewardtransactions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.rewards_transaction = action.payload.rewards_transaction;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["account_settings_page"]["rewards_transaction"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchOneTimeRewards = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.one_time_rewards = action.payload.rewards_transaction;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["account_settings_page"]["one_time_rewards"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchRecurringRewards = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.recurring_rewards = action.payload.rewards_transaction;
		state.account_setting_loading.loading_rewards = false;
	},
	prepare: (payload: {
		status: string;
		payload: StateInterface["account_settings_page"]["recurring_rewards"];
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const updateSearchFilterActiveOptions = {
	reducer: (
		state: StateInterface,
		action: { payload: { searchFilterId: SearchFilterId; newOptions: Array<SearchFilterOption> } },
	) => {
		state.search_filters_state[action.payload.searchFilterId].active_options = action.payload.newOptions;
	},
	prepare: (payload: { searchFilterId: SearchFilterId; newOptions: Array<SearchFilterOption> }) => ({
		payload: payload,
	}),
};

export const updateSearchFilterSelectedOptions = {
	reducer: (
		state: StateInterface,
		action: { payload: { searchFilterId: SearchFilterId; newOptions: Array<SearchFilterOption> } },
	) => {
		state.search_filters_state[action.payload.searchFilterId].selected_options = action.payload.newOptions;
	},
	prepare: (payload: { searchFilterId: SearchFilterId; newOptions: Array<SearchFilterOption> }) => ({
		payload: payload,
	}),
};

export const updateSearchFilterVisibility = {
	reducer: (state: StateInterface, action: { payload: { searchFilterId: SearchFilterId; isVisible: boolean } }) => {
		state.search_filters_state[action.payload.searchFilterId].is_visible = action.payload.isVisible;
	},
	prepare: (payload: { searchFilterId: SearchFilterId; isVisible: boolean }) => ({
		payload: payload,
	}),
};

export const updateSearchFilterApplied = {
	reducer: (state: StateInterface, action: { payload: { searchFilterId: SearchFilterId; isApplied: boolean } }) => {
		state.search_filters_state[action.payload.searchFilterId].is_applied = action.payload.isApplied;
	},
	prepare: (payload: { searchFilterId: SearchFilterId; isApplied: boolean }) => ({
		payload: payload,
	}),
};

export const updateSearchFilterIsAccordionOpen = {
	reducer: (
		state: StateInterface,
		action: { payload: { searchFilterId: SearchFilterId; isAccordionOpen: boolean } },
	) => {
		state.search_filters_state[action.payload.searchFilterId].is_accordion_open = action.payload.isAccordionOpen;
	},
	prepare: (payload: { searchFilterId: SearchFilterId; isAccordionOpen: boolean }) => ({
		payload: payload,
	}),
};

export const setSearchFilterMetrices = {
	reducer: (
		state: StateInterface,
		action: {
			payload: {
				boosted: Array<number>;
				non_boosted: Array<number>;
				filter_hash: string;
				no_of_boosted_results: number;
				no_of_non_boosted_results: number;
				total_no_of_results: number;
				boosted_from_other_locations: Array<number>;
				no_of_boosted_from_other_locations: number;
			};
		},
	) => {
		state.search_filter_metrices.boosted = [0];
		state.search_filter_metrices.non_boosted = [0];
		state.search_filter_metrices.boosted_from_other_locations = [0];
		state.search_filter_metrices.filter_hash = action.payload.filter_hash;
		state.search_filter_metrices.no_of_boosted_results = action.payload.no_of_boosted_results;
		state.search_filter_metrices.no_of_non_boosted_results = action.payload.no_of_non_boosted_results;
		state.search_filter_metrices.no_of_boosted_from_other_locations = action.payload.no_of_boosted_from_other_locations;
		state.search_filter_metrices.total_no_of_results = action.payload.total_no_of_results;
	},
	prepare: (payload: {
		boosted: Array<number>;
		non_boosted: Array<number>;
		filter_hash: string;
		no_of_boosted_results: number;
		no_of_non_boosted_results: number;
		total_no_of_results: number;
		boosted_from_other_locations: Array<number>;
		no_of_boosted_from_other_locations: number;
	}) => ({
		payload: payload,
	}),
};

export const CreateNewView = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.view_id = action.payload.view_id;
		state.view_time_spent_id = action.payload.view_time_spent_id;
	},
	prepare: (
		payload: {
			view_id: string | ObjectId;
			view_time_spent_id: string | ObjectId;
		} | null,
	) => {
		return {
			payload: payload,
		};
	},
};
export const setSearchAnimationDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.search_animation_settings = action.payload;
	},
	prepare: (payload: StateInterface["search_animation_settings"]) => ({
		payload: payload,
	}),
};
export const fetchSearchData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.SeacrhApiResponse = action.payload.payload;
		state.search_filter_metrices.boosted.push(action.payload.payload?.boosted.length);
		state.search_filter_metrices.non_boosted.push(action.payload.payload?.non_boosted.length);
		state.search_filter_metrices.boosted_from_other_locations.push(
			action.payload.payload?.boosted_from_other_locations
				? action.payload.payload?.boosted_from_other_locations.length
				: 0,
		);
		state.search_filter_metrices.filter_hash = action.payload.payload?.filter_hash;
		state.search_filter_metrices.no_of_boosted_results = action.payload.payload?.no_of_boosted_results;
		state.search_filter_metrices.no_of_non_boosted_results = action.payload.payload?.no_of_non_boosted_results;
		state.search_filter_metrices.no_of_boosted_from_other_locations =
			action.payload.payload?.no_of_boosted_results_from_other_locations;
		state.search_filter_metrices.total_no_of_results = action.payload.payload?.total_no_of_results;
	},
	prepare: (payload: SearchApiResponseInterface) => ({
		payload: payload,
	}),
};

export const fetchPropertyCardData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.property = action.payload.property_data;
	},
	prepare: (payload: PropertyInterface) => ({
		payload: payload,
	}),
};

// PropertyBoost
type PropertyBoostMobileStep = {
	step: number;
};
export const SetPropertyBoostMobileStepState = {
	prepare: (payload: PropertyBoostMobileStep) => ({
		payload: payload,
	}),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: PropertyBoostMobileStep = action.payload;
		if (data.step == 2 && state.boost.detail_error) {
			state.boost.mobile_step = 1;
		} else if (data.step == 3 && state.boost.location_error) {
			state.boost.mobile_step = 2;
		} else if (data.step == 4 && state.boost.points_error) {
			state.boost.mobile_step = 3;
		} else {
			state.boost.mobile_step = data.step;
		}
	},
};

type PropertyBoostSnack = {
	snack_state: boolean;
	snack_message: string;
	snack_severity: OverridableStringUnion<AlertColor, AlertPropsColorOverrides>;
};
export const SetPropertyBoostSnackState = {
	prepare: (payload: PropertyBoostSnack) => ({
		payload: payload,
	}),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: PropertyBoostSnack = action.payload;
		// state.boost.boost_snack_state = data.snack_state;
		// state.boost.boost_snack_message = data.snack_message;
		// state.boost.boost_snack_severity = data.snack_severity;
	},
};

type PropertyBoostModal = {
	state?: boolean;
	id?: string | ObjectId;
	title?: string;
	createdBy?: any;
	active?: boolean;
};

export const SetPropertyBoostModalState = {
	prepare: (payload: PropertyBoostModal) => ({ payload }),
	reducer: (state: StateInterface, action: any) => {
		const data: PropertyBoostModal = action.payload;

		if (data.id != undefined) state.boost.property_id = action.payload.id;
		if (data.title != undefined) state.boost.entity_title = action.payload.title;
		if (data.state != undefined) {
			state.boost.boost_modal_state = action.payload.state;
			if (!data.state) {
				state.boost = {
					...boost,
				};
			}
		}
		if (data.active != undefined) {
			state.boost.active = action.payload.active;
		}
		state.boost.boost_property_loading = true;
		if (data.createdBy != undefined) {
			state.boost.boosted_by.business_profile_id = action.payload.createdBy.business_profile_id._id;
			state.boost.boosted_by.is_business_profile = action.payload.createdBy.is_business_profile;
			state.boost.boosted_by.user_id = action.payload.createdBy.user_id._id;
		}
	},
};

export const SetPropertyBoostedByState = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.boost.boosted_by = action.payload;
	},
	prepare: (payload: BoostInterface["boosted_by"]) => ({
		payload: payload,
	}),
};

type PropertyBoostData = {
	property_id: ObjectId | string | null;
	boost_data: BoostInterface;
	property_title: string;
};
export const SetPropertyBoostDataState = {
	prepare: (payload: PropertyBoostData) => ({ payload }),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: PropertyBoostData = action.payload;
		const setTimeToZero = (date: Date | string) => {
			const temp = new Date(date);
			temp.setHours(0, 0, 0, 0);
			return temp;
		};

		if (data.boost_data) {
			// detail
			const temp_boost_end_date = new Date(data.boost_data.boost_end_date);
			const temp_boost_start_date = new Date(data.boost_data.boost_start_date);
			const temp_hours =
				(setTimeToZero(temp_boost_end_date).getTime() - setTimeToZero(temp_boost_start_date).getTime()) / 3600000;
			const temp_weeks_count = temp_hours / 168;

			// audience
			const temp_boost_location = data.boost_data.boost_location;
			let temp_selected_location = null;
			if (temp_boost_location.length) {
				temp_selected_location = temp_boost_location[0];
			}
			// budget
			const temp_points = data.boost_data.budget;
			const boost_minimum_points = Math.ceil(temp_hours * state.boost.average_hourly_rate);
			state.boost = {
				...state.boost,
				...data.boost_data,
				// property_id: data.property_id,
				// detail
				total_hours: temp_hours,
				temp_weeks_count: temp_weeks_count,
				boost_weeks_count: temp_weeks_count,
				temp_boost_end_date: temp_boost_end_date.toISOString(),
				temp_boost_start_date: temp_boost_start_date.toISOString(),
				// audience
				temp_boost_location: temp_boost_location,
				selected_location: temp_selected_location,
				// budget
				temp_points: temp_points,
				boost_minimum_points: boost_minimum_points,
				boost_data: data.boost_data,
				entity_title: data.property_title,
			};
		}
		state.boost.boost_property_loading = false;
	},
};

type PropertyBoostDetail = {
	snack?: boolean;
	weeks_count?: number;
	start_date?: string;
	temp_start_date?: string;
};

export const SetPropertyBoostDetailState = {
	prepare: (payload: PropertyBoostDetail) => ({
		payload: payload,
	}),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: PropertyBoostDetail = action.payload;
		const setTimeToZero = (date: Date | string) => {
			const temp = new Date(date);
			temp.setHours(0, 0, 0, 0);
			return temp;
		};
		const setBoostEndData = (weeks: number, date: Date | string) => {
			const hours = 168 * weeks;
			const start = new Date(date);
			const weeklyDate = new Date(start.getTime() + hours * 60 * 60 * 1000);

			return weeklyDate;
		};
		if (data.weeks_count != undefined) {
			state.boost.boost_weeks_count = data.weeks_count;
			const today = setTimeToZero(new Date());
			let weeks = state.boost.boost_weeks_count;
			let start_date = new Date();
			let hours = 168 * data.weeks_count;
			const isValidStart = start_date.getTime() >= today.getTime();
			if (isValidStart) {
				state.boost.detail_error = null;
			} else {
				state.boost.detail_error = "Please select valid start date";
				weeks = 1;
				start_date = today;
				hours = 168 * weeks;
			}
			state.boost.total_hours = hours;
			state.boost.boost_start_date = start_date.toISOString();
			state.boost.boost_end_date = setBoostEndData(weeks, start_date).toISOString();
			// const count = state.boost.boost_location.length > 0 ? state.boost.boost_location.length : 1;
			// state.boost.boost_minimum_points = Math.ceil(state.boost.average_hourly_rate * hours) * count;
			state.boost.boost_minimum_points = CalculateMinBoostBudget(state.boost.boost_location, hours);
			state.boost.boost_location_accordion = true;
		}
		if (data.temp_start_date != undefined && data.start_date == undefined) {
			state.boost.temp_boost_start_date = setTimeToZero(data.temp_start_date).toISOString();
		}
	},
};

type PropertyBoostLocation = {
	snack?: boolean;
	location?: BoostLocation[];
	tempLocation?: BoostLocation[];
	selected?: BoostLocation | null;
	newBoostId?: string;
};
export const SetPropertyBoostLocationState = {
	prepare: (payload: PropertyBoostLocation) => ({
		payload: payload,
	}),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: PropertyBoostLocation = action.payload;
		if (data.selected) state.boost.selected_location = data.selected;
		if (data.tempLocation) {
			if (data.tempLocation.length && state.boost.location_error) {
				state.boost.location_error = null;
			}
			if (data.tempLocation.length === 0) {
				state.boost.location_error = "One location is required!";
				state.boost.boost_location = [];
				state.boost.boost_minimum_points = 0;
				state.boost.temp_points = 0;
			}
			if (data.newBoostId && typeof data.newBoostId === "string" && data.newBoostId.length > 0) {
				state.boost._id = data.newBoostId;
			}
			state.boost.temp_boost_location = data.tempLocation;
			state.boost.boost_points_accordion = false;
			state.boost.budget = 0;
		}
		if (data.location) {
			if (!data.location.length) {
				state.boost.location_error = "One location is required!";
				state.boost.boost_minimum_points = 0;
				state.boost.temp_points = 0;
			} else {
				// show snack
				if (data.snack) {
					// state.boost.boost_snack_state = true;
					// state.boost.boost_snack_severity = "success";
					// state.boost.boost_snack_message = "Location saved";
				}
			}

			state.boost.boost_location = data.location;

			const count = data.location.length > 0 ? data.location.length : 1;
			state.boost.boost_minimum_points = data.location.length > 0 ? 11 * count : 0;
			state.boost.boost_points_accordion = true;
		}
	},
};

type PropertyBoostPoints = {
	snack?: boolean;
	points?: number;
	budget?: number;
	availablePoints?: number;
	checkAvailablePoints?: number;
};
export const SetPropertyBoostPointsState = {
	prepare: (payload: PropertyBoostPoints) => ({
		payload: payload,
	}),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: PropertyBoostPoints = action.payload;
		if (
			data.points != undefined &&
			data.points >= 0 &&
			data.availablePoints != undefined &&
			data.checkAvailablePoints == undefined
		) {
			state.boost.temp_points = data.points;
			if (
				data.points >= state.boost.boost_minimum_points &&
				data.points <= data.availablePoints &&
				state.boost.points_error
			) {
				state.boost.points_error = null;
			}
		}
		if (data.budget != undefined && data.availablePoints != undefined && data.checkAvailablePoints == undefined) {
			const points_required = state.boost.total_hours * state.boost.average_hourly_rate;
			if (data.budget < state.boost.boost_minimum_points) {
				state.boost.budget = 0;
				state.boost.total_amount = 0;
				state.boost.points_error = "Minimum points required!";
			} else if (data.budget > data.availablePoints) {
				state.boost.budget = 0;
				state.boost.total_amount = 0;
				state.boost.points_error = "You don't have enough points!";
			} else {
				state.boost.points_error = null;
				state.boost.total_amount = points_required;
				state.boost.budget = state.boost.boost_location.length > 0 ? Math.ceil(data.budget) : 0;

				// show snack
				if (data.snack) {
					// state.boost.boost_snack_state = true;
					// state.boost.boost_snack_severity = "success";
					// state.boost.boost_snack_message = "Budget saved";
				}
			}
		}
	},
};

type PropertyLoading = {
	submitLoading?: boolean;
};
export const SetPropertyLoadingState = {
	prepare: (payload: PropertyLoading) => ({
		payload: payload,
	}),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: PropertyLoading = action.payload;
		if (data.submitLoading) {
			state.boost.boost_submit_loading = true;
		}
	},
};

type CreatePropertyBoost = {
	boost: BoostInterface;
	sessionID?: string | ObjectId;
};
export const CreatePropertyBoostState = {
	prepare: (payload: CreatePropertyBoost) => ({
		payload: payload,
	}),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: CreatePropertyBoost = action.payload;
		if (data.boost) {
			state.boost.boost_data = data.boost;
			state.boost.active = true;
			// state.boost.boost_snack_state = true;
			// state.boost.boost_snack_message = "Proprtyu Boosted Successfully";
			// state.boost.boost_snack_severity = "success";
		}
		state.boost.boost_submit_loading = false;
		state.boost.can_submit = false;
		// state.boost.boost_modal_state = false;
	},
};

type CancelPropertyBoost = {
	boost: PropertyBoostInterface;
};
export const CancelPropertyBoostState = {
	prepare: (payload: CancelPropertyBoost) => ({
		payload: payload,
	}),
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: CancelPropertyBoost = action.payload;
		if (data.boost) {
			state.boost = data.boost;
			// state.boost.boost_snack_message = "Boost Cancelled Successfully";
			// state.boost.boost_snack_severity = "success";
			// state.boost.boost_snack_state = true;
		}
		state.boost.boost_submit_loading = false;
		state.boost.can_submit = false;
		state.boost.boost_modal_state = false;
	},
};

export const UpdatePropertyTitle = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_property_page.property.title = action.payload.payload.title;
		state.single_property_page.property.slug = action.payload.payload.slug;

		state.single_property_page.property.published = action.payload.payload.published;
		state.single_property_page.property.status = action.payload.payload.status;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateBusinessProfileLeadsStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.leads = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const UpdateBusinessProfileLeadsNotes = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_business_profile.leads = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const UpdateUserProfileLeadsStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.leads = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const UpdateUserProfileLeadsNotes = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.leads = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const UpdateServiceTitle = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_service_page.service_data.title = action.payload.payload.title;
		state.single_service_page.service_data.slug = action.payload.payload.slug;

		state.single_service_page.service_data.published = action.payload.payload.published;
		state.single_service_page.service_data.status = action.payload.payload.status;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const setLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.loading = action.payload;
	},
	prepare: (payload: StateInterface["loading"]) => ({
		payload: payload,
	}),
};

export const updateBeegrupointsAfterPurchase = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.account_settings_page.user_data.beegru_points = action.payload;
		state.redux_session &&
			state.redux_session.user.dbUser.beegru_points &&
			(state.redux_session.user.dbUser.beegru_points = action.payload);
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchLeadsById = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.leads = action.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setBusinessDashboardCurrentScreen = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.current_screen !== action.payload) {
			state.business_dashboard.current_screen = action.payload;
		}
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardShowContactDetailsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.show_contact_details_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardPromotionalEmailsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.receive_promotional_emails_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardSavedItems = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_saved_items_response.trigger_new_search) {
			state.business_dashboard.search_saved_items_response.saved_items_ids = action.payload.saved_item_ids;
			state.business_dashboard.search_saved_items_response.saved_items_count = action.payload.saved_items_count;
		} else {
			state.business_dashboard.search_saved_items_response.saved_items_ids.push(...action.payload.saved_item_ids);
		}
		state.business_dashboard.search_saved_items_response.trigger_new_search = false;
		state.business_dashboard.search_saved_items_response.saved_items_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setBusinessDashboardSavedItemsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_saved_items_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardSavedItemsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_saved_items_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardSavedItemsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_saved_items_response.saved_items_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardSavedItemsFilterListingType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_saved_items_response.filter_listing_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardSavedItemsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_saved_items_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardLeadsDesktop = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.leads = action.payload.leads;
		state.business_dashboard.search_leads_response.leads_count = action.payload.leads_count;
		state.business_dashboard.search_leads_response.leads_overview.new_leads_count = action.payload.new_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.follow_up_leads_count =
			action.payload.rnr_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.site_visit_leads_count =
			action.payload.site_visit_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.closed_leads_count =
			action.payload.closed_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.total_leads = action.payload.total_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.qualified_leads_count =
			action.payload.qualified_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.qualified_leads_count =
			action.payload.qualified_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.not_interested_leads_count =
			action.payload.junk_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.unassigned_leads_count =
			action.payload.unassigned_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.assigned_leads_count =
			action.payload.assigned_lead_count;
		state.business_dashboard.search_leads_response.admins = action.payload.admins;
		state.business_dashboard.search_leads_response.filter_campaign_ids = action.payload.boost_ids;
		state.business_profile_loading.buinessProfileLeads = false;
		state.business_dashboard.search_leads_response.leads_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessDashboardLeadsMobile = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_leads_response.trigger_new_search) {
			state.business_dashboard.search_leads_response.lead_ids = action.payload.leads;
			state.business_dashboard.search_leads_response.leads_count = action.payload.leads_count;
		} else {
			state.business_dashboard.search_leads_response.lead_ids.push(...action.payload.leads);
		}
		state.business_dashboard.search_leads_response.trigger_new_search = false;
		state.business_dashboard.search_leads_response.leads_loading = false;
		state.business_profile_loading.buinessProfileLeads = false;
		state.business_dashboard.search_leads_response.leads_overview.new_leads_count = action.payload.new_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.follow_up_leads_count =
			action.payload.rnr_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.site_visit_leads_count =
			action.payload.site_visit_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.closed_leads_count =
			action.payload.closed_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.total_leads = action.payload.total_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.qualified_leads_count =
			action.payload.qualified_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.not_interested_leads_count =
			action.payload.junk_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.unassigned_leads_count =
			action.payload.unassigned_leads_count;
		state.business_dashboard.search_leads_response.leads_overview.assigned_leads_count =
			action.payload.assigned_lead_count;
		state.business_dashboard.search_leads_response.admins = action.payload.admins;
		state.business_dashboard.search_leads_response.filter_campaign_ids = action.payload.boost_ids;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setFilterSelectedCampaignId = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_selected_campaign_id = action.payload;
	},
	prepare: (payload: string | ObjectId) => ({
		payload: payload,
	}),
};

export const setSearchLeadsResponseBoostIds = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_boost_ids = action.payload;
	},
	prepare: (payload: Array<string | ObjectId>) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.leads_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_status = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterAssignedStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_assigned_status = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterAssignedToUserIds = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_assigned_to_user_ids = action.payload;
	},
	prepare: (payload: Array<string>) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterDateRange = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_date_range = action.payload;
	},
	prepare: (payload: { option: string; start_date: string; end_date: string }) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterProperty = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_property_id = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterListingType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_listing_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterSortField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_sort_field = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterSortOrder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_sort_order = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardLeadsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBroadcastTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_broadcast_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.listings_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsFilterStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.filter_status = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsFilterListingType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.filter_listing_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsFilterPropertyId = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.filter_property_id = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsFilterTransactionType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.filter_transaction_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsFilterSortField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.filter_sort_field = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBroadcastFilterSortField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_broadcast_response.filter_sort_field = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBroadcastFilterStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_broadcast_response.filter_status = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsFilterSortOrder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.filter_sort_order = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBroadcastFilterSortOrder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_broadcast_response.filter_sort_order = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardListingsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardReviewsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_reviews_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardReviewsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_reviews_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};
export const setBusinessDashboardReviewsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_reviews_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardReviewsMobile = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_reviews_response.trigger_new_search) {
			state.business_dashboard.search_reviews_response.reviews_ids = action.payload.reviews;
			state.business_dashboard.search_reviews_response.reviews_count = action.payload.reviews_count;
		} else {
			state.business_dashboard.search_reviews_response.reviews_ids.push(...action.payload.reviews);
		}
		state.business_dashboard.search_reviews_response.trigger_new_search = false;
		state.business_dashboard.search_reviews_response.reviews_loading = false;
		state.business_dashboard.search_reviews_response.did_user_already_review = action.payload.didUserAlreadyReviewTheBP;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setBusinessDashboardReviewsFilterSortField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_reviews_response.filter_sort_field = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardReviewsFilterSortOrder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_reviews_response.filter_sort_order = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBeegruPointTransactionsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_beegru_point_transactions_response.transactions_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBeegruPointTransactionsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_beegru_point_transactions_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBeegruPointTransactionsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_beegru_point_transactions_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};
export const setBusinessDashboardBeegruPointTransactionsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_beegru_point_transactions_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardBeegruPointTransactions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_beegru_point_transactions_response.trigger_new_search) {
			state.business_dashboard.search_beegru_point_transactions_response.transactions = action.payload.transactions;
			state.business_dashboard.search_beegru_point_transactions_response.transactions_count =
				action.payload.transactions_count;
		} else {
			state.business_dashboard.search_beegru_point_transactions_response.transactions.push(
				...action.payload.transactions,
			);
		}
		state.business_dashboard.search_beegru_point_transactions_response.trigger_new_search = false;
		state.business_dashboard.search_beegru_point_transactions_response.transactions_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardCashTransactionsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_cash_transactions_response.transactions_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardCashTransactionsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_cash_transactions_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardCashTransactionsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_cash_transactions_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};
export const setBusinessDashboardCashTransactionsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_cash_transactions_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardCashTransactions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_cash_transactions_response.trigger_new_search) {
			state.business_dashboard.search_cash_transactions_response.transactions = action.payload.transactions;
			state.business_dashboard.search_cash_transactions_response.transactions_count = action.payload.transactions_count;
		} else {
			state.business_dashboard.search_cash_transactions_response.transactions.push(...action.payload.transactions);
		}
		state.business_dashboard.search_cash_transactions_response.trigger_new_search = false;
		state.business_dashboard.search_cash_transactions_response.transactions_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

// payment transactions

export const setBusinessDashboardPaymentTransactionsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_payment_transactions_response.transactions_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardPaymentTransactionsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_payment_transactions_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardPaymentTransactionsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_payment_transactions_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardPaymentTransactionsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_payment_transactions_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardPaymentTransactions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_payment_transactions_response.trigger_new_search) {
			state.business_dashboard.search_payment_transactions_response.transactions =
				action.payload.payload?.transactions || action.payload.transactions || [];
			state.business_dashboard.search_payment_transactions_response.transactions_count =
				action.payload.payload?.transactions_count || action.payload.transactions_count || 0;
		} else {
			const transactionsToAdd = action.payload.payload?.transactions || action.payload.transactions || [];
			if (transactionsToAdd.length > 0) {
				state.business_dashboard.search_payment_transactions_response.transactions.push(...transactionsToAdd);
			}
		}
		state.business_dashboard.search_payment_transactions_response.trigger_new_search = false;
		state.business_dashboard.search_payment_transactions_response.transactions_loading = false;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

// rewards history

export const setBusinessDashboardRewardsHistoryLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_rewards_history_response.rewards_history_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardRewardsHistoryTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_rewards_history_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardRewardsHistoryInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_rewards_history_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};
export const setBusinessDashboardRewardsHistoryFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_rewards_history_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardRewardsHistory = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_rewards_history_response.trigger_new_search) {
			state.business_dashboard.search_rewards_history_response.rewards_history = action.payload.rewards_history;
			state.business_dashboard.search_rewards_history_response.rewards_history_count =
				action.payload.rewards_history_count;
		} else {
			state.business_dashboard.search_rewards_history_response.rewards_history.push(...action.payload.rewards_history);
		}
		state.business_dashboard.search_rewards_history_response.trigger_new_search = false;
		state.business_dashboard.search_rewards_history_response.rewards_history_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

// rewards

export const setBusinessDashboardRewardsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_rewards_response.rewards_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardRewardsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_rewards_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardRewardsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_rewards_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardRewards = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_rewards_response.trigger_new_search) {
			state.business_dashboard.search_rewards_response.rewards = action.payload.rewards;
		} else {
			state.business_dashboard.search_rewards_response.rewards.push(...action.payload.rewards);
		}
		state.business_dashboard.search_rewards_response.trigger_new_search = false;
		state.business_dashboard.search_rewards_response.rewards_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

// referrals

export const setBusinessDashboardReferralsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_referrals_response.referrals_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardReferralsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_referrals_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardReferralsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_referrals_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};
export const setBusinessDashboardReferralsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_referrals_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardReferrals = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_referrals_response.trigger_new_search) {
			state.business_dashboard.search_referrals_response.referrals = action.payload.referrals;
			state.business_dashboard.search_referrals_response.referrals_count = action.payload.referrals_count;
		} else {
			state.business_dashboard.search_referrals_response.referrals.push(...action.payload.referrals);
		}
		state.business_dashboard.search_referrals_response.trigger_new_search = false;
		state.business_dashboard.search_referrals_response.referrals_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

// kyc

export const fetchBusinessDashboardKycInfo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.kyc_info = action.payload.kyc_data;
	},
	prepare: (payload: { kyc_data: KYCInformationBusinessProfile | null }) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardListings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_listings_response.trigger_new_search) {
			state.business_dashboard.search_listings_response.listing_ids = action.payload.listings;
			state.business_dashboard.search_listings_response.listings_count = action.payload.listings_count;
			// Reset pagination on a new search so load-more resumes from page 2, not a stale
			// page number, which would skip/overshoot pages and leave the "load more" loader
			// stuck visible while fetching empty pages.
			state.business_dashboard.search_listings_response.filter_page_number = 1;
		} else {
			const existingIds = new Set(
				state.business_dashboard.search_listings_response.listing_ids.map((id: any) => id.toString()),
			);
			const newListings = action.payload.listings.filter((id: any) => !existingIds.has(id.toString()));
			state.business_dashboard.search_listings_response.listing_ids.push(...newListings);
		}
		state.business_dashboard.search_listings_response.trigger_new_search = false;
		state.business_dashboard.search_listings_response.listings_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessDashboardListingsCount = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.listings_filter_count = action.payload.listings;
		state.business_dashboard.search_listings_response.listings_filter_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessProfileUserViewListings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_listings_response.trigger_new_search) {
			state.business_dashboard.search_listings_response.listing_ids = action.payload.listings;
			state.business_dashboard.search_listings_response.listings_count = action.payload.listings_count;
		} else {
			state.business_dashboard.search_listings_response.listing_ids.push(...action.payload.listings);
		}
		state.business_dashboard.search_listings_response.trigger_new_search = false;
		state.business_dashboard.search_listings_response.listings_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessDashboardAdmins = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.admins.admins = action.payload.business_profile.admin_user_details;
		state.business_dashboard.admins.is_admin_fetching = false;
		state.business_dashboard.admins.admin_loading = false;
		// state.business_dashboard.business_profile.admin_user_details = action.payload.business_profile.admin_user_details;
		state.business_dashboard.business_profile.employee_details = action.payload.business_profile.employee_details;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setBusinessDashboardAdminLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.admins.is_admin_fetching = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const businessDashboardAddAdminUserDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* add-admin now sends an invite and answers with a null payload (the screen re-fetches
		   the list), so only an array ever replaces the list: a null or an error must not
		   wipe it. */
		if (Array.isArray(action.payload)) {
			state.business_dashboard.admins.admins = action.payload;
		}
		state.business_dashboard.admins.is_admin_fetching = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const businessDashboardDeleteAdminUserDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.admins.admins = action.payload;
		state.business_dashboard.admins.is_admin_fetching = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessDashboardLeadsOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.leads_overview = action.payload.leads_overview;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessDashboardPropertiesOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.listings_overview = action.payload.listings_overview;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const switchBusinessDashboardContantDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.show_contact_details = action.payload;
		state.business_dashboard.show_contact_details_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const switchBusinessDashboardPromotionalEmails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.redux_session) {
			state.redux_session.user.dbUser.user_privacy.send_promotional_emails = action.payload;
		}
		state.business_dashboard.receive_promotional_emails_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessDashboardOFBsOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.listings_overview = action.payload.listings_overview;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessDashboardServicesOverview = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.listings_overview = action.payload.listings_overview;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const fetchBusinessDashboardBoostsMobile = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.search_boosts_response.trigger_new_search) {
			state.business_dashboard.search_boosts_response.boost_ids = action.payload.boosts;
			state.business_dashboard.search_boosts_response.boosts_count = action.payload.boosts_count;
		} else {
			state.business_dashboard.search_boosts_response.boost_ids.push(...action.payload.boosts);
		}
		state.business_dashboard.search_boosts_response.trigger_new_search = false;
		state.business_dashboard.search_boosts_response.boosts_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setPropertyIdForCampaignDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.property_id = action.payload.property_id;
	},
	prepare: (payload: { property_id: string | ObjectId }) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBoostsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_boosts_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBoostsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_boosts_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBoostsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_boosts_response.boosts_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBoostsFilterStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_boosts_response.filter_status = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBoostsFilterListingType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_boosts_response.filter_listing_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBoostsFilterSortField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_boosts_response.filter_sort_field = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBoostsFilterSortOrder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_boosts_response.filter_sort_order = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardBoostsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_boosts_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const UpdateBusinessProfileTitle = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.title = action.payload.payload.title;
		state.business_dashboard.business_profile.slug = action.payload.payload.slug;
		state.redux_session &&
			state.redux_session.user.dbUser.pages[0].page_title &&
			(state.redux_session.user.dbUser.pages[0].page_title = action.payload.payload.title);
		state.redux_session &&
			state.redux_session.user.dbUser.pages[0].page_slug &&
			(state.redux_session.user.dbUser.pages[0].page_slug = action.payload.payload.slug);
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const UpdateBusinessProfilePicture = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.display_picture_url = action.payload.profile_picture;
		if (action.payload.is_user_owner) {
			state.profile_context.business_profile_image.exists = action.payload.profile_picture.exists;
			state.profile_context.business_profile_image.file_id = action.payload.profile_picture.file_id;
		}
		// if (action.payload.snack_value === "upload_profile_picture") {
		// 	state.business_profile_snackbar.upload_profile_picture = true;
		// } else if (action.payload.snack_value === "edit_profile_picture") {
		// 	state.business_profile_snackbar.edit_profile_picture = true;
		// }
	},
	prepare: (payload: StateInterface["business_profile"]["display_picture_url"]) => ({
		payload: payload,
	}),
};

export const UpdateBusinessProfileLanguages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.languages = action.payload.payload.languages;
		state.business_profile_snackbar.language = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const UpdateBusinessProfileAboutDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.about = action.payload.about;
		state.business_dashboard.business_profile.email = action.payload.email;
		state.business_dashboard.business_profile.phone = action.payload.phone;
		state.business_profile_snackbar.update_about = true;
	},
	prepare: (payload: {
		status: string;
		payload: BusinessProfileInterface;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessProfileAreasOfOperation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.business_profile.areas_of_operation = action.payload.areas_of_operation;
		if (action.payload.regulatory_permits !== undefined) {
			state.business_dashboard.business_profile.regulatory_permits = action.payload.regulatory_permits;
		}
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const UpdateBusinessProfileListingsCount = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_listings_response.listings_overview.sale_count = action.payload.sale_count;
		state.business_dashboard.search_listings_response.listings_overview.rent_count = action.payload.rent_count;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setBusinessDashboardAnalyticsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.analytics_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardAnalyticsGraphSource = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.anal_graph_source = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardAnalyticsFilterListingType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.filter_listing_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardAnalyticsFilterListingId = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.filter_listing_id = action.payload;
	},
	prepare: (payload: string | ObjectId) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardAnalyticsFilterGraphTypeY = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.filter_graph_type_y = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardAnalyticsFilterGraphDurationX = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.filter_graph_duration_x = action.payload;

		// if (state.business_dashboard.search_analytics_response.graph_screen_temp_data) {
		// 	switch (action.payload) {
		// 		case "day":
		// 			state.business_dashboard.search_analytics_response.graph_screen_x_axis =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.last_24_hours.x;
		// 			state.business_dashboard.search_analytics_response.graph_screen_stat_total =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.last_24_hours.y.reduce(
		// 					(sum: number, next_number: number) => sum + next_number,
		// 					0,
		// 				);
		// 			break;

		// 		case "week":
		// 			state.business_dashboard.search_analytics_response.graph_screen_x_axis =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.last_7_days.x;
		// 			state.business_dashboard.search_analytics_response.graph_screen_stat_total =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.last_7_days.y.reduce(
		// 					(sum: number, next_number: number) => sum + next_number,
		// 					0,
		// 				);
		// 			break;

		// 		case "month":
		// 			state.business_dashboard.search_analytics_response.graph_screen_x_axis =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.last_30_days.x;
		// 			state.business_dashboard.search_analytics_response.graph_screen_stat_total =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.last_30_days.y.reduce(
		// 					(sum: number, next_number: number) => sum + next_number,
		// 					0,
		// 				);
		// 			break;

		// 		case "year":
		// 			state.business_dashboard.search_analytics_response.graph_screen_x_axis =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.last_12_months.x;
		// 			state.business_dashboard.search_analytics_response.graph_screen_stat_total =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.last_12_months.y.reduce(
		// 					(sum: number, next_number: number) => sum + next_number,
		// 					0,
		// 				);
		// 			break;

		// 		case "all":
		// 			state.business_dashboard.search_analytics_response.graph_screen_x_axis =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.all.x;
		// 			state.business_dashboard.search_analytics_response.graph_screen_stat_total =
		// 				state.business_dashboard.search_analytics_response.graph_screen_temp_data.all.y.reduce(
		// 					(sum: number, next_number: number) => sum + next_number,
		// 					0,
		// 				);
		// 			break;

		// 		case "custom":
		// 			state.business_dashboard.search_analytics_response.graph_screen_x_axis = [];
		// 			state.business_dashboard.search_analytics_response.graph_screen_stat_total = 0;
		// 			break;

		// 		default:
		// 			state.business_dashboard.search_analytics_response.graph_screen_x_axis = [];
		// 			state.business_dashboard.search_analytics_response.graph_screen_stat_total = 0;
		// 			break;
		// 	}
		// } else {
		// 	state.business_dashboard.search_analytics_response.graph_screen_y_axis = [];
		// 	state.business_dashboard.search_analytics_response.graph_screen_x_axis = [];
		// 	state.business_dashboard.search_analytics_response.graph_screen_stat_total = 0;
		// }
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardAnalyticsFilterStartDateX = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.filter_start_date_x = action.payload;
	},
	prepare: (payload: string | Date) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardAnalyticsFilterEndDateX = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.filter_end_date_x = action.payload;
	},
	prepare: (payload: string | Date) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardUserViewTabValue = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.user_view_tab_value = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const FetchBusinessDashboardAnalyticsProfileViews = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.overview_profile_views = action.payload.view_count;
		state.business_dashboard.search_analytics_response.overview_profile_views_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FetchBusinessDashboardAnalyticsLeadsThisWeek = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.overview_total_listing_leads =
			action.payload.total_leads_this_week;
		state.business_dashboard.search_analytics_response.overview_leads_difference_since_last_week =
			action.payload.difference_since_last_week;
		state.business_dashboard.search_analytics_response.overview_leads_is_difference_negative =
			action.payload.overview_leads_is_difference_negative;
		state.business_dashboard.search_analytics_response.overview_total_listing_leads_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FetchBusinessDashboardAnalyticsListingViewsThisWeek = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.overview_total_listing_views =
			action.payload.total_views_this_week;
		state.business_dashboard.search_analytics_response.overview_views_difference_since_last_week =
			action.payload.difference_since_last_week;
		state.business_dashboard.search_analytics_response.overview_views_is_difference_negative =
			action.payload.overview_views_is_difference_negative;
		state.business_dashboard.search_analytics_response.overview_total_listing_views_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FetchBusinessDashboardAnalyticsMostViewedListing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.overview_most_viewed_listing_id =
			action.payload.most_viewed_property;
		state.business_dashboard.search_analytics_response.overview_most_viewed_listing_views = action.payload.view_count;
		state.business_dashboard.search_analytics_response.overview_most_viewed_listing_views_difference_since_last_week =
			action.payload.difference_since_last_week;
		state.business_dashboard.search_analytics_response.overview_most_viewed_listing_is_difference_negative =
			action.payload.overview_most_viewed_listing_is_difference_negative;
		state.business_dashboard.search_analytics_response.overview_most_viewed_listing_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FetchBusinessDashboardAnalyticsMostLeadsGeneratingListing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.overview_most_leads_generated_listing_id =
			action.payload.most_leads_generated_property;
		state.business_dashboard.search_analytics_response.overview_most_leads_generated_listing_leads =
			action.payload.leads_count;
		state.business_dashboard.search_analytics_response.overview_most_leads_generated_listing_leads_difference_since_last_week =
			action.payload.difference_since_last_week;
		state.business_dashboard.search_analytics_response.overview_most_leads_generated_listing_is_difference_negative =
			action.payload.overview_most_leads_generated_listing_is_difference_negative;
		state.business_dashboard.search_analytics_response.overview_most_leads_generated_listing_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FetchBusinessDashboardAnalyticsMostSavedListing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.overview_most_saved_listing_id =
			action.payload.most_saved_listing;
		state.business_dashboard.search_analytics_response.overview_most_saved_listing_saves = action.payload.save_count;
		state.business_dashboard.search_analytics_response.overview_most_saved_listing_saves_difference_since_last_week =
			action.payload.difference_since_last_week;
		state.business_dashboard.search_analytics_response.overview_most_saved_listing_is_difference_negative =
			action.payload.overview_most_saved_listing_is_difference_negative;
		state.business_dashboard.search_analytics_response.overview_most_saved_listing_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FetchBusinessDashboardAnalyticsGraph = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.graph_screen_temp_data = action.payload.graph_data;

		if (action.payload.graph_data) {
			switch (state.business_dashboard.search_analytics_response.filter_graph_duration_x) {
				case "day":
					state.business_dashboard.search_analytics_response.graph_screen_y_axis =
						action.payload.graph_data.last_24_hours.y;
					state.business_dashboard.search_analytics_response.graph_screen_x_axis =
						action.payload.graph_data.last_24_hours.x;
					state.business_dashboard.search_analytics_response.graph_screen_stat_total =
						action.payload.graph_data.last_24_hours.y.reduce(
							(sum: number, next_number: number) => sum + next_number,
							0,
						);
					break;

				case "week":
					state.business_dashboard.search_analytics_response.graph_screen_y_axis =
						action.payload.graph_data.last_7_days.y;
					state.business_dashboard.search_analytics_response.graph_screen_x_axis =
						action.payload.graph_data.last_7_days.x;
					state.business_dashboard.search_analytics_response.graph_screen_stat_total =
						action.payload.graph_data.last_7_days.y.reduce((sum: number, next_number: number) => sum + next_number, 0);
					break;

				case "month":
					state.business_dashboard.search_analytics_response.graph_screen_y_axis =
						action.payload.graph_data.last_30_days.y;
					state.business_dashboard.search_analytics_response.graph_screen_x_axis =
						action.payload.graph_data.last_30_days.x;
					state.business_dashboard.search_analytics_response.graph_screen_stat_total =
						action.payload.graph_data.last_30_days.y.reduce((sum: number, next_number: number) => sum + next_number, 0);
					break;

				case "year":
					state.business_dashboard.search_analytics_response.graph_screen_y_axis =
						action.payload.graph_data.last_12_months.y;
					state.business_dashboard.search_analytics_response.graph_screen_x_axis =
						action.payload.graph_data.last_12_months.x;
					state.business_dashboard.search_analytics_response.graph_screen_stat_total =
						action.payload.graph_data.last_12_months.y.reduce(
							(sum: number, next_number: number) => sum + next_number,
							0,
						);
					break;

				case "all":
					state.business_dashboard.search_analytics_response.graph_screen_y_axis = action.payload.graph_data.all.y;
					state.business_dashboard.search_analytics_response.graph_screen_x_axis = action.payload.graph_data.all.x;
					state.business_dashboard.search_analytics_response.graph_screen_stat_total =
						action.payload.graph_data.all.y.reduce((sum: number, next_number: number) => sum + next_number, 0);
					break;

				case "custom":
					state.business_dashboard.search_analytics_response.graph_screen_y_axis = [];
					state.business_dashboard.search_analytics_response.graph_screen_x_axis = [];
					state.business_dashboard.search_analytics_response.graph_screen_stat_total = 0;
					break;

				default:
					state.business_dashboard.search_analytics_response.graph_screen_y_axis = [];
					state.business_dashboard.search_analytics_response.graph_screen_x_axis = [];
					state.business_dashboard.search_analytics_response.graph_screen_stat_total = 0;
					break;
			}
		} else {
			state.business_dashboard.search_analytics_response.graph_screen_y_axis = [];
			state.business_dashboard.search_analytics_response.graph_screen_x_axis = [];
			state.business_dashboard.search_analytics_response.graph_screen_stat_total = 0;
		}
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const FetchBusinessDashboardAnalyticsGraphCustomDates = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_analytics_response.graph_screen_y_axis = action.payload.custom_date_data.y;
		state.business_dashboard.search_analytics_response.graph_screen_x_axis = action.payload.custom_date_data.x;
		state.business_dashboard.search_analytics_response.graph_screen_stat_total =
			action.payload.custom_date_data.y.reduce((sum: number, next_number: number) => sum + next_number, 0);
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};
export const CreateNewShares = {
	reducer: (state: StateInterface, action: AnyAction) => {},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchSingleUserSavedItems = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.single_user_profile.search_saved_items_response.trigger_new_search) {
			state.single_user_profile.search_saved_items_response.saved_items_ids = action.payload.saved_item_ids;
			state.single_user_profile.search_saved_items_response.saved_items_count = action.payload.saved_items_count;
		} else {
			state.single_user_profile.search_saved_items_response.saved_items_ids.push(...action.payload.saved_item_ids);
		}
		state.single_user_profile.search_saved_items_response.trigger_new_search = false;
		state.single_user_profile.search_saved_items_response.saved_items_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const setUserSavedItemsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.search_saved_items_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setUserSavedItemsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.search_saved_items_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setUserSavedItemsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.search_saved_items_response.saved_items_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setUserSavedItemsFilterListingType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.search_saved_items_response.filter_listing_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setUserSavedItemsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.single_user_profile.search_saved_items_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateModalOpen = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.ui.modal.open = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserCountryCode = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.country_code = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserPhone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.phone = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserOTP = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.otp = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserIsSignUp = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.is_sign_up = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserIsSignIn = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.is_sign_in = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserIsSignedIn = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.is_signed_in = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserUserId = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.user_id = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserHasReferralCode = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.has_referral_code = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserReferralCode = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.referral_code = action.payload;
		state.login_flow_global_state.referrer.referrer_id = action.payload;
		if (action.payload.startsWith("BGRU")) {
			state.login_flow_global_state.referrer.referrer_type = "bdx";
		} else {
			state.login_flow_global_state.referrer.referrer_type = "user";
		}
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateStageTriggerSource = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.stage.trigger_source = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateActionCallbackUrl = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.action.callback_url = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateActionSignIn = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.action.sign_in = action.payload;
	},
	prepare: (payload: (session: any) => void) => ({
		payload: [
			"session",
			"this.session=session;" +
				payload
					.toString()
					.slice(payload.toString().indexOf("{") + 1, payload.toString().indexOf("}"))
					.trim(),
		],
	}),
};

export const setLoginFlowGlobalStateActionSignUp = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.action.sign_up = action.payload;
	},
	prepare: (payload: (session: any) => void) => ({
		payload: [
			"session",
			"this.session=session;" +
				payload
					.toString()
					.slice(payload.toString().indexOf("{") + 1, payload.toString().indexOf("}"))
					.trim(),
		],
	}),
};
export const setLoginFlowGlobalStateActionSignOut = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.action.sign_out = action.payload;
	},
	prepare: (payload: () => void) => ({
		payload: [
			payload
				.toString()
				.slice(payload.toString().indexOf("{") + 1, payload.toString().indexOf("}"))
				.trim(),
		],
	}),
};

export const setLoginFlowGlobalStateUiType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.ui.type = action.payload;
	},
	prepare: (payload: "modal" | "redirect") => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUiSnackbarOpen = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.ui.snackbar.open = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUiSnackbarMessage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.ui.snackbar.message = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUiSnackbarType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.ui.snackbar.type = action.payload;
	},
	prepare: (payload: "info" | "warning" | "success" | "error") => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserFirstName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.first_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserLastName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.last_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateUserDisplayName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.user.display_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateReferrerFirstName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.referrer.first_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateReferrerLastName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.referrer.last_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateReferrerDisplayName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.referrer.display_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateReferrerCountryCode = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.referrer.country_code = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setLoginFlowGlobalStateReferrerPhone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.login_flow_global_state.referrer.phone = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setUsersSettingsCurrentScreen = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.account_settings_page.current_screen !== action.payload) {
			state.account_settings_page.current_screen = action.payload;
		}
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

/* Chat Reducers */

export const setOpenChatModal = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.open_chat_modal = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setChatScreen = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.screen = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setChatListTab = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.chat_list_tab = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setChatListTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.chat_list.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setChatListInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.chat_list.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setChatListFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.chat_list.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setBroadcastListTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.broadcast_list.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBroadcastListInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.broadcast_list.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBroadcastListLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.broadcast_list.broadcasts_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBroadcastListFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.broadcast_list.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setChatMessagesTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.current_chat.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setChatMessagesInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.current_chat.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setChatMessagesLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.chat_list.chats_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setChatMessagesFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.current_chat.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchUserChats = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/*
		 * Chat audit I8, 17 Oct 2026: a 401 / 403 / failure answers no list (payload null or
		 * missing), so only the loading flag is reset. The route's "No chats found!" answer still
		 * carries an empty list and is applied as before, whatever its status.
		 */
		const data = action.payload?.payload;
		if (!data || !Array.isArray(data.chats)) {
			state.chat_states.chat_list.chats_loading = false;
			return;
		}

		if (state.chat_states.chat_list.trigger_new_search) {
			state.chat_states.chat_list.chats = data.chats;
			state.chat_states.chat_list.chats_count = data.chats_count;
		} else {
			// A row already listed (the list moved while paging) is not added twice (chat audit I7).
			const listedChatIds: Set<string> = new Set(
				state.chat_states.chat_list.chats.map((chat: any) => String(chat?._id || "")),
			);
			state.chat_states.chat_list.chats.push(
				...data.chats.filter((chat: any) => !listedChatIds.has(String(chat?._id || ""))),
			);
		}

		/* The badge is the app's number (chat audit Q11): page 1's total. Later pages answer 0, which
		   used to drop the badge whenever the list scrolled. */
		if (data.page === undefined || Number(data.page) === 1) {
			state.chat_states.chat_list.total_unread_count = Number(data.total_unread_count || 0);
		}
		state.chat_states.chat_list.trigger_new_search = false;
		state.chat_states.chat_list.chats_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			chats: Array<ChatInterface>;
			chats_count: number;
			total_unread_count: number;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchReceivedBroadcasts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/*
		 * Chat audit I8, 17 Oct 2026: an answer with no list (payload null, or the validation
		 * failure that echoes the business id) only resets the loading flag. The route's
		 * refusal deliberately carries an empty list and is still applied, as it documents.
		 */
		const data = action.payload?.payload;
		if (!data || !Array.isArray(data.broadcasts)) {
			state.chat_states.broadcast_list.broadcasts_loading = false;
			return;
		}

		if (state.chat_states.broadcast_list.trigger_new_search) {
			state.chat_states.broadcast_list.broadcasts = data.broadcasts;
			state.chat_states.broadcast_list.broadcast_ids = data.broadcast_ids;
			state.chat_states.broadcast_list.broadcasts_count = data.broadcasts_count;
			state.chat_states.broadcast_list.pending_broadcasts_count = data.pending_broadcasts_count;
		} else {
			// The route sends no broadcast_ids today; spreading undefined would throw.
			state.chat_states.broadcast_list.broadcast_ids?.push(...(data.broadcast_ids ?? []));
			// A row already listed is not added twice (chat audit I7).
			const listedBroadcastIds: Set<string> = new Set(
				state.chat_states.broadcast_list.broadcasts.map((broadcast: any) => String(broadcast?._id || "")),
			);
			state.chat_states.broadcast_list.broadcasts.push(
				...data.broadcasts.filter((broadcast: any) => !listedBroadcastIds.has(String(broadcast?._id || ""))),
			);
		}

		state.chat_states.broadcast_list.trigger_new_search = false;
		state.chat_states.broadcast_list.broadcasts_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			broadcasts: Array<BroadcastInterface>;
			broadcast_ids: Array<string | ObjectId>;
			broadcasts_count: number;
			pending_broadcasts_count: number;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const removeReceivedBroadcastFromList = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* An invite ignored from the chat drawer (18 Oct 2026): pending invites are rows of the chat
		   list (lib/chat/userChatListCore.ts), so its row leaves the list, with its unread. */
		const rowId: string = String(action.payload?.broadcast_id ?? "");
		const row: any = state.chat_states.chat_list.chats.find((chat: any) => String(chat._id ?? "") === rowId);
		if (row) {
			state.chat_states.chat_list.chats = state.chat_states.chat_list.chats.filter(
				(chat: any) => String(chat._id ?? "") !== rowId,
			);
			state.chat_states.chat_list.chats_count = Math.max(0, state.chat_states.chat_list.chats_count - 1);
			const unread: number = Number(
				(row.unread_messages_count || []).reduce((sum: number, count: any) => sum + Number(count?.unread_messages_count || 0), 0),
			);
			state.chat_states.chat_list.total_unread_count = Math.max(0, state.chat_states.chat_list.total_unread_count - unread);
		}
	},
	prepare: (payload: { broadcast_id: string | ObjectId; invite_status?: string }) => ({
		payload: payload,
	}),
};

export const acceptedBroadcast = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* An invite accepted from the chat drawer (18 Oct 2026): its pending row leaves the list, and
		   the list reloads its first page when it shows again, bringing the new chat as a real row. */
		const rowId: string = String(action.payload?.broadcast_id ?? "");
		state.chat_states.chat_list.chats = state.chat_states.chat_list.chats.filter(
			(chat: any) => String(chat._id ?? "") !== rowId,
		);
		state.chat_states.chat_list.initial_load_done = false;
	},
	prepare: (payload: { broadcast_id: string | ObjectId; chat_id: string | ObjectId }) => ({
		payload: payload,
	}),
};

export const fetchSentBroadcasts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.business_dashboard.property_broadcast_response.trigger_new_search) {
			state.business_dashboard.property_broadcast_response.broadcast_ids = action.payload.payload.broadcast_ids;
			state.business_dashboard.property_broadcast_response.broadcasts_count = action.payload.payload.broadcasts_count;
			// The status chips' counts ride on every page-one fetch (broadcast/fetch-sent-broadcasts).
			if (action.payload.payload.status_counts) {
				state.business_dashboard.property_broadcast_response.status_counts = action.payload.payload.status_counts;
			}
		} else {
			state.business_dashboard.property_broadcast_response.broadcast_ids.push(...action.payload.payload.broadcast_ids);
		}

		state.business_dashboard.property_broadcast_response.trigger_new_search = false;
		state.business_dashboard.property_broadcast_response.broadcasts_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			broadcast_ids: Array<string | ObjectId>;
			broadcasts_count: number;
			status_counts?: { all: number; active: number; in_review: number; draft: number };
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchChatMessages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// Chat audit I8, 17 Oct 2026: a 401 / 403 / failure has no page, so only the loading flag resets.
		const data = action.payload?.payload;
		if (!data || !Array.isArray(data.messages)) {
			state.chat_states.current_chat.messages_loading = false;
			return;
		}

		// A first page replaces the thread (and brings the quick replies, page 1 only).
		const isFirstPage: boolean = state.chat_states.current_chat.trigger_new_search === true;

		if (state.chat_states.current_chat.trigger_new_search) {
			state.chat_states.current_chat.messages = data.messages;
			state.chat_states.current_chat.messages_count = data.messages_count;
		} else {
			/*
			 * Chat audit I9, 17 Oct 2026: paging is skip-based, so after new messages arrive a page
			 * repeats rows already in the thread. Append only the ones not there yet.
			 */
			const knownIds: Set<string> = new Set(
				state.chat_states.current_chat.messages.map((message) => String(message._id)),
			);
			state.chat_states.current_chat.messages.push(
				...data.messages.filter((message: ChatMessageInterface) => !knownIds.has(String(message._id))),
			);
		}

		// A blocked pair freezes the composer (chat/fetch-messages answers it on every page).
		if (action.payload.status === "success") {
			state.chat_states.current_chat.is_blocked = data.is_blocked === true;
			// The viewer's own settings (lib/chat/chatSettings.ts): mute, and their side's block.
			state.chat_states.current_chat.is_muted = data.is_muted === true;
			state.chat_states.current_chat.my_block_id = typeof data.my_block_id === "string" ? data.my_block_id : "";
			// "Share my contact" only for a business with a phone of its own (19 Oct 2026).
			state.chat_states.current_chat.can_share_contact = data.can_share_contact === true;
			// The app's fields (chat audit 2.2): a request to accept, a read-only thread, the pair's chats.
			state.chat_states.current_chat.is_request = data.is_request === true;
			state.chat_states.current_chat.is_read_only = data.is_read_only === true;
			if (Array.isArray(data.chat_ids)) {
				state.chat_states.current_chat.chat_ids = data.chat_ids.map((id: any) => String(id));
			}
			if (data.canonical_chat_id) {
				state.chat_states.current_chat.canonical_chat_id = String(data.canonical_chat_id);
			}
			if (isFirstPage) {
				state.chat_states.current_chat.unread_count = Number(data.unread_count) > 0 ? Number(data.unread_count) : 0;
				state.chat_states.current_chat.quick_replies = Array.isArray(data.quick_replies)
					? data.quick_replies.filter((reply: any) => typeof reply === "string")
					: [];
			}
		}

		state.chat_states.current_chat.trigger_new_search = false;
		state.chat_states.current_chat.messages_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			messages: Array<ChatMessageInterface>;
			messages_count: number;
			is_blocked?: boolean;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const setCurrentChat = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* Update current chat state with selected chat details */

		/*
		 * Chat audit I2, 17 Oct 2026: opening a different chat must not carry the last one's
		 * messages (they showed under a broadcast request card, and the old count drove load more
		 * and quick replies). The same chat set again keeps its thread.
		 */
		const previousChatId: string = String(state.chat_states.current_chat.chat_id ?? "");
		const isSameChat: boolean = previousChatId !== "" && previousChatId === String(action.payload.chat_id ?? "");

		state.chat_states.current_chat = {
			...state.chat_states.current_chat,
			...(isSameChat ? {} : { messages: [], messages_count: 0 }),
			chat_id: action.payload.chat_id,
			chat_type: action.payload.chat_type,
			chat_title: action.payload.chat_title,
			chat_description: action.payload.chat_description,
			chat_dp: action.payload.chat_dp,
			is_broadcast: action.payload.chat_is_broadcast,
			notification_settings: action.payload.chat_notification_settings,
			participants: action.payload.chat_participants,
			created_at: action.payload.chat_created_at,
			// Learnt again from this chat's fetch-messages; never carried over from the last chat.
			is_blocked: false,
			is_muted: false,
			my_block_id: "",
			can_share_contact: false,
			// The list row already knows whether it is a request, so the Accept bar shows at once.
			is_request: action.payload.is_request === true,
			is_read_only: false,
			chat_ids: [String(action.payload.chat_id ?? "")],
			canonical_chat_id: String(action.payload.chat_id ?? ""),
			quick_replies: [],
			unread_count: 0,
			// A reply never carries over to another chat, nor an edit.
			reply_to: isSameChat ? state.chat_states.current_chat.reply_to ?? null : null,
			editing: isSameChat ? state.chat_states.current_chat.editing ?? null : null,
			messages_loading: false,
			initial_load_done: false,
			filter_page_number: 1,
			chat_source_collection: action.payload.chat_source_collection,
			chat_last_message: action.payload.chat_last_message,
			chat_broadcast_id: action.payload.chat_broadcast_id,
			property_id: action.payload.property_id,
			invite_status: action.payload.invite_status,
			has_unread_message: action.payload.has_unread_message,
		};

		/* Reset unread count for this chat in chat list */
		const chatIndex = state.chat_states.chat_list.chats.findIndex(
			(chat) => chat._id.toString() === action.payload.chat_id.toString(),
		);
		if (chatIndex !== -1) {
			// Find the current user's unread count entry and reset it
			state.chat_states.chat_list.chats[chatIndex].unread_messages_count = state.chat_states.chat_list.chats[
				chatIndex
			].unread_messages_count.map((count) => {
				if (
					(count.is_business_profile &&
						count.business_profile_id.toString() === state.profile_context.business_profile_id) ||
					(!count.is_business_profile && count.user_id.toString() === state.profile_context.user_id)
				) {
					state.chat_states.chat_list.total_unread_count -= count.unread_messages_count;
					return {
						...count,
						unread_messages_count: 0,
					};
				}
				return count;
			});
		}

		/* Set screen to 'chat' to show chat messages */

		state.chat_states.screen = "chat";
	},
	prepare: (payload: {
		chat_id: string | ObjectId;
		chat_type: string;
		chat_title: string;
		chat_description: string;
		chat_dp: ProfilePicture;
		chat_is_broadcast: boolean;
		chat_notification_settings: NotificationSettingInterface;
		chat_participants: Array<Participants>;
		chat_created_at: Date | string;
		chat_source_collection?: string | undefined;
		chat_last_message?: LastMessageInterface | undefined;
		chat_broadcast_id: string | ObjectId | undefined;
		property_id?: string | ObjectId | undefined;
		invite_status?: string | undefined;
	}) => ({
		payload: payload,
	}),
};

export const createNewBroadcast = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// state.broadcast_states = action.payload;
		state.business_dashboard.property_broadcast_response.broadcasted_property_id = action.payload;
	},
	prepare: (payload: any) => ({
		payload: payload.payload,
	}),
};

export const sendChatMessage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// Chat audit I8, 17 Oct 2026: a 401 / 403 / failure (payload null) adds nothing.
		if (action.payload?.status === "success" && action.payload.payload?.message) {
			// Update current chat messages
			state.chat_states.current_chat.messages = [
				action.payload.payload.message,
				...state.chat_states.current_chat.messages,
			];
			state.chat_states.current_chat.messages_count++;

			// Update chat in chat list (the chat is typed as nullable; without it there is nothing to move)
			const sentChatId: string | null = action.payload.payload.chat?._id
				? action.payload.payload.chat._id.toString()
				: null;
			const existingChatIndex = sentChatId
				? state.chat_states.chat_list.chats.findIndex((chat) => chat._id.toString() === sentChatId)
				: -1;

			if (existingChatIndex !== -1) {
				// Remove chat from current position
				state.chat_states.chat_list.chats.splice(existingChatIndex, 1);
				// Add updated chat with new last message to beginning of array
				state.chat_states.chat_list.chats.unshift({
					...action.payload.payload.chat,
					last_message: action.payload.payload.chat.last_message,
				});
			}
		}
		// The server refused the send because the two sides are blocked: freeze the composer.
		if (action.payload?.error === "You can't message this profile") {
			state.chat_states.current_chat.is_blocked = true;
		}
		state.chat_states.current_chat.messages_loading = false;
		state.chat_states.current_chat.pending_uploads = [];
		state.chat_states.current_chat.selected_files = [];
		state.chat_states.current_chat.current_message = "";
	},
	prepare: (payload: {
		status: string;
		payload: {
			chat: ChatInterface | null;
			message: ChatMessageInterface | null;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};
export const deleteChat = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// Chat audit I8, 17 Oct 2026: a 401 / 403 / failure (payload null) removes nothing.
		if (action.payload?.status === "success" && action.payload.payload?.delete_chat?._id) {
			// Remove chat from chat list
			state.chat_states.chat_list.chats = state.chat_states.chat_list.chats.filter(
				(chat) => chat._id.toString() !== action.payload.payload.delete_chat._id.toString(),
			);

			// Decrease chat count
			state.chat_states.chat_list.chats_count--;

			// Change screen to chat list
			state.chat_states.screen = "chat_list";

			// Reset current chat
			state.chat_states.current_chat = {
				...state.chat_states.current_chat,
				chat_id: "",
				chat_title: "",
				chat_description: "",
				participants: [],
				messages: [],
				messages_count: 0,
				messages_loading: false,
				trigger_new_search: false,
				initial_load_done: false,
				filter_page_number: 1,
			};
		}
		state.chat_states.current_chat.messages_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			delete_chat: ChatInterface | null;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

/*

* A message deleted from the open conversation, applied once the server has said yes
* (deleteChatMessageThunk dispatches this only on success).
* "everyone": the bubble stays where it is and becomes the deleted bubble, WhatsApp style. Its
* text, files and cards are dropped, quotes of it read "This message was deleted", and the
* chat list preview follows when it was the newest message.
* "me": the message leaves this viewer's thread, and quotes of it lose their text.

*/

export const deleteChatMessage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const messageId: string = String(action.payload.message_id);
		const messages: Array<ChatMessageInterface> = state.chat_states.current_chat.messages;
		const index: number = messages.findIndex((message) => String(message._id) === messageId);

		if (action.payload.delete_for === "everyone") {
			if (index !== -1) {
				messages[index].message_type = "deleted";
				messages[index].is_deleted = true;
				messages[index].body = "";
				messages[index].attachments = [];
				messages[index].is_broadcast = false;
				messages[index].reply_preview = undefined;

				/* The newest message in the thread is the chat list's preview. */

				if (index === 0) {
					const chatIndex: number = state.chat_states.chat_list.chats.findIndex(
						(chat) => String(chat._id) === String(state.chat_states.current_chat.chat_id),
					);
					if (chatIndex !== -1 && state.chat_states.chat_list.chats[chatIndex].last_message) {
						state.chat_states.chat_list.chats[chatIndex].last_message.body = "This message was deleted";
					}
				}
			}
			messages.forEach((message) => {
				if (message.reply_preview && String(message.reply_preview.message_id) === messageId) {
					message.reply_preview = { ...message.reply_preview, is_deleted: true, body: "" };
				}
			});
		} else {
			if (index !== -1) {
				messages.splice(index, 1);
				state.chat_states.current_chat.messages_count = Math.max(0, state.chat_states.current_chat.messages_count - 1);
			}
			messages.forEach((message) => {
				if (message.reply_preview && String(message.reply_preview.message_id) === messageId) {
					message.reply_preview = { ...message.reply_preview, body: "", is_available: false };
				}
			});
		}
	},
	prepare: (payload: { message_id: string | ObjectId; delete_for: "everyone" | "me" }) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardPropertyBroadcastTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_broadcast_response.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardPropertyBroadcastInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_broadcast_response.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardPropertyBroadcastsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_broadcast_response.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchRealtimeNewChatMessage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// Chat audit I8, 17 Oct 2026: a 401 / 403 / failure (payload null) changes nothing.
		if (action.payload?.status === "success" && action.payload.payload?.message && action.payload.payload.chat) {
			/* One conversation per pair (chat audit 2.2): the open thread and a list row cover every
			   chat of the pair, so a message in any of them belongs to them. */
			const incomingChat: any = action.payload.payload.chat;
			const incomingChatId: string = String(incomingChat._id);
			const openChatIds: Array<string> = [
				String(state.chat_states.current_chat.chat_id ?? ""),
				...(state.chat_states.current_chat.chat_ids || []).map((id) => String(id)),
			];

			// Add message to current chat if it's the active chat
			if (
				state.chat_states.current_chat.chat_id &&
				openChatIds.includes(incomingChatId) &&
				state.chat_states.screen === "chat"
			) {
				// Check if message already exists
				const existingMessageIndex = state.chat_states.current_chat.messages.findIndex(
					(msg) => msg._id.toString() === action.payload.payload.message._id.toString(),
				);

				if (existingMessageIndex !== -1) {
					// Replace existing message
					state.chat_states.current_chat.messages[existingMessageIndex] = action.payload.payload.message;
				} else {
					// Add new message and sort by created_at in descending order (newest first)
					state.chat_states.current_chat.messages = [
						action.payload.payload.message,
						...state.chat_states.current_chat.messages,
					];
					state.chat_states.current_chat.messages_count++;
				}
			}

			/* Update chat list (chat audit 2.2). A row is one conversation (lib/chat/userChatListCore.ts):
			   it is found by its own id or any chat of its group, and keeps its id, group and request
			   flag; only the newest message, the time and the viewer's unread count move. The badge
			   total moves by the same amount. */
			const profileContext: any = (state as any).profile_context;
			const isViewerEntry = (entry: any): boolean =>
				!!profileContext &&
				(profileContext.is_business_profile === true
					? entry?.is_business_profile === true &&
						String(entry?.business_profile_id) === String(profileContext.business_profile_id)
					: entry?.is_business_profile !== true && String(entry?.user_id) === String(profileContext.user_id));
			const viewerUnreadIn = (entries: any): number => {
				const list: Array<any> = Array.isArray(entries) ? entries : [];
				const entry: any = list.find(isViewerEntry) || (list.length === 1 ? list[0] : null);
				return Number(entry?.unread_messages_count || 0);
			};
			const rows: Array<any> = state.chat_states.chat_list.chats as Array<any>;
			const existingChatIndex = rows.findIndex(
				(chat: any) =>
					String(chat._id) === incomingChatId ||
					(chat.group_chat_ids || []).some((id: any) => String(id) === incomingChatId),
			);
			const incomingUnread: number = viewerUnreadIn(incomingChat.unread_messages_count);

			if (existingChatIndex !== -1) {
				const row: any = rows[existingChatIndex];
				const previousUnread: number = viewerUnreadIn(row.unread_messages_count);
				// The canonical chat carries the conversation's count; a sibling only adds to it.
				const nextUnread: number =
					String(row._id) === incomingChatId ? incomingUnread : Math.max(previousUnread, incomingUnread);
				const rowEntries: Array<any> = Array.isArray(row.unread_messages_count) ? row.unread_messages_count : [];
				const hasViewerEntry: boolean = rowEntries.some(isViewerEntry) || rowEntries.length === 1;
				// Remove chat from current position
				rows.splice(existingChatIndex, 1);
				// Add the updated row to the beginning of the array
				rows.unshift({
					...row,
					last_message: incomingChat.last_message,
					updated_at: incomingChat.updated_at,
					// The server's preview was for the older message; the card falls back to the new body.
					last_message_preview: undefined,
					unread_messages_count: hasViewerEntry
						? rowEntries.map((entry: any) =>
								isViewerEntry(entry) || rowEntries.length === 1
									? { ...entry, unread_messages_count: nextUnread }
									: entry,
							)
						: incomingChat.unread_messages_count,
				});
				state.chat_states.chat_list.total_unread_count = Math.max(
					0,
					(state.chat_states.chat_list.total_unread_count || 0) + nextUnread - previousUnread,
				);
			} else {
				// Add new chat to beginning of chats array
				rows.unshift(incomingChat);
				state.chat_states.chat_list.chats_count++;
				state.chat_states.chat_list.total_unread_count = (state.chat_states.chat_list.total_unread_count || 0) + incomingUnread;
			}
		}
	},
	prepare: (payload: {
		status: string;
		payload: {
			chat: ChatInterface | null;
			message: ChatMessageInterface | null;
			current_screen: string;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};
/* A chat request the viewer accepted (chat/accept-chat-request, chat audit 2.2): the composer opens
   and the list row stops reading as a request. */
/* The viewer just blocked the other side of the open chat (chat audit Q12): the composer freezes. */
/* The message the composer replies to (chat audit I13), or null to stop replying. */
export const setChatReplyTo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.current_chat.reply_to = action.payload || null;
		// One strip at a time: a reply ends an edit.
		if (action.payload) state.chat_states.current_chat.editing = null;
	},
	prepare: (payload: { message_id: string; sender_name: string; body: string } | null) => ({
		payload: payload,
	}),
};

/* The message the composer edits (18 Oct 2026), or null to stop. Starting an edit ends a reply. */
export const setChatEditing = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.current_chat.editing = action.payload || null;
		if (action.payload) state.chat_states.current_chat.reply_to = null;
	},
	prepare: (payload: { message_id: string; body: string } | null) => ({
		payload: payload,
	}),
};

/* A message's text after an edit was saved (18 Oct 2026): the bubble shows it with "Edited". */
export const applyMessageEdit = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const message: any = state.chat_states.current_chat.messages.find(
			(m: any) => String(m._id) === String(action.payload?.message_id ?? ""),
		);
		if (message) {
			message.body = String(action.payload?.body ?? message.body);
			message.is_edited = true;
		}
	},
	prepare: (payload: { message_id: string; body: string }) => ({
		payload: payload,
	}),
};

/* The chat list's total with no search term (18 Oct 2026): search is offered above 5. */
export const setChatListAllCount = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.chat_states.chat_list.all_chats_count = Number(action.payload || 0);
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

/* The other side of [chat_ids] started ([typing] true) or stopped typing (18 Oct 2026). */
export const setChatTyping = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const ids: Array<string> = (action.payload?.chat_ids || []).map((id: any) => String(id));
		const current: Array<string> = state.chat_states.typing_chat_ids || [];
		state.chat_states.typing_chat_ids =
			action.payload?.typing === true
				? Array.from(new Set([...current, ...ids]))
				: current.filter((id) => !ids.includes(id));
	},
	prepare: (payload: { chat_ids: Array<string>; typing: boolean }) => ({
		payload: payload,
	}),
};

/* The open chat's own settings changed from its header (18 Oct 2026): mute, block, unblock. */
export const setCurrentChatSettings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const current: any = state.chat_states.current_chat;
		if (typeof action.payload?.is_muted === "boolean") current.is_muted = action.payload.is_muted;
		if (typeof action.payload?.is_blocked === "boolean") current.is_blocked = action.payload.is_blocked;
		if (typeof action.payload?.my_block_id === "string") current.my_block_id = action.payload.my_block_id;
	},
	prepare: (payload: { is_muted?: boolean; is_blocked?: boolean; my_block_id?: string }) => ({
		payload: payload,
	}),
};

/* The open chat's receipts re-read after a `chat-read` / `chat-delivered` frame (18 Oct 2026). They
   only move forward, and in order: every own message up to the newest one served as delivered (or
   read) is delivered (or read) too, the app's rule. Messages are newest first. */
export const setMessageReceipts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const served: Array<any> = Array.isArray(action.payload?.served) ? action.payload.served : [];
		const viewer: any = action.payload?.viewer || {};
		const isOwn = (message: any): boolean =>
			viewer.is_business_profile === true
				? message?.sender?.is_business_profile === true &&
					String(message?.sender?.business_profile_id?._id ?? message?.sender?.business_profile_id) === String(viewer.business_profile_id)
				: message?.sender?.is_business_profile !== true &&
					String(message?.sender?.user_id?._id ?? message?.sender?.user_id) === String(viewer.user_id);
		const messages: Array<any> = state.chat_states.current_chat.messages as Array<any>;
		for (const flag of ["is_delivered", "is_read"]) {
			const reached = new Set(served.filter((m) => m?.[flag] === true).map((m) => String(m._id)));
			const newest = messages.findIndex((m) => reached.has(String(m._id)) && isOwn(m));
			if (newest < 0) continue;
			for (let i = newest; i < messages.length; i += 1) {
				if (!isOwn(messages[i])) continue;
				messages[i][flag] = true;
				if (flag === "is_read") messages[i].is_delivered = true;
			}
		}
	},
	prepare: (payload: { served: Array<any>; viewer: { is_business_profile: boolean; user_id: string; business_profile_id: string } }) => ({
		payload: payload,
	}),
};

/* One message as the server serves it now (18 Oct 2026): a `message-deleted` frame re-read, so the
   bubble turns into its tombstone. Only a message already in the open thread is replaced. */
export const replaceChatMessage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const served: any = action.payload;
		if (!served || !served._id) return;
		const messages: Array<any> = state.chat_states.current_chat.messages as Array<any>;
		const index: number = messages.findIndex((m: any) => String(m._id) === String(served._id));
		if (index >= 0) messages[index] = served;
	},
	prepare: (payload: ChatMessageInterface) => ({
		payload: payload,
	}),
};

/* The list's unread counts, previews and badge from a fresh page 1 (18 Oct 2026), after a
   `chat-read` frame for a chat that is not open (the viewer's side read it on another device or
   a teammate did) or a deleted last message. Rows are updated in place; nothing is added,
   removed or reordered, and the loading flags are left alone. */
export const syncChatListUnread = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const data: any = action.payload?.payload;
		if (!data || !Array.isArray(data.chats)) return;
		const served: Map<string, any> = new Map(data.chats.map((chat: any) => [String(chat?._id || ""), chat]));
		state.chat_states.chat_list.chats.forEach((row: any) => {
			const fresh: any = served.get(String(row?._id || ""));
			if (!fresh) return;
			if (Array.isArray(fresh.unread_messages_count)) row.unread_messages_count = fresh.unread_messages_count;
			if (fresh.last_message) row.last_message = fresh.last_message;
		});
		state.chat_states.chat_list.total_unread_count = Number(data.total_unread_count || 0);
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

/* One message's reactions changed (a reaction made here, or a `message-reaction` frame re-read). */
export const setMessageReactions = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const messageId: string = String(action.payload?.message_id ?? "");
		const message: any = state.chat_states.current_chat.messages.find((m: any) => String(m._id) === messageId);
		if (message) message.reaction_summary = Array.isArray(action.payload?.reaction_summary) ? action.payload.reaction_summary : [];
	},
	prepare: (payload: { message_id: string; reaction_summary: Array<{ emoji: string; count: number; by_viewer: boolean }> }) => ({
		payload: payload,
	}),
};

export const chatRequestAccepted = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const chatId: string = String(action.payload?.chat_id ?? "");
		const current: any = state.chat_states.current_chat;
		if (String(current.chat_id ?? "") === chatId || (current.chat_ids || []).includes(chatId)) {
			current.is_request = false;
		}
		(state.chat_states.chat_list.chats as Array<any>).forEach((chat: any) => {
			if (String(chat._id) === chatId || (chat.group_chat_ids || []).some((id: any) => String(id) === chatId)) {
				chat.is_request = false;
			}
		});
	},
	prepare: (payload: { chat_id: string }) => ({
		payload: payload,
	}),
};

/* A chat request the viewer declined (chat/decline-chat-request, 19 Oct 2026): its row leaves the
   list (matched by the chat or any chat of its group) and, when it is the open chat, the thread
   closes back to the list, as after deleting a chat. */
export const chatRequestDeclined = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const chatId: string = String(action.payload?.chat_id ?? "");
		if (!chatId) return;
		const before: number = state.chat_states.chat_list.chats.length;
		state.chat_states.chat_list.chats = (state.chat_states.chat_list.chats as Array<any>).filter(
			(chat: any) => !(String(chat._id) === chatId || (chat.group_chat_ids || []).some((id: any) => String(id) === chatId)),
		);
		const removed: number = before - state.chat_states.chat_list.chats.length;
		if (removed > 0) state.chat_states.chat_list.chats_count = Math.max(0, state.chat_states.chat_list.chats_count - removed);
		const current: any = state.chat_states.current_chat;
		if (String(current.chat_id ?? "") === chatId || (current.chat_ids || []).includes(chatId)) {
			state.chat_states.screen = "chat_list";
			state.chat_states.current_chat = {
				...state.chat_states.current_chat,
				chat_id: "",
				chat_title: "",
				chat_description: "",
				participants: [],
				messages: [],
				messages_count: 0,
				messages_loading: false,
				trigger_new_search: false,
				initial_load_done: false,
				filter_page_number: 1,
			};
		}
	},
	prepare: (payload: { chat_id: string }) => ({
		payload: payload,
	}),
};

export const setBroadcastForm = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.commission_offered) state.broadcast_data.commission_offered = action.payload.commission_offered;
		if (action.payload.no_of_agents) state.broadcast_data.no_of_agents = action.payload.no_of_agents;
		state.broadcast_data.notes = action.payload.notes || "";
		if (action.payload.open_to_negotiation !== undefined)
			state.broadcast_data.open_to_negotiation = action.payload.open_to_negotiation;
	},
	prepare: (payload: Partial<StateInterface["broadcast_data"]>) => ({
		payload: payload,
	}),
};

export const fetchUserProperties = {
	reducer: (state: StateInterface, action: AnyAction) => {},
	prepare: (payload: {
		status: string;
		payload: {
			properties: Array<ChatInterface>;
			properties_count: number;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchFeatureDiscoveryTracker = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.feature_discovery_tracker = action.payload.payload;
	},
	prepare: (payload: {
		status: string;
		payload: {
			broadcast: {
				viewed: boolean;
				viewed_at: Date | null;
				skipped: boolean;
			};
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const updateFeatureDiscoveryTracker = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.feature_discovery_tracker = action.payload.payload;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const setAiMetaBoostedListingsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.filter_status === "active") {
			state.business_dashboard.search_ai_boosts_response.active_boost.trigger_new_search =
				action.payload.trigger_new_search;
		} else if (action.payload.filter_status === "completed") {
			state.business_dashboard.search_ai_boosts_response.completed_boost.trigger_new_search =
				action.payload.trigger_new_search;
		}
	},
	prepare: (payload: { trigger_new_search: boolean; filter_status: string }) => ({
		payload: payload,
	}),
};

export const setAiMetaBoostedListingsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.filter_status === "active") {
			state.business_dashboard.search_ai_boosts_response.active_boost.initial_load_done =
				action.payload.initial_load_done;
		} else if (action.payload.filter_status === "completed") {
			state.business_dashboard.search_ai_boosts_response.completed_boost.initial_load_done =
				action.payload.initial_load_done;
		}
	},
	prepare: (payload: { initial_load_done: boolean; filter_status: string }) => ({
		payload: payload,
	}),
};

export const fetchRecomendedPropertiesToBoost = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_ai_boosts_response.recommended_properties_to_boost.recommended_properties =
			action.payload.payload.recommended_properties;
		state.business_dashboard.search_ai_boosts_response.recommended_properties_to_boost.recommended_properties_count =
			action.payload.payload.recommended_properties_count;
		state.business_dashboard.search_ai_boosts_response.recommended_properties_to_boost.recommended_properties_loading = false;
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const fetchAiMetaBoostedListings = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_ai_boosts_response.active_boost.active_boost_ids =
			action.payload.payload.active_boost_ids;
		state.business_dashboard.search_ai_boosts_response.completed_boost.completed_boost_ids =
			action.payload.payload.completed_boost_ids;
		state.business_dashboard.search_ai_boosts_response.active_boost.active_boosts_loading = false;
		state.business_dashboard.search_ai_boosts_response.completed_boost.completed_boosts_loading = false;
		state.business_dashboard.search_ai_boosts_response.completed_boost.completed_boosts_count =
			action.payload.payload.completed_boost_count;
		state.business_dashboard.search_ai_boosts_response.active_boost.active_boosts_count =
			action.payload.payload.active_boost_count;
		state.business_dashboard.search_ai_boosts_response.property_count = action.payload.payload.property_count;
		state.business_dashboard.search_ai_boosts_response.boost_load_complete = true;
	},
	prepare: (payload: {
		status: string;
		payload: {
			property_count: number;
			active_boost_ids: Array<string | ObjectId>;
			active_boost_count: number;
			completed_boost_ids: Array<string | ObjectId>;
			completed_boost_count: number;
			filter_status: string;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const setRecommendedPropertiesTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_ai_boosts_response.recommended_properties_to_boost.trigger_new_search =
			action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setRecommendedPropertiesInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_ai_boosts_response.recommended_properties_to_boost.initial_load_done =
			action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

interface AIBoostFormData {
	total_amount: number;
	expected_qualified_leads: number;
	duration: number;
}

export const setBusinessDashboardAiBoostFormBudget = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.ai_boosts.expected_qualified_leads = action.payload.expected_qualified_leads;
		state.ai_boosts.budget.total_budget = action.payload.total_amount;
		state.ai_boosts.budget.meta_budget = action.payload.total_amount;
	},
	prepare: (payload: AIBoostFormData) => ({
		payload: payload,
	}),
};

export const setBusinessDashboardAiBoostFormCustomBudget = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.ai_boosts.client_details.business_name = action.payload.payload.name;
		state.ai_boosts.client_details.poc_phone.phone_number = action.payload.payload.phone.phone_number;
		state.ai_boosts.client_details.poc_phone.country_code = action.payload.payload.phone.country_code;
		state.ai_boosts.client_details.budget = action.payload.payload.budget;
		state.ai_boosts.client_details.contact_preferences = action.payload.payload.contact_preferences;
	},
	prepare: (payload: AiBoostB2BLeadsInterface) => ({
		payload: payload,
	}),
};

export const setAiBoostFormOfflinePayment = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// state.ai_boosts.contact_details.name = action.payload.payload.name;
		// state.ai_boosts.contact_details.phone.phone_number = action.payload.payload.phone.phone_number;
		// state.ai_boosts.contact_details.phone.country_code = action.payload.payload.phone.country_code;
		// state.ai_boosts.contact_details.budget = action.payload.payload.budget;
		// state.ai_boosts.contact_details.contact_preferences = action.payload.payload.contact_preferences;
	},
	prepare: (payload: AiBoostB2BLeadsInterface) => ({
		payload: payload,
	}),
};

export const setAiBoostFormAdCreativeImage = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.ai_boosts.references.website_url = action.payload.website_url;
		state.ai_boosts.references.additional_notes = action.payload.additional_notes;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const setAiBoostFormAdCreativeReferenceImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.ai_boosts.references.creatives = action.payload;
	},
	prepare: (
		payload: Array<{
			file_id: ObjectId | string;
			mime_type: string;
			preview: string;
		}>,
	) => ({
		payload: payload,
	}),
};

export const setAiBoostFormAdCreativeBrandLogo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.ai_boosts.references.logo = action.payload;
	},
	prepare: (payload: { file_id: ObjectId | string; mime_type: string; preview: string }) => ({
		payload: payload,
	}),
};

export const setAiBoostFormPropertyDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.ai_boosts.created_by.business_profile_id = action.payload.property_data.created_by.business_profile_id._id;
		state.ai_boosts.created_by.is_business_profile = action.payload.property_data.created_by.is_business_profile;
		state.ai_boosts.created_by.user_id = action.payload.property_data.created_by.user_id._id;
		state.ai_boosts.property_id = action.payload.property_data._id;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const setAiMetaBoostedListingsFilterStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_ai_boosts_response.filter_status = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setAiMetaBoostedListingsFilterSortField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_ai_boosts_response.filter_sort_field = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setAiMetaBoostedListingsFilterSortOrder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_ai_boosts_response.filter_sort_order = action.payload;
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchAiBoostBannerCardDetailsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.banner_card_details.loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setAiBoostDetailsToNull = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta = {
			filter_selected_platform: "combined",
			loading: true,
			leads_loading: true,
			budget_loading: true,
			impressions_loading: true,
			clicks_loading: true,
			ctr_loading: true,
			property_id: "",
			property_slug: "",
			landing_page_status: "",
			meta_leads_loading: true,
			meta_budget_loading: true,
			meta_impressions_loading: true,
			meta_clicks_loading: true,
			meta_ctr_loading: true,
			google_leads_loading: true,
			google_budget_loading: true,
			google_impressions_loading: true,
			google_clicks_loading: true,
			google_ctr_loading: true,
			banner_card_details: {
				property_id: "",
				boost_status: "",
				active_boost_id: "",
				active_boost_start_date: "",
				active_boost_end_date: "",
				active_boost_budget: 0,
				active_boost_plan: "",
				active_boost_exists: false,
				loading: true,
			},
			all_boosts_list_loading: true,
			all_boosts_list_response_status_code: "",
			all_boosts_list: [],
			filter_selected_boost_id: "",
			filter_start_date: "",
			filter_end_date: "",
			boost_metrics: {
				qualified_leads_count: 0,
				total_budget_spent: 0,
				total_budget: 0,
				total_impressions: 0,
				total_clicks: 0,
				ctr: 0,
			},
			meta_metrics: {
				qualified_leads_count: 0,
				total_budget_spent: 0,
				total_budget: 0,
				total_impressions: 0,
				total_clicks: 0,
				ctr: 0,
			},

			google_metrics: {
				qualified_leads_count: 0,
				total_budget_spent: 0,
				total_budget: 0,
				total_impressions: 0,
				total_clicks: 0,
				ctr: 0,
			},

			display_metrics: {
				qualified_leads_count: 0,
				total_budget_spent: 0,
				total_budget: 0,
				total_impressions: 0,
				total_clicks: 0,
				ctr: 0,
				source_platform: "meta",
			},
			all_campaigns_performance_metrics: {
				loading: true,
				meta_loading: true,
				google_loading: true,
				report_type: "quarterly",
				campaign_wise_list: {
					boosts: [],
				},
				meta_campaign_wise_list: {
					boosts: [],
				},
				google_campaign_wise_list: {
					boosts: [],
				},
				quarterly_list: [
					{
						quarter: "",
						total_budget_spent: 0,
						qualified_leads: 0,
						boosts: [],
					},
				],
				meta_quarterly_list: [
					{
						quarter: "",
						total_budget_spent: 0,
						qualified_leads: 0,
						boosts: [],
					},
				],
				google_quarterly_list: [
					{
						quarter: "",
						total_budget_spent: 0,
						qualified_leads: 0,
						boosts: [],
					},
				],
				date_range_list: {
					boosts: [],
				},
				meta_date_range_list: {
					boosts: [],
				},
				google_date_range_list: {
					boosts: [],
				},
			},
			ad_previews: {
				initial_load_done: false,
				filter_page_number: 1,
				filter_ad_id: "",
				ad_ids: [],
				total_ads_count: 0,
				loading: false,
				ads: [],
			},
			activity_logs: {
				initial_load_done: false,
				activity_logs_loading: true,
				activity_logs: [],
				ai_summary: "",
				filter_page_number: 1,
				total_activity_logs_count: 0,
			},
			google_campaign_lists: [],
			google_campaign_details: {
				qualified_leads_count: 0,
				total_budget_spent: 0,
				total_budget: 0,
				impressions: 0,
				clicks: 0,
				ctr: 0,
				campaign_start_date: "",
				campaign_end_date: "",
			},
		};
	},
	prepare: () => ({
		payload: {}, // Add an empty payload to satisfy the PrepareAction type
	}),
};

export const fetchAiBoostBannerCardDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.banner_card_details = action.payload.payload;
		state.business_dashboard.property_ai_boost_details_meta.filter_selected_boost_id = action.payload.payload
			.active_boost_exists
			? action.payload.payload.active_boost_id
			: "all";
		state.business_dashboard.property_ai_boost_details_meta.banner_card_details.loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			property_id: ObjectId | string;
			boost_status: string;
			active_boost_id: string | ObjectId;
			active_boost_start_date: Date | string;
			active_boost_end_date: Date | string;
			active_boost_budget: number;
			active_boost_plan: string;
			active_boost_exists: boolean;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchAiBoostAllBoostListsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.all_boosts_list_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchAiBoostAllBoostLists = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.statusCode === 200) {
			state.business_dashboard.property_ai_boost_details_meta.all_boosts_list = action.payload.payload.all_boosts_list;
			state.business_dashboard.property_ai_boost_details_meta.all_boosts_list_loading = false;
			state.business_dashboard.property_ai_boost_details_meta.all_boosts_list_response_status_code =
				action.payload.statusCode.toString();
			state.business_dashboard.property_ai_boost_details_meta.landing_page_status =
				action.payload.payload.landing_page_status;
			state.business_dashboard.property_ai_boost_details_meta.property_slug = action.payload.payload.property_slug;
		} else {
			state.business_dashboard.property_ai_boost_details_meta.all_boosts_list_response_status_code =
				action.payload.statusCode.toString();
		}
	},
	prepare: (payload: {
		status: string;
		payload: {
			all_boosts_list: {
				boost_id: string | ObjectId;
				boost_start_date: Date | string;
				boost_end_date: Date | string;
				boost_status: string;
				boost_budget: number;
				boost_plan: string;
			};
			landing_page_status: string;
		};
		missing_fields: string[];
		error: string | null;
		statusCode: number | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchGoogleCampaignsLists = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.statusCode === 200) {
			state.business_dashboard.property_ai_boost_details_meta.google_campaign_lists =
				action.payload.payload.all_boosts_list;
			// state.business_dashboard.property_ai_boost_details_meta.all_boosts_list_loading = false;
			// state.business_dashboard.property_ai_boost_details_meta.all_boosts_list_response_status_code =
			// 	action.payload.statusCode.toString();
			// state.business_dashboard.property_ai_boost_details_meta.landing_page_status =
			// 	action.payload.payload.landing_page_status;
			// state.business_dashboard.property_ai_boost_details_meta.property_slug = action.payload.payload.property_slug;
		} else {
			state.business_dashboard.property_ai_boost_details_meta.all_boosts_list_response_status_code =
				action.payload.statusCode.toString();
		}
	},
	prepare: (payload: {
		status: string;
		payload: {
			all_boosts_list: {
				boost_id: string | ObjectId;
				boost_start_date: Date | string;
				boost_end_date: Date | string;
				boost_status: string;
				boost_budget: number;
				boost_plan: string;
			};
			landing_page_status: string;
		};
		missing_fields: string[];
		error: string | null;
		statusCode: number | null;
	}) => ({
		payload: payload,
	}),
};

export const setMetaAiBoostDetailsSelectedCampaign = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.filter_selected_boost_id = action.payload;
	},
	prepare: (payload: string | ObjectId) => ({
		payload: payload,
	}),
};

export const fetchMetaAiBoostsQualifiedLeads = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.boost_metrics.qualified_leads_count =
			action.payload.payload.qualified_leads_count;
		state.business_dashboard.property_ai_boost_details_meta.leads_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.meta_metrics.qualified_leads_count =
			action.payload.payload.qualified_leads_count;
		state.business_dashboard.property_ai_boost_details_meta.meta_leads_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			qualified_leads_count: number;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchMetaAiBoostsBudgetDetails = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.boost_metrics.total_budget_spent =
			action.payload.payload.total_budget_spent;
		state.business_dashboard.property_ai_boost_details_meta.boost_metrics.total_budget =
			action.payload.payload.total_budget;
		state.business_dashboard.property_ai_boost_details_meta.budget_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.meta_metrics.total_budget_spent =
			action.payload.payload.total_budget_spent;
		state.business_dashboard.property_ai_boost_details_meta.meta_metrics.total_budget =
			action.payload.payload.total_budget;
		state.business_dashboard.property_ai_boost_details_meta.meta_budget_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			total_budget_spent: number;
			total_budget: number;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchMetaAiBoostsCampaignStats = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.boost_metrics.total_impressions =
			action.payload.payload.impressions;
		state.business_dashboard.property_ai_boost_details_meta.boost_metrics.total_clicks = action.payload.payload.clicks;
		state.business_dashboard.property_ai_boost_details_meta.boost_metrics.ctr = action.payload.payload.ctr;
		state.business_dashboard.property_ai_boost_details_meta.impressions_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.clicks_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.ctr_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.meta_metrics.total_impressions =
			action.payload.payload.impressions;
		state.business_dashboard.property_ai_boost_details_meta.meta_metrics.total_clicks = action.payload.payload.clicks;
		state.business_dashboard.property_ai_boost_details_meta.meta_metrics.ctr = action.payload.payload.ctr;
		state.business_dashboard.property_ai_boost_details_meta.meta_impressions_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.meta_clicks_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.meta_ctr_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			impressions: number;
			clicks: number;
			ctr: number;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchGoogleAIBoostCampaignMetrics = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.google_campaign_details.impressions =
			action.payload.payload.impressions;
		state.business_dashboard.property_ai_boost_details_meta.google_campaign_details.clicks =
			action.payload.payload.clicks;
		state.business_dashboard.property_ai_boost_details_meta.google_campaign_details.ctr = action.payload.payload.ctr;
		state.business_dashboard.property_ai_boost_details_meta.impressions_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.clicks_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.ctr_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.google_metrics.total_impressions =
			action.payload.payload.impressions;
		state.business_dashboard.property_ai_boost_details_meta.google_metrics.total_clicks = action.payload.payload.clicks;
		state.business_dashboard.property_ai_boost_details_meta.google_metrics.ctr = action.payload.payload.ctr;
		state.business_dashboard.property_ai_boost_details_meta.google_metrics.total_budget =
			action.payload.payload.total_budget;
		state.business_dashboard.property_ai_boost_details_meta.google_metrics.total_budget_spent =
			action.payload.payload.total_budget_spent;
		state.business_dashboard.property_ai_boost_details_meta.google_impressions_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.google_clicks_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.google_ctr_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.google_budget_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			total_budget_spent: number;
			total_budget: number;
			impressions: number;
			clicks: number;
			ctr: number;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchGoogleAIBoostsQualifiedLeads = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.google_metrics.qualified_leads_count =
			action.payload.payload.qualified_leads_count;
		state.business_dashboard.property_ai_boost_details_meta.google_leads_loading = false;
		// Keep old field too
		state.business_dashboard.property_ai_boost_details_meta.leads_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			qualified_leads_count: number;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const setFilterSelectedPlatform = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.filter_selected_platform = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setMetaAiBoostAdPreviewsFilterPageNumberCount = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.filter_page_number = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.filter_ad_id =
			state.business_dashboard.property_ai_boost_details_meta.ad_previews.ad_ids[action.payload - 1] || "";
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const fetchMetaAiBoostAllAdIds = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.ad_ids = action.payload.payload.ad_ids;
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.total_ads_count =
			action.payload.payload.total_ads_count;
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.filter_ad_id =
			action.payload.payload.ad_ids[0] || "";
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.initial_load_done = true;
	},
	prepare: (payload: {
		status: string;
		payload: {
			ad_ids: Array<string>;
			total_ads_count: number;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchMetaAiBoostsAdCreatives = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.ads = [action.payload.payload];
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.loading = false;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const setMetaCreativesLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.ad_previews.loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setMetaAiBoostActivityLogsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.activity_logs.activity_logs_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchMetaAiBoostActivityLogdData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload.page_number === 1) {
			state.business_dashboard.property_ai_boost_details_meta.activity_logs.activity_logs =
				action.payload.payload.activity_logs;
		} else {
			state.business_dashboard.property_ai_boost_details_meta.activity_logs.activity_logs.push(
				...action.payload.payload.activity_logs,
			);
		}
		state.business_dashboard.property_ai_boost_details_meta.activity_logs.total_activity_logs_count =
			action.payload.payload.total_activity_logs_count;
		state.business_dashboard.property_ai_boost_details_meta.activity_logs.initial_load_done === false
			? (state.business_dashboard.property_ai_boost_details_meta.activity_logs.initial_load_done = true)
			: null;
		state.business_dashboard.property_ai_boost_details_meta.activity_logs.activity_logs_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			activity_logs: Array<{
				action: string;
				action_summary: string;
				action_performed_at: Date | string;
				action_performed_by: string;
			}>;
			total_activity_logs_count: number;
			page_number: number;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const genetateAIBoostActivityLogsSummary = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.activity_logs.activity_logs.unshift(action.payload.payload);
		state.business_dashboard.property_ai_boost_details_meta.activity_logs.activity_logs_loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			action: string;
			action_summary: string;
			action_performed_at: Date | string;
			action_performed_by: string;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchHomepageDashboardStats = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.homePage.dashboard_stats.total_leads_count = action.payload.total_leads_count;
		state.homePage.dashboard_stats.new_leads_count = action.payload.new_leads_count;
		state.homePage.dashboard_stats.published_properties_count = action.payload.published_properties_count;
		state.homePage.dashboard_stats.loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			total_leads_count: number;
			new_leads_count: number;
			published_properties_count: number;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload.payload,
	}),
};

export const updateBroadcastReadStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// Chat audit I8, 17 Oct 2026: prepare passes null for anything but a success with a row.
		if (!action.payload) {
			return;
		}
		if (
			state.chat_states.broadcast_list.broadcasts.find(
				(broadcast) => broadcast.broadcast?._id.toString() === action.payload._id?.toString(),
			)
		) {
			const index = state.chat_states.broadcast_list.broadcasts.findIndex(
				(broadcast) => broadcast.broadcast?._id.toString() === action.payload._id?.toString(),
			);
			if (index !== -1) {
				state.chat_states.broadcast_list.broadcasts[index].unread_messages_count = [];
			}
		}

		if (state.chat_states.chat_list.chats.find((chat) => chat._id?.toString() === action.payload._id?.toString())) {
			const chatIndex = state.chat_states.chat_list.chats.findIndex(
				(chat) => chat._id?.toString() === action.payload._id?.toString(),
			);
			if (chatIndex !== -1) {
				state.chat_states.chat_list.chats[chatIndex].unread_messages_count = [];
			}
		}
	},
	prepare: (payload: {
		status: string;
		payload: {
			broadcast_id: string | ObjectId;
			read: boolean;
		};
		missing_fields: string[];
		error: string | null;
	}) => ({
		// Chat audit I8, 17 Oct 2026: a refusal echoes the request body, so only a success clears the badge.
		payload: payload?.status === "success" ? payload.payload : null,
	}),
};

export const setSingleLeadData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.single_lead_details = action.payload;
	},
	prepare: (payload: StateInterface["business_dashboard"]["single_lead_details"]) => ({
		payload: payload,
	}),
};

export const handleLeadsDetailsStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.payload.status === "junk") {
			// Remove the lead from the leads array
			state.business_dashboard.leads = state.business_dashboard.leads.filter(
				(lead: CtaLeadsInterface) => lead._id !== action.payload.payload.lead_id,
			);
		} else {
			state.business_dashboard.single_lead_details.status = action.payload.payload.status;
			state.business_dashboard.single_lead_details.activity_log = action.payload.payload.activity_log;
			state.business_dashboard.leads.map((lead) => {
				lead._id === action.payload.payload.lead_id ? (lead.activity_log = action.payload.payload.activity_log) : lead;
				lead._id === action.payload.payload.lead_id ? (lead.status = action.payload.payload.status) : lead;
			});
		}
	},
	prepare: (payload: {
		status: string;
		payload: {
			lead_id: string | ObjectId;
			status: string;
			activity_log: Array<{
				action: string;
				notes: string;
				action_performed_at: Date | string;
				created_by: {
					is_business_profile: boolean;
					user_id: string | ObjectId;
					business_profile_id: string | ObjectId;
				};
			}>;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const removejunkLeadFromList = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.leads = state.business_dashboard.leads.filter(
			(lead: CtaLeadsInterface) => lead._id !== action.payload,
		);
	},
	prepare: (payload: string | ObjectId) => ({
		payload: payload,
	}),
};

export const setLeadDetailsReminder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// Multi-reminder support: the API now returns the FULL updated reminders
		// array (`all_reminders`) alongside the single touched one (`reminders`,
		// kept for back-compat). Prefer the full array so an edit/add doesn't
		// wipe out the lead's other reminders; falls back to the old
		// wrap-the-one-reminder behaviour if an older API response shows up.
		const allReminders = action.payload.payload.all_reminders ?? [action.payload.payload.reminders];
		state.business_dashboard.single_lead_details.reminders = allReminders;
		state.business_dashboard.leads.map((lead) => {
			lead._id === action.payload.payload.lead_id ? (lead.reminders = allReminders) : lead;
		});
	},
	prepare: (payload: {
		status: string;
		payload: {
			lead_id: string | ObjectId;
			reminders: {
				trigger_reminder_at: Dayjs | null;
				reminder_at: Dayjs | null;
				title: string;
				created_at: Date | string;
			};
			all_reminders?: Array<{
				_id: string;
				trigger_reminder_at: Dayjs | null;
				reminder_at: Dayjs | null;
				title: string;
				created_at: Date | string;
			}>;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const handleDeleteLeadReminder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// Multi-reminder support: use the API's full post-delete array instead of
		// unconditionally clearing to []; a delete now only removes the one
		// reminder the user targeted, not every reminder on the lead.
		const allReminders = action.payload.payload.all_reminders ?? [];
		state.business_dashboard.single_lead_details.reminders = allReminders;
		state.business_dashboard.leads.map((lead) => {
			lead._id === action.payload.payload.lead_id ? (lead.reminders = allReminders) : lead;
		});
	},
	prepare: (payload: {
		is_reminder_deleted: boolean;
		lead_id?: string | ObjectId;
		all_reminders?: Array<{
			_id: string;
			trigger_reminder_at: Dayjs | null;
			reminder_at: Dayjs | null;
			title: string;
			created_at: Date | string;
		}>;
	}) => ({
		payload: payload,
	}),
};

export const addNewNoteToLeadActivityLogs = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.single_lead_details.activity_log = action.payload.payload.activity_log;
		state.business_dashboard.leads.map((lead) => {
			lead._id === action.payload.payload.lead_id ? (lead.activity_log = action.payload.payload.activity_log) : lead;
		});
	},
	prepare: (payload: {
		status: string;
		payload: {
			lead_id: string | ObjectId;
			activity_log: Array<{
				action: string;
				notes: string;
				action_performed_at: Date | string;
				created_by: {
					is_business_profile: boolean;
					user_id: string | ObjectId;
					business_profile_id: string | ObjectId;
				};
			}>;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const updateBusinessDashboardLeadsAssignedTo = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// A refused assignment answers payload null: leave the leads as they are.
		if (!Array.isArray(action.payload?.leads)) return;
		action.payload.leads.forEach((updatedLead: CtaLeadsInterface) => {
			const leadIndex = (state.business_dashboard.leads as CtaLeadsInterface[]).findIndex(
				(lead) => lead._id.toString() === updatedLead._id.toString(),
			);
			if (leadIndex !== -1) {
				state.business_dashboard.leads[leadIndex] = {
					...state.business_dashboard.leads[leadIndex],
					...updatedLead,
				};
				state.business_dashboard.single_lead_details.activity_log = updatedLead.activity_log;
			}
			state.business_dashboard.single_lead_details.assigned_to = action.payload.leads[0].assigned_to;
		});
	},
	prepare: (payload: { status: string; payload: any; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload.payload,
	}),
};

export const fetchBusinessDashboardTeamMembers = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.search_leads_response.admins = action.payload.admins;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const fetchAiMetaActiveCampaigns = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* A refusal or an error has no payload: show no campaigns rather than throw here, which left the Home widget's
		   spinner running for good (2 Nov 2026). */
		const activeBoosts = action.payload?.payload?.active_boost || [];
		state.business_dashboard.home_screen.ai_boost_widget.active_campaigns = activeBoosts;
		state.business_dashboard.home_screen.ai_boost_widget.active_campaigns_count = activeBoosts.length;
		state.business_dashboard.home_screen.ai_boost_widget.loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			active_boost: Array<{
				boost_id: string | ObjectId;
				property_id: string | ObjectId;
			}>;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};
export const fetchHomeScreenBroadcastWidget = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.home_screen.broadcast_widget.broadcast_widget_data =
			action.payload.payload.responseArr.map((item: any) => ({
				property_title: item.property_title || "",
				delivered_count: item.delivered || 0,
				accepted_count: item.accepted || 0,
			}));
		state.business_dashboard.home_screen.broadcast_widget.loading = false;
	},
	prepare: (payload: {
		status: string;
		payload: {
			responseArr: Array<{
				property_title: string;
				delivered: number;
				accepted: number;
			}>;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchHomePageStats = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.home_page_stats = action.payload.payload;
		state.home_page_stats.initial_load_done = true;
	},
	prepare: (payload: {
		status: string;
		payload: {
			total_properties: number;
			total_published_properties: number;
			total_drafts: number;
			total_broadcasts: number;
			total_ai_boosts: number;
			total_leads: number;
			new_leads_last_7_days: number;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchTopAgentAreas = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.home_screen.agent_trends_widget.top_agent_areas = action.payload.areas;
		state.business_dashboard.home_screen.agent_trends_widget.total_agent_count = action.payload.total_agent_count;
		state.business_dashboard.home_screen.agent_trends_widget.loading = false;
	},
	prepare: (payload: {
		areas: Array<{
			location_name: string;
			location_coordinates: Array<number>;
			agent_count: number;
		}>;
		total_agent_count: number;
	}) => ({
		payload: payload,
	}),
};

export const fetchPerformanceAnalyticsBusinessDashboardHomeWidget = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.home_screen.performance_analysis_widget.performance_analytics_data = action.payload.graph;
		state.business_dashboard.home_screen.performance_analysis_widget.total_listings = action.payload.total_listings;
		state.business_dashboard.home_screen.performance_analysis_widget.loading = false;
	},
	prepare: (payload: {
		status: string;
		graph: Array<any>;
		period: string;
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const fetchAllCampaignMetrics = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.all_campaigns_performance_metrics.report_type =
			action.payload.payload.report_type;
		if (action.payload.payload.report_type === "quarterly") {
			if (!Array.isArray(action.payload.payload.quarterlyList)) {
				return;
			}
			state.business_dashboard.property_ai_boost_details_meta.all_campaigns_performance_metrics.quarterly_list =
				action.payload.payload.quarterlyList;
		} else if (action.payload.payload.report_type === "campaign_wise") {
			state.business_dashboard.property_ai_boost_details_meta.all_campaigns_performance_metrics.campaign_wise_list.boosts =
				action.payload.payload.campaignWiseList;
		} else {
			state.business_dashboard.property_ai_boost_details_meta.all_campaigns_performance_metrics.date_range_list.boosts =
				action.payload.payload.campaignWiseList;
		}
		state.business_dashboard.property_ai_boost_details_meta.leads_loading = false;
		state.business_dashboard.property_ai_boost_details_meta.budget_loading = false;
	},
	prepare: (payload: { status: string; missingFields: Array<string> | null; error: string | null }) => ({
		payload: payload,
	}),
};

export const setMetricsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.impressions_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.clicks_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.ctr_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.leads_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.budget_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.meta_impressions_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.meta_clicks_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.meta_ctr_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.meta_leads_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.meta_budget_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.google_impressions_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.google_clicks_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.google_ctr_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.google_leads_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.google_budget_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setBudgetAndLeadsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.property_ai_boost_details_meta.leads_loading = action.payload;
		state.business_dashboard.property_ai_boost_details_meta.budget_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const setSearchSortBy = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.search_sort_by = action.payload;
	},
	prepare: (payload: StateInterface["search_sort_by"]) => ({
		payload: payload,
	}),
};

export const setNerSearchTerm = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.ner_search_states.search_term = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setNerLastSearchedTerm = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.ner_search_states.last_searched_term = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const fetchClientCrmIntegrationWebhook = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			state.business_dashboard.crm_integration_details.is_enabled = action.payload.payload.is_enabled;
			state.business_dashboard.crm_integration_details.initial_load_done = true;
			state.business_dashboard.crm_integration_details.webhook_info = {
				channel_id: action.payload.payload.channel_id,
				crm_name: action.payload.payload.crm_name,
				api_method: action.payload.payload.api_method,
				webhook_url: action.payload.payload.webhook_url,
				api_key: action.payload.payload.api_key,
				headers: action.payload.payload.headers || [],
			};
			state.business_dashboard.crm_integration_details.payload_info = {
				channel_id: action.payload.payload.field_name_mapping.channel_id,
				payload_type: action.payload.payload.payload_type,
				lead_id: action.payload.payload.field_name_mapping.lead_id,
				name: action.payload.payload.field_name_mapping.name,
				phone: action.payload.payload.field_name_mapping.phone,
				email: action.payload.payload.field_name_mapping.email,
				project_name: action.payload.payload.field_name_mapping.project_name,
				status: action.payload.payload.field_name_mapping.status,
				notes: action.payload.payload.field_name_mapping.notes,
				lead_source: action.payload.payload.field_name_mapping.lead_source,
				enquired_at: action.payload.payload.field_name_mapping.enquired_at,
				api_key: action.payload.payload.field_name_mapping.api_key,
			};
		}
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const CreateCrmIntegrationWebhook = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.is_enabled = action.payload.payload.is_enabled;
		state.business_dashboard.crm_integration_details.initial_load_done = true;
		state.business_dashboard.crm_integration_details.webhook_info = {
			channel_id: action.payload.payload.channel_id,
			crm_name: action.payload.payload.crm_name,
			api_method: action.payload.payload.api_method,
			webhook_url: action.payload.payload.webhook_url,
			api_key: action.payload.payload.api_key,
			headers: action.payload.payload.headers || [],
		};
		state.business_dashboard.crm_integration_details.payload_info = {
			channel_id: action.payload.payload.field_name_mapping.channel_id,
			payload_type: action.payload.payload.payload_type,
			lead_id: action.payload.payload.field_name_mapping.lead_id,
			name: action.payload.payload.field_name_mapping.name,
			phone: action.payload.payload.field_name_mapping.phone,
			email: action.payload.payload.field_name_mapping.email,
			project_name: action.payload.payload.field_name_mapping.project_name,
			status: action.payload.payload.field_name_mapping.status,
			notes: action.payload.payload.field_name_mapping.notes,
			lead_source: action.payload.payload.field_name_mapping.lead_source,
			enquired_at: action.payload.payload.field_name_mapping.enquired_at,
			api_key: action.payload.payload.field_name_mapping.api_key,
		};
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const UpdateCrmIntegrationWebhook = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			state.business_dashboard.crm_integration_details.is_enabled = action.payload.payload.is_enabled;
		}
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const TestCrmIntegrationWebhook = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.test_client_crm_integration_webhooks = action.payload.payload;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const setCRMApiMethod = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.webhook_info.api_method = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMWebhookURL = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.webhook_info.webhook_url = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMName = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.webhook_info.crm_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setChannelId = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.webhook_info.channel_id = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMEmailField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.email = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMChannelIDField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.channel_id = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMWebhookApiKey = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.webhook_info.api_key = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMHeadersKey = {
	reducer: (state: StateInterface, action: AnyAction) => {
		return {
			...state,
			business_dashboard: {
				...state.business_dashboard,
				crm_integration_details: {
					...state.business_dashboard.crm_integration_details,
					webhook_info: {
						...state.business_dashboard.crm_integration_details.webhook_info,
						headers: state.business_dashboard.crm_integration_details.webhook_info.headers.map((header, index) =>
							index === action.payload.index ? { ...header, key: action.payload.newKey } : header,
						),
					},
				},
			},
		};
	},
	prepare: (payload: { index: number; newKey: string }) => ({
		payload,
	}),
};

export const setCRMHeadersValue = {
	reducer: (state: StateInterface, action: AnyAction) => {
		return {
			...state,
			business_dashboard: {
				...state.business_dashboard,
				crm_integration_details: {
					...state.business_dashboard.crm_integration_details,
					webhook_info: {
						...state.business_dashboard.crm_integration_details.webhook_info,
						headers: state.business_dashboard.crm_integration_details.webhook_info.headers.map((header, index) =>
							index === action.payload.index ? { ...header, value: action.payload.newValue } : header,
						),
					},
				},
			},
		};
	},
	prepare: (payload: { index: number; newValue: string }) => ({
		payload,
	}),
};

export const setCRMAddNewHeaderField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.webhook_info.headers.push(action.payload);
	},
	prepare: (payload: { key: string; value: string }) => ({
		payload, // Ensure the payload is returned correctly
	}),
};

export const setCRMRemoveHeaderField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.webhook_info.headers.splice(action.payload, 1);
	},
	prepare: (payload: number) => ({
		payload: payload,
	}),
};

export const setCRMPayloadType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.payload_type = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMLeadIDField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.lead_id = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMNameField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMPhoneNumberField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.phone = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMProjectNameField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.project_name = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMStatusField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.status = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMNotesField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.notes = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMLeadSourceField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.lead_source = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMEnquiredAtField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.enquired_at = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const setCRMAPIKeyField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.crm_integration_details.payload_info.api_key = action.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboardActivityLogsData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		// state.business_dashboard.activity_logs.members_and_admins = action.payload.payload.members_and_admins;
		state.business_dashboard.activity_logs.activity_logs = action.payload.payload.activity_logs;
		state.business_dashboard.activity_logs.filter_tab_value = action.payload.payload.filter_tab_value;
		state.business_dashboard.activity_logs.filter_activity_by = action.payload.payload.filter_activity_by;
		state.business_dashboard.activity_logs.filter_time_period = action.payload.payload.filter_time_period;
		state.business_dashboard.activity_logs.page = action.payload.payload.page;
		state.business_dashboard.activity_logs.page_size = action.payload.payload.page_size;
		state.business_dashboard.activity_logs.total_count = action.payload.payload.total_count;
		state.business_dashboard.activity_logs.total_pages = action.payload.payload.total_pages;
		state.business_dashboard.activity_logs.activity_logs_loading = false;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const loadMoreBusinessDashboardActivityLogsData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (Array.isArray(state.business_dashboard.activity_logs.activity_logs)) {
			state.business_dashboard.activity_logs.activity_logs.push(...action.payload.payload.activity_logs);
		} else {
			state.business_dashboard.activity_logs.activity_logs = action.payload.payload.activity_logs;
		}
		state.business_dashboard.activity_logs.page = action.payload.payload.page;
		state.business_dashboard.activity_logs.total_count = action.payload.payload.total_count;
		state.business_dashboard.activity_logs.total_pages = action.payload.payload.total_pages;
		state.business_dashboard.activity_logs.activity_logs_loading = false;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const fetchBusinessDashboarMembersAndAdminsData = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.activity_logs.members_and_admins = action.payload.payload.members_and_admins;
		state.business_dashboard.activity_logs.initial_load_done = true;
	},
	prepare: (payload: {
		status: string;
		payload: {
			members_and_admins: Array<{
				_id: string | ObjectId;
				name: string;
				role: string;
				is_selected: boolean;
			}>;
		};
		missingFields: Array<string> | null;
		error: string | null;
	}) => ({
		payload: payload,
	}),
};

export const setActivityLogsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.business_dashboard.activity_logs.activity_logs_loading = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

/*
 * The dashboard's Team list (business-dashboard/business-profile-admins/
 * fetch-business-profile-admins). The action carries the route's envelope plus the
 * `request` it answered ({ business_profile_id, page }). Page 1 replaces the list (a new
 * load, a search, a refresh after a write), later pages append. The viewer's standing and
 * the business it belongs to are stored with it, so screens read their rights from the
 * server instead of matching their phone to a row.
 */
export const fetchBusinessProfileAdmins = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const team = state.business_dashboard.admins;
		const payload = action.payload?.payload;
		const request = action.payload?.request || {};
		const page = Number(payload?.page || request.page || 1);
		team.admin_loading = false;
		team.is_admin_fetching = false;
		team.initial_load_done = true;

		if (!payload || !Array.isArray(payload.admins)) {
			/* A refused or failed first page leaves nothing to show: never keep another profile's team on screen. */
			if (page === 1) {
				team.admins = [];
				team.total_admins_count = 0;
				team.viewer = null;
				team.business_profile_id = String(request.business_profile_id || "");
			}
			return;
		}

		if (page === 1) {
			team.admins = payload.admins;
		} else {
			(team.admins as any[]).push(...payload.admins);
		}
		if (payload.totalCount !== undefined) {
			team.total_admins_count = payload.totalCount;
		}
		team.viewer = payload.viewer ?? null;
		team.business_profile_id = String(request.business_profile_id || "");
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

/* A saved Edit (update-business-profile-admin answers { user_id, role, access }): patch that row in place. */
export const updateBusinessProfileAdminRole = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const saved = action.payload?.payload;
		if (!saved || !saved.user_id) return;
		const adminIndex = (state.business_dashboard.admins.admins as any[]).findIndex(
			(admin) => String(admin.user_id) === String(saved.user_id) && admin.status !== "invite-pending",
		);
		if (adminIndex !== -1) {
			state.business_dashboard.admins.admins[adminIndex].role = saved.role;
			state.business_dashboard.admins.admins[adminIndex].access = saved.access;
		}
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

/* A removal (remove-business-profile-admin answers { user_id, status: "removed" }): drop that row. */
export const removeBusinessProfileAdmin = {
	reducer: (state: StateInterface, action: AnyAction) => {
		const removed = action.payload?.payload;
		if (!removed || !removed.user_id) return;
		const before = state.business_dashboard.admins.admins.length;
		state.business_dashboard.admins.admins = state.business_dashboard.admins.admins.filter(
			(admin) => !(String(admin.user_id) === String(removed.user_id) && admin.status !== "invite-pending"),
		);
		if (state.business_dashboard.admins.admins.length < before) {
			state.business_dashboard.admins.total_admins_count -= 1;
		}
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

/*
 * No longer dispatched (6 Oct 2026): addNewBusinessProfileAdminThunk returns the invite
 * route's answer and the Team screen re-fetches page 1, so every new pending row arrives with
 * the server's action flags. Kept tolerant of a payload without an admins array.
 */
export const addNewBusinessProfileAdmin = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (!Array.isArray(action.payload?.admins)) return;
		(state.business_dashboard.admins.admins as any[]).push(...action.payload.admins);
		state.business_dashboard.admins.total_admins_count += action.payload.admins.length;
	},
	prepare: (payload: any) => ({
		payload: payload,
	}),
};

export const fetchKeyHighlights = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.localities = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchLocalitiesFaq = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.localities = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchCoreDetailsAndImages = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.localities = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchCuratedPropertyIds = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.localities = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchPriceTrends = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.localities = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const fetchMapDetailsAndVicinity = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.localities = action.payload;
	},
	prepare: (payload: boolean) => ({
		payload: payload,
	}),
};

export const updateCurrency = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.redux_session && state.redux_session.user && state.redux_session.user.dbUser)
			state.redux_session.user.dbUser.settings.site_preferences.default_currency = action.payload.payload;
	},
	prepare: (payload: string) => ({
		payload: payload,
	}),
};

export const updateCookieConsent = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.redux_session && state.redux_session.user && state.redux_session.user.dbUser)
			state.redux_session.user.dbUser.cookie_policies = action.payload.payload;
	},
	prepare: (payload: { necessary: boolean; analytics: boolean; marketing: boolean; other: boolean }) => ({
		payload: payload,
	}),
};

export const SetTeamInvite = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.team_invite = action.payload;
	},
	prepare: (payload: TeamInviteInterface) => ({ payload }),
};

/* ======================== */
/* ? Requirements Reducers  */
/* ======================== */

export const createRequirement = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			state.my_requirements.requirements.unshift(action.payload.payload);
			state.my_requirements.requirements_count += 1;
		}
	},
	prepare: (payload: any) => ({ payload }),
};

export const fetchMyRequirements = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			/* Page 2 onwards (the screen's infinite scroll) adds to the list; page 1, or a caller that sends no page
			   (the user profile page), replaces it. */
			const page: number = Number(action.payload.page) || 1;
			const incoming: Array<any> = action.payload.payload.requirements || [];
			if (page > 1) {
				const seen = new Set((state.my_requirements.requirements || []).map((req: any) => String(req._id)));
				state.my_requirements.requirements = [
					...(state.my_requirements.requirements || []),
					...incoming.filter((req: any) => !seen.has(String(req._id))),
				];
			} else {
				state.my_requirements.requirements = incoming;
			}
			state.my_requirements.filter_page_number = page;
			/* Page 1 carries the status chips' counts (myRequirementsCore); later pages send null and keep them. */
			if (action.payload.payload.status_counts) state.my_requirements.status_counts = action.payload.payload.status_counts;
			state.my_requirements.requirements_count = action.payload.payload.requirements_count;
		}
		state.my_requirements.initial_load_done = true;
		state.my_requirements.loading = false;
	},
	prepare: (payload: any) => ({ payload }),
};

export const fetchSingleRequirement = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			state.requirement = action.payload.payload;
			state.my_requirements.selected_requirement = action.payload.payload;
			state.my_requirements.selected_requirement_loading = false;
		}
	},
	prepare: (payload: any) => ({ payload }),
};

export const updateRequirementStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			const updated = action.payload.payload;
			state.requirement = updated;
			state.my_requirements.selected_requirement = updated;
			const index = state.my_requirements.requirements.findIndex(
				(r: any) => r._id.toString() === updated._id.toString(),
			);
			if (index !== -1) {
				state.my_requirements.requirements[index] = updated;
			}
		}
	},
	prepare: (payload: any) => ({ payload }),
};

export const updateRequirement = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			const updated = action.payload.payload;
			state.requirement = updated;
			state.my_requirements.selected_requirement = updated;
			const index = state.my_requirements.requirements.findIndex(
				(r: any) => r._id.toString() === updated._id.toString(),
			);
			if (index !== -1) {
				state.my_requirements.requirements[index] = updated;
			}
		}
	},
	prepare: (payload: any) => ({ payload }),
};

export const closeRequirement = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			const updated = action.payload.payload;
			state.requirement = updated;
			state.my_requirements.selected_requirement = updated;
			const index = state.my_requirements.requirements.findIndex(
				(r: any) => r._id.toString() === updated._id.toString(),
			);
			if (index !== -1) {
				state.my_requirements.requirements[index] = updated;
			}
		}
	},
	prepare: (payload: any) => ({ payload }),
};

export const setMyRequirementsFilterStatus = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.my_requirements.filter_status = action.payload;
	},
	prepare: (payload: string) => ({ payload }),
};

export const setMyRequirementsFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.my_requirements.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({ payload }),
};

export const setMyRequirementsFilterSortField = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.my_requirements.filter_sort_field = action.payload;
	},
	prepare: (payload: string) => ({ payload }),
};

export const setMyRequirementsFilterSortOrder = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.my_requirements.filter_sort_order = action.payload;
	},
	prepare: (payload: string) => ({ payload }),
};

export const setMyRequirementsTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.my_requirements.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({ payload }),
};

export const setMyRequirementsLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.my_requirements.loading = action.payload;
	},
	prepare: (payload: boolean) => ({ payload }),
};

export const setMyRequirementsInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.my_requirements.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({ payload }),
};

/* ================================ */
/* ? Requirements Feed Reducers     */
/* ================================ */

export const fetchRequirementsFeed = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			/* Page 2 onwards (the screen's infinite scroll) adds to the list; page 1 replaces it. */
			const page: number = Number(action.payload.page) || 1;
			const incoming: Array<any> = action.payload.payload.requirements || [];
			if (page > 1) {
				const seen = new Set((state.requirements_feed.requirements || []).map((req: any) => String(req._id)));
				state.requirements_feed.requirements = [
					...(state.requirements_feed.requirements || []),
					...incoming.filter((req: any) => !seen.has(String(req._id))),
				];
			} else {
				state.requirements_feed.requirements = incoming;
			}
			state.requirements_feed.filter_page_number = page;
			state.requirements_feed.requirements_count = action.payload.payload.requirements_count;
			/* Also keep the per-tab count in sync with whichever mode was fetched */
			const mode = action.payload.mode;
			if (mode === "feed") state.requirements_feed.feed_count = action.payload.payload.requirements_count;
			else if (mode === "responded")
				state.requirements_feed.responded_count = action.payload.payload.requirements_count;
			else if (mode === "saved") state.requirements_feed.saved_count = action.payload.payload.requirements_count;
			/* The v2 feed (fetchRequirementsFeedV2Thunk): the Feed tab counts what the chips and filters show, the page's
			   total plus page 1's rails (the rails take their requirements out of the list); Saved and Responded take
			   the badges the route sends with every mode. A rail's See all leaves the Feed count alone. */
			if (mode === "feed_v2" && page === 1 && !action.payload.rail) {
				const railsTotal: number =
					(action.payload.payload.highest_match?.length || 0) +
					(action.payload.payload.in_your_areas?.length || 0) +
					(action.payload.payload.highest_brokerage?.length || 0);
				state.requirements_feed.feed_count = (action.payload.payload.requirements_count || 0) + railsTotal;
			}
			if (mode === "feed_v2" || mode === "claimed" || mode === "saved") {
				if (typeof action.payload.payload.saved_count === "number")
					state.requirements_feed.saved_count = action.payload.payload.saved_count;
				if (typeof action.payload.payload.claimed_count === "number")
					state.requirements_feed.responded_count = action.payload.payload.claimed_count;
			}
		}
		state.requirements_feed.initial_load_done = true;
		state.requirements_feed.loading = false;
	},
	prepare: (payload: any) => ({ payload }),
};

export const fetchRequirementsFeedCounts = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			state.requirements_feed.feed_count = action.payload.payload.feed_count;
			state.requirements_feed.responded_count = action.payload.payload.responded_count;
			state.requirements_feed.saved_count = action.payload.payload.saved_count;
		}
	},
	prepare: (payload: any) => ({ payload }),
};

export const respondToRequirement = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (action.payload.status === "success") {
			/* Remove from feed */
			state.requirements_feed.requirements = state.requirements_feed.requirements.filter(
				(r: any) => r._id.toString() !== action.payload.payload.requirement._id.toString(),
			);
			state.requirements_feed.requirements_count = Math.max(0, state.requirements_feed.requirements_count - 1);
			state.requirements_feed.feed_count = Math.max(0, state.requirements_feed.feed_count - 1);
			state.requirements_feed.responded_count = state.requirements_feed.responded_count + 1;
		}
	},
	prepare: (payload: any) => ({ payload }),
};

export const saveRequirement = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.saved_count = state.requirements_feed.saved_count + 1;
	},
	prepare: (payload: any) => ({ payload }),
};

export const unsaveRequirement = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.saved_count = Math.max(0, state.requirements_feed.saved_count - 1);
	},
	prepare: (payload: any) => ({ payload }),
};

export const trackContactAction = {
	reducer: (state: StateInterface, action: AnyAction) => {
		/* No state change needed */
	},
	prepare: (payload: any) => ({ payload }),
};

export const setRequirementsFeedFilterTransactionType = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.filter_transaction_type = action.payload;
	},
	prepare: (payload: string) => ({ payload }),
};

export const setRequirementsFeedFilterPropertyCategory = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.filter_property_category = action.payload;
	},
	prepare: (payload: string) => ({ payload }),
};

export const setRequirementsFeedFilterLocation = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.filter_location = action.payload;
	},
	prepare: (payload: string) => ({ payload }),
};

export const setRequirementsFeedFilterPageNumber = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.filter_page_number = action.payload;
	},
	prepare: (payload: number) => ({ payload }),
};

export const setRequirementsFeedLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.loading = action.payload;
	},
	prepare: (payload: boolean) => ({ payload }),
};

export const setRequirementsFeedInitialLoadDone = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.initial_load_done = action.payload;
	},
	prepare: (payload: boolean) => ({ payload }),
};

export const setRequirementsFeedTriggerNewSearch = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.requirements_feed.trigger_new_search = action.payload;
	},
	prepare: (payload: boolean) => ({ payload }),
};

export const setOpenCreateRequirementModal = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.open_create_requirement_modal = action.payload;
	},
	prepare: (payload: boolean) => ({ payload }),
};

export const setMyRequirementsSelectedRequirementLoading = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.my_requirements.selected_requirement_loading = action.payload;
	},
	prepare: (payload: boolean) => ({ payload }),
};

export const setPropertyTranslations = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.property_translations = action.payload;
	},
	prepare: (payload: PropertyTranslationsInterface) => ({ payload }),
};

export const setBbmPushNotificationTemplate = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.bbm_push_notification_template = action.payload;
	},
	prepare: (payload: BbmPushNotificationTemplateInterface) => ({ payload }),
};

export const setBbmScheduledPushNotificationLog = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.bbm_scheduled_push_notification_log = action.payload;
	},
	prepare: (payload: BbmScheduledPushNotificationLogInterface) => ({ payload }),
};

export const setNotificationQueue = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.notification_queue = action.payload;
	},
	prepare: (payload: NotificationQueueInterface) => ({ payload }),
};

export const SetPlatformAccessLevel = {
	reducer: (state: StateInterface, action: AnyAction) => {
		if (state.redux_session && state.redux_session.user && state.redux_session.user.dbUser) {
			state.redux_session.user.dbUser.platform_access_level = action.payload;
		}

		/*

		? Update profile_context so the navbar immediately reflects the team's name and display picture.

		*/

		const bp = action.payload?.business_profile;
		const user_id = state.profile_context?.user_id || state.redux_session?.user?.dbUser?._id;

		if (bp && user_id) {
			const updatedProfileContext = {
				is_business_profile: true,
				user_id: user_id,
				business_profile_id: action.payload.business_profile_id,
				business_profile_image: bp.display_picture_url ?? { file_id: "", mime_type: "" },
				page_type: bp.page_type ?? "",
				page_title: bp.page_title ?? "",
				page_slug: bp.page_slug ?? "",
				subscription_information: bp.subscription_information ?? {
					subscription_id: "",
					plan_slug: "",
					end_date: "",
					active: false,
				},
				kyc_info: bp.kyc_info ?? { kyc_status: "" },
				remember_choice: false,
			};

			state.profile_context = updatedProfileContext;

			/*

			? Also update the session's profile_context so components reading from redux_session re-render.

			*/

			if (state.redux_session?.user?.dbUser) {
				state.redux_session.user.dbUser.profile_context = {
					...updatedProfileContext,
					user_id: user_id?.toString(),
				};
			}
		}
	},
	prepare: (payload: any) => ({ payload }),
};

/*

& Sales Support Investment reducer - sets the currently loaded sales support investment.

*/

export const SetSalesSupportInvestment = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.sales_support_investment = action.payload;
	},
	prepare: (payload: SalesSupportInvestmentInterface) => ({ payload }),
};

/*

& Property Closure reducer - sets the currently loaded property closure.

*/

export const SetPropertyClosure = {
	reducer: (state: StateInterface, action: AnyAction) => {
		state.property_closure = action.payload;
	},
	prepare: (payload: PropertyClosureInterface) => ({ payload }),
};
